/* Typed API client. Wraps fetch with:
     - bearer token from MSAL (injected by the caller via getAccessToken)
     - JSON request/response
     - typed helpers for each backend endpoint we call

   Keeps the call sites simple (e.g. preflightAuth(token, { query_type })
   returns a typed PreflightAuthResult) and lets the MSW handlers in
   api/mocks/ intercept the same fetch calls when VITE_USE_MOCKS=true. */

import type {
  AgentDomain,
  ArtifactJobStatus,
  ArtifactRef,
  AttachmentChip,
  BidToContractDrift,
  DriftBaselineExposure,
  EnsureConversationThreadResult,
  PendingCheckin,
  PreflightAuthResult,
  RunAgentRequest,
  RunAgentResponse,
  RunMeta,
  SourceCitation,
  ToolResponse,
} from './schemas';

const APIM = import.meta.env.VITE_APIM_BASE_URL || 'http://localhost:7071';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message);
  }
}

/** Coerce any backend error payload into a readable string. FastAPI 422s
 *  return `detail` as an ARRAY of {loc, msg, type} objects, and some
 *  HTTPExceptions carry dict details — feeding those into Error/ApiError
 *  renders as "[object Object]" in every chat rail and error panel, masking
 *  the real failure. Route every error-body read through this instead. */
export function errorText(v: unknown, fallback = 'request failed'): string {
  if (typeof v === 'string') return v.trim() || fallback;
  if (v == null) return fallback;
  if (Array.isArray(v)) {
    const parts = v.map((d) => {
      if (typeof d === 'string') return d;
      if (d && typeof d === 'object' && 'msg' in d) {
        const rec = d as { msg: unknown; loc?: unknown };
        const loc = Array.isArray(rec.loc) ? `${rec.loc.join('.')}: ` : '';
        return `${loc}${String(rec.msg)}`;
      }
      try { return JSON.stringify(d); } catch { return String(d); }
    });
    return parts.filter(Boolean).join('; ') || fallback;
  }
  if (v instanceof Error) return v.message || fallback;
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return fallback; }
  }
  return String(v);
}

/* Bounded retry for rate-limit rejections (429). APIM's rate-limit-by-key
   rejects the call in the inbound pipeline — BEFORE it reaches any backend —
   so re-sending is safe for every method (nothing executed server-side).
   Honors Retry-After when the response carries it (capped at 10s), else
   backs off 1s/2s. Backend-originated throttles (Procore shed, Buildr
   rate-limit) surface as HTTP-200 ToolResponse envelopes, not raw 429s,
   so they never enter this path. */
const RETRY_429_ATTEMPTS = 2;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** run-agent's per-user turn limit (inference_service
 *  foundry/turn_rate_limit.py): a 429 whose body carries this code. */
export const RATE_LIMITED_CODE = 'rate_limited';

/** What the user reads when the turn limit refused a send. Mirrors the
 *  backend's sentence, which is what the 429's `detail` carries (with the
 *  wait in minutes when it is long); this copy covers an empty one. */
export const RATE_LIMITED_TEXT =
  "You've sent a lot of requests in the last few minutes. Please wait a moment and try again.";

/** The send was refused by the per-user turn limit. Not a stream death and
 *  not a service restart: no run started, so there is nothing to recover. */
export function isRateLimited(e: unknown): boolean {
  return e instanceof ApiError && e.status === 429 && e.code === RATE_LIMITED_CODE;
}

/** The plain sentence for a rate-limited send: the backend's when it sent
 *  one, else the same sentence from here. */
export function rateLimitedText(message: unknown): string {
  return typeof message === 'string' && message.trim() ? message.trim() : RATE_LIMITED_TEXT;
}

/** The pause switch (inference_service foundry/agent_pause.py): an
 *  administrator has paused Daedalus, and run-agent refused the turn with a
 *  503 whose body carries this code. No run started. */
export const AGENT_PAUSED_CODE = 'agent_paused';

/** What the user reads while Daedalus is paused (decided 2026-10-27). */
export const AGENT_PAUSED_TEXT = 'Daedalus is paused while we fix a problem. Try again shortly.';

/** The send was refused by the pause switch: not a stream death (a 503
 *  would otherwise start the recovery poll) and not a failure to report. */
export function isAgentPaused(e: unknown): boolean {
  return e instanceof ApiError && e.status === 503 && e.code === AGENT_PAUSED_CODE;
}

/** The model was throttled or unavailable after the SDK's own retries
 *  (inference_service foundry/model_busy.py): run-agent answers the turn as
 *  failed with this error_code and MODEL_BUSY_TEXT as its message. Nothing
 *  the person did, and no run is left to recover. */
export const MODEL_BUSY_CODE = 'model_busy';

/** What the user reads when the model is busy (2026-10-27). */
export const MODEL_BUSY_TEXT = 'Daedalus is busy right now. Try again in a minute.';

/** True for a 429 from the backend's turn limit rather than the gateway's
 *  rate limiter. Its wait is minutes, so re-sending a second or two later
 *  would only be refused again and keep the user waiting for the message:
 *  the bounded retry stops and the caller shows it. Reads a clone, so the
 *  caller can still read the body. */
export async function isTurnLimitResponse(res: Response): Promise<boolean> {
  if (res.status !== 429) return false;
  try {
    const body = await res.clone().json();
    return body?.error_code === RATE_LIMITED_CODE;
  } catch {
    return false;
  }
}

function retryDelayMs(res: Response, attempt: number): number {
  const ra = Number(res.headers.get('Retry-After'));
  if (Number.isFinite(ra) && ra > 0) return Math.min(ra, 10) * 1000;
  return 1000 * 2 ** attempt;
}

async function call<T>(
  path: string,
  token: string | null,
  body: unknown,
  extraHeaders?: Record<string, string>,
  options?: { method?: 'GET' | 'POST' | 'DELETE' }
): Promise<T> {
  const method = options?.method ?? 'POST';
  let res: Response;
  for (let attempt = 0; ; attempt++) {
    res = await fetch(`${APIM}${path}`, {
      method,
      headers: {
        ...(method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
        ...(token && token !== 'mock-token' ? { Authorization: `Bearer ${token}` } : {}),
        ...(extraHeaders ?? {}),
      },
      body: method === 'GET' ? undefined : JSON.stringify(body),
    });
    if (res.status !== 429 || attempt >= RETRY_429_ATTEMPTS) break;
    if (await isTurnLimitResponse(res)) break;
    await sleep(retryDelayMs(res, attempt));
  }
  if (!res.ok) {
    let msg = res.statusText;
    let code: string | undefined;
    try {
      const err = await res.json();
      msg = errorText(err?.error?.message ?? err?.detail, msg);
      // Backends bubble machine-stable codes in a few shapes — accept
      // any of them so callers can branch on a single ApiError.code.
      // Order matters: deeper / more specific shapes first so a generic
      // top-level "code" doesn't shadow a structured error envelope.
      code =
        err?.error?.error_code ??
        err?.error?.code ??
        err?.error_code ??
        err?.code ??
        undefined;
    } catch {
      /* ignore body parse errors */
    }
    throw new ApiError(msg, res.status, code);
  }
  return (await res.json()) as T;
}

/* ---------------- Read-through response cache ----------------
   Dashboard/list rollups are refetched on every tab switch because the
   scenes unmount/remount and there is no client cache — against a
   cross-region, DTU-constrained SQL backend that re-runs the full
   aggregation each time. This is a deliberately tiny stale-for-TTL cache:
   the promise is stored immediately (so two components mounting in the
   same tick share ONE request), failures are evicted so errors never
   stick — both rejections AND resolved ToolResponse envelopes carrying
   success=false (the endpoint wrappers throw on those per-caller, after
   caching, so without eviction a transient backend error would be served
   for the full TTL) — and entries expire after ttlMs. Read-only rollups
   only — never cache anything a write on the same surface must
   immediately reflect. */

const DEFAULT_CACHE_TTL_MS = 60_000;
const responseCache = new Map<string, { promise: Promise<unknown>; expiresAt: number }>();

/** Memoize an arbitrary promise-returning fetch under a cache key.
 *  Exported so sibling API modules (buildrOutreach.ts) can share the
 *  same cache + invalidation without duplicating the mechanism. */
export function memoCall<T>(key: string, ttlMs: number, fn: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const hit = responseCache.get(key);
  if (hit && hit.expiresAt > now) return hit.promise as Promise<T>;
  const promise = fn();
  responseCache.set(key, { promise, expiresAt: now + ttlMs });
  const evict = () => {
    if (responseCache.get(key)?.promise === promise) responseCache.delete(key);
  };
  promise.then((v) => {
    // Duck-typed on the ToolResponse shape: memoCall also caches non-envelope
    // fetches (blob URLs, sibling modules), which sail through untouched.
    if (v && typeof v === 'object' && (v as { success?: unknown }).success === false) evict();
  }, evict);
  return promise;
}

/** Drop cached responses — all of them, or those whose key (path|body)
 *  starts with pathPrefix. Call after a write that must be visible on
 *  the next read of a cached surface. */
export function invalidateCachedCalls(pathPrefix?: string): void {
  if (!pathPrefix) {
    responseCache.clear();
    return;
  }
  for (const key of Array.from(responseCache.keys())) {
    if (key.startsWith(pathPrefix)) responseCache.delete(key);
  }
}

/** call(), but read-through cached. Key = path + request body, so
 *  filter variants cache independently. Token is deliberately not part
 *  of the key — the SPA is single-user and silent refresh rotates the
 *  token string without changing identity. */
function cachedCall<T>(
  path: string,
  token: string | null,
  body: unknown,
  ttlMs: number = DEFAULT_CACHE_TTL_MS
): Promise<T> {
  return memoCall<T>(`${path}|${JSON.stringify(body ?? {})}`, ttlMs, () => call<T>(path, token, body));
}

/* ---------------- Tools Service ---------------- */

/* Silent delegated-Graph-token provider, registered by MsalBootstrap. Lets
   preflightAuth attach X-Graph-Token (for SharePoint library probing) without
   threading the token through every chat call site. Null in mock/dev modes. */
let _graphTokenProvider: (() => Promise<string | null>) | null = null;
export function setGraphTokenProvider(fn: (() => Promise<string | null>) | null): void {
  _graphTokenProvider = fn;
}

/** The user's delegated Graph token as an X-Graph-Token header, or undefined
 *  when there is none (no provider, acquisition failed, or the mock token).
 *  Never throws: a call that can go without it must not fail on it. Used by
 *  preflightAuth (SharePoint library probe) and by the previz calls that
 *  name a SharePoint file or folder, which tools-service reads AS the user. */
async function graphTokenHeader(): Promise<Record<string, string> | undefined> {
  try {
    const graphToken = _graphTokenProvider ? await _graphTokenProvider() : null;
    if (graphToken && graphToken !== 'mock-graph-token') {
      return { 'X-Graph-Token': graphToken };
    }
  } catch {
    /* never block the call on graph-token acquisition */
  }
  return undefined;
}

/* ---------------- Agent-flow domain wire vocabulary ----------------------
   The agent-flow endpoints — preflight-auth, run-agent, ensure-conversation-
   thread, reset-conversation, and the Foundry tool-proxy allowlists — speak a
   LONG-form domain vocabulary ('executive', 'accounting', …), while the SPA
   (and the admin domain-deployments endpoints in admin_v2.py) use the short
   forms 'exec'/'accting'. Only those two diverge; every other domain is
   spelled identically on both sides. Translate at the wire boundary so the SPA
   keeps one internal vocabulary while these endpoints receive the exact names
   they validate against (PreflightAuthRequest.query_type as an AgentDomain
   Literal; SUPPORTED_AGENT_DOMAINS for run-agent). Without this an 'exec' /
   'accting' turn 422s on preflight-auth (`body.query_type: Input should be
   'pm', … 'executive' …`) and, past that, again on run-agent ('Unknown agent
   domain'). Coordinator/PM/etc. turns are unaffected (identity map). */
const AGENT_FLOW_WIRE_DOMAIN: Partial<Record<AgentDomain, string>> = {
  exec: 'executive',
  accting: 'accounting',
};

/** Map a SPA short-form domain to the long-form name the agent-flow endpoints
 *  expect. Identity for every domain except 'exec'/'accting'.
 *  Exported for api/streaming.ts (the run-agent-stream caller). */
export function toAgentFlowDomain(domain: AgentDomain): string {
  return AGENT_FLOW_WIRE_DOMAIN[domain] ?? domain;
}

/** Reverse of toAgentFlowDomain — stream events carry the backend's
 *  long-form domain names; map back to the SPA vocabulary so SPEC_BY_ID
 *  lookups (specialist display names) work. Unknown values pass through. */
const AGENT_FLOW_WIRE_DOMAIN_REVERSE: Record<string, AgentDomain> = {
  executive: 'exec',
  accounting: 'accting',
};
export function fromAgentFlowDomain(wire: string): AgentDomain {
  return AGENT_FLOW_WIRE_DOMAIN_REVERSE[wire] ?? (wire as AgentDomain);
}

export async function preflightAuth(
  token: string | null,
  params: {
    query_type: AgentDomain;
    scope_project_id?: number | null;
    /** The existing conversation this turn will continue (P1 thread
     *  ownership). The backend binds it to the session only if the user owns
     *  it, and run-agent refuses any other thread_id for that session. Leave
     *  it out for a fresh-thread turn. */
    thread_id?: string | null;
  }
): Promise<PreflightAuthResult> {
  // Attach the user's delegated Graph token so the backend can probe, AS the
  // user, which restricted SharePoint sites they can open (library-level
  // security trimming). Best-effort and silent: if it isn't available the
  // probe simply no-ops server-side. Mock token is ignored.
  const extraHeaders = await graphTokenHeader();
  const { thread_id, ...rest } = params;
  const once = async (): Promise<PreflightAuthResult> => {
    const wrap = await call<ToolResponse<PreflightAuthResult>>(
      '/tools/agents/preflight-auth',
      token,
      {
        ...rest,
        query_type: toAgentFlowDomain(params.query_type),
        // Only when there is one, so a fresh-thread turn's body is unchanged.
        ...(thread_id ? { thread_id } : {}),
      },
      extraHeaders
    );
    if (!wrap.data?.length) throw new ApiError('preflight returned no data', 500);
    return wrap.data[0];
  };
  const first = await once();
  // "unverified": the backend could not check ownership (a SQL fault), so
  // nothing was bound. That is not a refusal, and one more try usually binds.
  if (thread_id && first.thread_binding === 'unverified') return once();
  return first;
}

/** run-agent refused the thread for this session: the backend enforces P1
 *  thread ownership and the session was not bound to it. */
export function isThreadNotOwned(e: unknown): boolean {
  return e instanceof ApiError && e.status === 403 && e.code === 'thread_not_owned';
}

/** After a thread_not_owned refusal: preflight once more naming the thread
 *  (the first may have met a fault). The bound session, or null when the
 *  thread still won't bind — gone (deleted in another tab) or not this
 *  user's — and the caller should carry the turn into a fresh conversation,
 *  saying so with THREAD_RESTARTED_TEXT. */
export async function rebindThread(
  token: string | null,
  params: { query_type: AgentDomain; scope_project_id?: number | null },
  threadId: string
): Promise<PreflightAuthResult | null> {
  const again = await preflightAuth(token, { ...params, thread_id: threadId });
  return again.session_id && again.bound_thread_id === threadId ? again : null;
}

/** The reply for a turn the AI service's safety filter refused
 *  (RunAgentResponse.error_code 'content_filtered', inference_service
 *  foundry/content_filter.py). Mirrors the backend's USER_MESSAGE, which is
 *  what error_message carries; this copy covers an empty one. */
export const CONTENT_FILTERED_TEXT =
  "I couldn't process that message because the AI service's safety filter " +
  'flagged it. Please rephrase and try again.';

/** What the user reads for a content_filtered turn: the backend's plain
 *  sentence when it sent one, else the same sentence from here. Never the
 *  raw provider error. */
export function contentFilteredText(message: unknown): string {
  return typeof message === 'string' && message.trim() ? message.trim() : CONTENT_FILTERED_TEXT;
}

/** Shown when a turn had to move to a fresh conversation (see rebindThread). */
export const THREAD_RESTARTED_TEXT =
  "This conversation can't be continued any more, so I've started a new one " +
  'and answered your message there.';

/** A session that may continue `threadId` (P1 thread ownership).
 *
 *  run-agent refuses a thread_id its session was not bound to at preflight.
 *  A surface that learns its thread only AFTER preflighting — a lazy first
 *  send minting one, ensure-conversation-thread resolving a channel's durable
 *  thread, the thread_lost retry minting a replacement — passes the session it
 *  has. It is reused when it is already bound to that thread (the steady
 *  state, no extra round trip); otherwise preflight runs again naming it. */
export async function preflightForThread(
  token: string | null,
  params: { query_type: AgentDomain; scope_project_id?: number | null },
  threadId: string,
  current?: PreflightAuthResult | null
): Promise<PreflightAuthResult> {
  if (current?.session_id && current.bound_thread_id === threadId) return current;
  return preflightAuth(token, { ...params, thread_id: threadId });
}

export async function ensureConversationThread(
  token: string | null,
  params: {
    user_oid: string;
    channel_key: string;
    domain: AgentDomain;
    /** Required by the backend — pass preflight's session_id through. */
    session_id: string;
    correlation_id?: string;
    /** Project anchor for the new thread (migration 090). Set by the
     *  in-project Threads tab; omitted for global/home threads. */
    project_id?: number | null;
  }
): Promise<EnsureConversationThreadResult> {
  // Backend returns EnsureConversationThreadResponse directly — {thread_id,
  // needs_new_thread} — NOT wrapped in a ToolResponse list. Earlier code
  // assumed the same wrapped pattern as preflight-auth, which made the
  // parse silently throw (no `data` field) and the caller's `catch {}`
  // swallowed it, dropping the durable thread_id every turn.
  return call<EnsureConversationThreadResult>(
    '/tools/agents/channel/ensure-conversation-thread',
    token,
    { ...params, domain: toAgentFlowDomain(params.domain) }
  );
}

export async function resetConversation(
  token: string | null,
  params: { user_oid: string; channel_key: string; domain: AgentDomain }
): Promise<void> {
  // Returns ResetConversationResponse directly — {archived: bool}.
  await call<{ archived: boolean }>(
    '/tools/agents/channel/reset-conversation',
    token,
    { ...params, domain: toAgentFlowDomain(params.domain) }
  );
}

/* ----- Item 2: thread list + resume + hydrate -------------------------- */

export interface ConversationListItem {
  id: number;
  thread_id: string;
  channel_key: string;
  domain: AgentDomain;
  title: string | null;
  created_at: string | null;
  last_active_at: string | null;
  archived_at: string | null;
  /** NOT an in-flight indicator despite the name — the backend computes it
   *  as `archived_at IS NULL`, i.e. "not archived". There is no
   *  agent-is-currently-running signal on any conversation response, so do
   *  not render this as a live dot. */
  is_live: boolean;
  /** Last message, trimmed to a preview, and whether a message in this
   *  thread produced an artifact.
   *
   *  OPTIONAL and currently absent: list-conversations returns thread
   *  metadata only. Both are derivable server-side from conversation_messages
   *  (which already stores artifacts_json) in the same query — declared here
   *  so the row renders them the moment the backend supplies them, and
   *  degrades to a title-only row until then. Do NOT fill these by fetching
   *  messages per thread; that is an N+1 across the whole list. */
  last_message_preview?: string | null;
  last_message_role?: 'user' | 'assistant' | null;
  has_artifact?: boolean;
  /** A Foundry turn is executing on this thread RIGHT NOW — the real
   *  in-flight signal, read from the run-lock keyspace, as opposed to
   *  `is_live` above which only means "not archived".
   *
   *  Best-effort: false when Redis is unreachable, and slightly stale by
   *  construction — a replica that died mid-turn leaves its lock until the
   *  TTL expires. Fine for an indicator; don't gate behavior on it. */
  is_running?: boolean;
  /** Project this thread is anchored to (migration 090), or null for
   *  global/home threads. */
  project_id: number | null;
}

/** Project grouping for the thread list (migration 090):
 *   'all'     — no project filter (legacy / cross-project).
 *   'global'  — only un-anchored threads (home sidebar).
 *   'project' — only threads anchored to project_id (project Threads tab). */
export type ConversationScope = 'all' | 'global' | 'project';

export async function listConversations(
  token: string | null,
  params: {
    domain?: AgentDomain;
    limit?: number;
    query?: string;
    scope?: ConversationScope;
    project_id?: number;
  } = {}
): Promise<ConversationListItem[]> {
  const resp = await call<{ conversations: ConversationListItem[] }>(
    '/tools/agents/channel/list-conversations',
    token,
    {
      domain: params.domain ?? 'coordinator',
      limit: params.limit ?? 50,
      ...(params.scope ? { scope: params.scope } : {}),
      ...(params.project_id != null ? { project_id: params.project_id } : {}),
      ...(params.query && params.query.trim() ? { query: params.query.trim() } : {}),
    }
  );
  return resp.conversations ?? [];
}

/** Retroactively anchor a thread to a project (or un-anchor with
 *  project_id=null). Backend gates on project access + thread ownership. */
export async function assignConversationProject(
  token: string | null,
  params: { thread_id: string; project_id: number | null }
): Promise<{ updated: boolean }> {
  return call<{ updated: boolean }>(
    '/tools/agents/channel/assign-conversation-project',
    token,
    params
  );
}

export interface ResumeConversationResult {
  thread_id: string;
  channel_key: string;
  domain: AgentDomain;
  freed_thread_id: string | null;
}

export async function resumeConversation(
  token: string | null,
  params: { thread_id: string }
): Promise<ResumeConversationResult> {
  return call<ResumeConversationResult>(
    '/tools/agents/channel/resume-conversation',
    token,
    params
  );
}

export async function setConversationTitle(
  token: string | null,
  params: { thread_id: string; title: string; overwrite?: boolean }
): Promise<{ updated: boolean }> {
  return call<{ updated: boolean }>(
    '/tools/agents/channel/set-conversation-title',
    token,
    {
      thread_id: params.thread_id,
      title: params.title,
      overwrite: params.overwrite ?? false,
    }
  );
}

export interface ConversationMessageItem {
  id: string | null;
  role: 'user' | 'assistant' | string | null;
  text: string;
  created_at: string | null;
  // Artifacts the assistant produced on this turn (mirrored into SQL
  // by append-message). Populated only on the assistant row, only
  // when the turn produced one; absent on plain text turns.
  artifacts?: ArtifactRef[];
  // Source citations from this assistant turn (mirrored into SQL by
  // append-message). Re-supplies ChatMarkdown's fail-closed allowlist on
  // hydration so cited daedalus-doc:// links stay clickable after refresh.
  // Absent on plain text turns / turns with no citations.
  source_citations?: SourceCitation[];
  // Files the user pinned to this turn (migration 166). Pointers only —
  // `blob_uri` addresses the bytes, `extracted_text` is not mirrored.
  // Populated on the user row; absent on assistant rows and on turns
  // written before the column existed.
  attachments?: AttachmentChip[];
  // Per-turn run metadata (migration 175): correlation id, tools called,
  // terminal status. Assistant rows only; absent on rows written before
  // the column existed and on rows healed by sync-thread-tail.
  run_meta?: RunMeta;
  // An unanswered "Was this answer right?" on this answer (migration 287),
  // returned so the question is still there when the chat is reopened.
  // Assistant rows only; absent almost always.
  feedback_ask?: FeedbackAsk | null;
}

/** "Was this answer right?" on one answer. The server decides which answers
 *  get one (tools_service/agents/answer_feedback.py); the tap goes to
 *  answerFeedback with `ask_id`. */
export interface FeedbackAsk {
  ask_id: number;
  correlation_id?: string | null;
}

export type FeedbackAnswer = 'yes' | 'partly' | 'no' | 'dismissed';

/** Record the person's answer. Partly / No may follow with a second call
 *  carrying the same answer and "What was off?". A 404 means there is no
 *  open question of theirs with that id (answered elsewhere, or expired). */
export async function answerFeedback(
  token: string | null,
  params: { ask_id: number; answer: FeedbackAnswer; comment?: string }
): Promise<{ recorded: boolean }> {
  return call<{ recorded: boolean }>(
    '/tools/agents/channel/answer-feedback',
    token,
    params
  );
}

export interface DeleteConversationResult {
  deleted: boolean;
  was_live: boolean;
  memories_saved: number;
}

export async function deleteConversation(
  token: string | null,
  params: { thread_id: string; save_memory: boolean }
): Promise<DeleteConversationResult> {
  return call<DeleteConversationResult>(
    '/tools/agents/channel/delete-conversation',
    token,
    params
  );
}

export interface UserMemory {
  id: number;
  project_id: number | null;
  content: string;
  category: string | null;
  source_thread_id: string | null;
  source_thread_title: string | null;
  extracted_at: string | null;
}

export async function listMemories(
  token: string | null,
  params: { limit?: number; project_id?: number } = {}
): Promise<UserMemory[]> {
  const resp = await call<{ memories: UserMemory[] }>(
    '/tools/agents/channel/list-memories',
    token,
    { limit: params.limit ?? 100, ...(params.project_id ? { project_id: params.project_id } : {}) }
  );
  return resp.memories ?? [];
}

export async function archiveMemory(
  token: string | null,
  params: { memory_id: number }
): Promise<{ archived: boolean }> {
  return call<{ archived: boolean }>(
    '/tools/agents/channel/archive-memory',
    token,
    params
  );
}

export interface MirrorMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  foundry_item_id?: string;
  // Artifact descriptors to persist alongside the assistant text so
  // chat-reopen can re-render the artifact chips. Server stashes the
  // JSON in conversation_messages.artifacts_json.
  artifacts?: ArtifactRef[];
  // Source citations to persist alongside the assistant text so
  // chat-reopen can re-supply ChatMarkdown's fail-closed allowlist
  // (keeping cited daedalus-doc:// links clickable after refresh).
  // Server stashes the JSON in conversation_messages.source_citations_json.
  source_citations?: SourceCitation[];
  // Files the user pinned to this turn, persisted so the attachment
  // stays findable after the conversation scrolls away. The server keeps
  // the pointer fields and drops the rest, so sending whole chips here
  // does not put 20k of extracted text per file into the mirror.
  attachments?: AttachmentChip[];
  // Per-turn run metadata (assistant rows only): correlation id, tools
  // called, terminal status. Server stashes the JSON in
  // conversation_messages.run_meta_json — the forensic record Prometheus
  // reads when the user reports a problem with a turn.
  run_meta?: RunMeta;
}

export interface AppendMessagesResult {
  inserted: number;
  // The question to show under this turn's answer, when the server asks one.
  feedback_ask?: FeedbackAsk | null;
}

export async function appendMessages(
  token: string | null,
  params: { thread_id: string; messages: MirrorMessage[] }
): Promise<AppendMessagesResult> {
  return call<AppendMessagesResult>(
    '/tools/agents/channel/append-message',
    token,
    params
  );
}

export async function getConversationItems(
  token: string | null,
  params: { thread_id: string; limit?: number }
): Promise<ConversationMessageItem[]> {
  const resp = await call<{ items: ConversationMessageItem[] }>(
    '/tools/agents/channel/get-conversation-items',
    token,
    { thread_id: params.thread_id, limit: params.limit ?? 100 }
  );
  return resp.items ?? [];
}

/** Heal the SQL mirror from the live Foundry conversation and return ONLY the
 *  newly mirrored rows (empty when the tail was already mirrored). This is how
 *  a turn whose HTTP stream died mid-run (ingress cuts streams at ~240s; deep
 *  sweeps run past that) reaches the transcript: the run finishes server-side
 *  and lands in the Foundry thread, and this endpoint copies the missing tail
 *  into SQL. Called by the send-recovery poll and best-effort on rehydration. */
export interface SyncThreadTailResult {
  mirrored: number;
  items: ConversationMessageItem[];
  /** A turn is still executing on the thread. null/absent = unknown. */
  running?: boolean | null;
  /** The owner's unexpired check-in on this thread (a long job waiting for
   *  its next part), so a recovered or reopened conversation can offer
   *  Continue. null = none; absent = a backend that does not say. */
  pending_checkin?: PendingCheckin | null;
}

export async function syncThreadTail(
  token: string | null,
  // state_only: whether a turn is still running, nothing healed (the wait
  // for a turn reopened mid-answer polls this, then heals once).
  params: { thread_id: string; state_only?: boolean }
): Promise<SyncThreadTailResult> {
  return call<SyncThreadTailResult>(
    '/tools/agents/channel/sync-thread-tail',
    token,
    params.state_only ? { thread_id: params.thread_id, state_only: true } : { thread_id: params.thread_id }
  );
}

/* ---------------- Inference Service ---------------- */

export async function runAgent(
  token: string | null,
  domain: AgentDomain,
  req: RunAgentRequest
): Promise<RunAgentResponse> {
  return call<RunAgentResponse>(
    `/generate/foundry/run-agent/${toAgentFlowDomain(domain)}`,
    token,
    req
  );
}

/* ---------------- Artifact panel (in-browser editor) -------------------- */

export interface RenderedColumn {
  header: string;
  key: string;
  width?: number;
  format?: 'text' | 'number' | 'currency' | 'date' | 'percent' | 'boolean' | string;
}

export interface RenderedSheet {
  name: string;
  data_source: string;
  columns: RenderedColumn[];
  rows: Record<string, unknown>[];
  freeze_panes?: string | null;
  sort_by?: string | null;
  sort_order?: 'asc' | 'desc' | null;
}

export interface RenderedArtifact {
  run_id: number;
  version_number: number;
  parent_run_id: number | null;
  document_type_key: string;
  document_title: string;
  output_format: 'xlsx' | 'docx' | 'pptx' | string;
  status: string;
  file_url: string | null;
  sharepoint_path: string | null;
  summary: string;
  // Format-specific fields. xlsx returns sheets[], pptx slides[], docx sections+field_values.
  sheets?: RenderedSheet[];
  slides?: unknown[];
  sections?: unknown[];
  field_values?: Record<string, unknown>;
  project_name?: string;
  report_date?: string;
}

export async function getRenderedArtifact(
  token: string | null,
  params: { run_id: number }
): Promise<RenderedArtifact> {
  const wrap = await call<ToolResponse<RenderedArtifact>>(
    '/tools/documents/get-rendered',
    token,
    params
  );
  if (!wrap.data?.length) throw new ApiError('get-rendered returned no data', 500);
  return wrap.data[0];
}

export interface SaveEditsSheet {
  name: string;
  data_source: string;
  rows: Record<string, unknown>[];
}

export interface SaveEditsResult {
  run_id: number;
  parent_run_id: number;
  version_number: number;
  document_title: string;
  document_type_key: string;
  output_format: string;
  status: string;
  file_url: string;
  sharepoint_path: string | null;
  blob_path: string | null;
  filename: string;
}

export interface EmbedInfo {
  drive_id: string;
  drive_item_id: string;
  web_url: string | null;
  embed_url: string;
  name: string | null;
  output_format: string | null;
}

export interface EmbedParams {
  run_id?: number;
  sharepoint_url?: string;
  drive_id?: string;
  drive_item_id?: string;
}

/** The backend's codes for "opening this file needs your own Microsoft
 *  sign-in": no delegated Graph token came with the request, or Graph said
 *  it expired. The server opens a file under the platform's identity only
 *  for your own run or a document the platform produced that you may read;
 *  anything else opens as you, so SharePoint decides. */
const GRAPH_SIGN_IN_CODES: ReadonlySet<string> = new Set([
  'delegated_token_required',
  'delegated_token_expired',
]);

export function needsGraphSignIn(err: unknown): boolean {
  return err instanceof ApiError && !!err.code && GRAPH_SIGN_IN_CODES.has(err.code);
}

export async function getEmbedInfo(
  token: string | null,
  params: EmbedParams,
  userGraphToken?: string | null
): Promise<EmbedInfo> {
  // When the caller provides a user-scoped Graph token, forward it as
  // X-User-Graph-Token. The backend then uses it to call Graph
  // /preview, so the resulting WOPI token is bound to the real user
  // (isusurer:true), SharePoint checks their access, and Office for the
  // Web renders an editable iframe. Without it the backend opens only
  // your own run or a document the platform produced that you may read
  // (read-only); anything else answers delegated_token_required.
  const extraHeaders = userGraphToken && userGraphToken !== 'mock-graph-token'
    ? { 'X-User-Graph-Token': userGraphToken }
    : undefined;
  const wrap = await call<ToolResponse<EmbedInfo>>(
    '/tools/documents/get-embed-info',
    token,
    params,
    extraHeaders
  );
  if (!wrap.success) {
    const code = wrap.error_code ?? undefined;
    const status = code && GRAPH_SIGN_IN_CODES.has(code) ? 401 : 500;
    throw new ApiError(wrap.error ?? 'get-embed-info failed', status, code);
  }
  if (!wrap.data?.length) throw new ApiError('get-embed-info returned no data', 500);
  return wrap.data[0];
}

export interface SharepointEntry {
  drive_item_id: string;
  drive_id: string;
  name: string;
  web_url: string | null;
  size: number | null;
  last_modified: string | null;
  kind: 'file' | 'folder';
  output_format: string | null;
}

export interface SharepointSite {
  label: string;
  drive_id: string;
  root_path: string;
  writable: boolean;
}

export async function listSites(
  token: string | null
): Promise<SharepointSite[]> {
  const wrap = await call<ToolResponse<SharepointSite>>(
    '/tools/documents/list-sites',
    token,
    {}
  );
  return wrap.data ?? [];
}

export async function listSharepoint(
  token: string | null,
  params: {
    drive_id?: string;
    project_id?: number;
    procore_project_id?: number;
    name_pattern?: string;
    subfolder?: string;
    extensions?: string[];
    max_results?: number;
  }
): Promise<SharepointEntry[]> {
  const wrap = await call<ToolResponse<SharepointEntry>>(
    '/tools/documents/list-sharepoint',
    token,
    {
      extensions: params.extensions ?? ['xlsx', 'docx', 'pptx'],
      max_results: params.max_results ?? 100,
      ...(params.drive_id ? { drive_id: params.drive_id } : {}),
      ...(params.project_id ? { project_id: params.project_id } : {}),
      ...(params.procore_project_id
        ? { procore_project_id: params.procore_project_id }
        : {}),
      ...(params.name_pattern ? { name_pattern: params.name_pattern } : {}),
      ...(params.subfolder ? { subfolder: params.subfolder } : {}),
    }
  );
  return wrap.data ?? [];
}

export async function saveArtifactEdits(
  token: string | null,
  params: { run_id: number; sheets: SaveEditsSheet[] }
): Promise<SaveEditsResult> {
  const wrap = await call<ToolResponse<SaveEditsResult>>(
    '/tools/documents/save-edits',
    token,
    params
  );
  if (!wrap.data?.length) throw new ApiError('save-edits returned no data', 500);
  return wrap.data[0];
}

/* ---------------- Attachments ---------------- */

async function callMultipart<T>(
  path: string,
  token: string | null,
  formData: FormData
): Promise<T> {
  // Intentionally do NOT set Content-Type — the browser computes the
  // multipart boundary for us. Setting it by hand breaks parsing.
  const res = await fetch(`${APIM}${path}`, {
    method: 'POST',
    headers: {
      ...(token && token !== 'mock-token' ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const err = await res.json();
      msg = errorText(err?.error?.message ?? err?.detail, msg);
    } catch {
      /* ignore body parse errors */
    }
    throw new ApiError(msg, res.status);
  }
  return (await res.json()) as T;
}

export async function uploadFileAttachment(
  token: string | null,
  file: File,
  opts: { project_id?: string } = {}
): Promise<AttachmentChip> {
  const form = new FormData();
  form.append('file', file);
  if (opts.project_id) form.append('project_id', opts.project_id);
  const wrap = await callMultipart<ToolResponse<AttachmentChip>>(
    '/tools/attachments/file',
    token,
    form
  );
  if (!wrap.data?.length) {
    throw new ApiError('attachment upload returned no data', 500);
  }
  return wrap.data[0];
}

export async function addUrlAttachment(
  token: string | null,
  url: string
): Promise<AttachmentChip> {
  const wrap = await call<ToolResponse<AttachmentChip>>(
    '/tools/attachments/url',
    token,
    { url }
  );
  if (!wrap.data?.length) {
    throw new ApiError('url attachment returned no data', 500);
  }
  return wrap.data[0];
}

export async function listKnowledgePacks(
  token: string | null
): Promise<AttachmentChip[]> {
  const wrap = await call<ToolResponse<AttachmentChip>>(
    '/tools/attachments/packs/list',
    token,
    { include_inactive: false }
  );
  return wrap.data ?? [];
}

/* ---------------- Admin v2 ---------------- */

export interface AdminScopeRow {
  role: string;
  user_count: number;
}

export async function adminScopeMatrix(token: string | null): Promise<AdminScopeRow[]> {
  const wrap = await call<ToolResponse<AdminScopeRow>>(
    '/tools/admin/scope-matrix/get',
    token,
    {}
  );
  return wrap.data ?? [];
}

export interface AuditLogRow {
  id: number;
  timestamp: string | null;
  user_entra_id: string;
  endpoint: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  correlation_id: string | null;
  metadata: string | null;
}

export async function adminAuditLogsQuery(
  token: string | null,
  filters: {
    limit?: number;
    cursor_id?: number;
    user_entra_id?: string;
    entity_type?: string;
    action?: 'INSERT' | 'UPDATE' | 'DELETE';
  } = {}
): Promise<{ rows: AuditLogRow[]; next_cursor: number | null }> {
  const wrap = await call<ToolResponse<AuditLogRow>>(
    '/tools/admin/audit-logs/query',
    token,
    { limit: filters.limit ?? 100, ...filters }
  );
  const next = wrap.suggested_alternatives?.[0];
  return {
    rows: wrap.data ?? [],
    next_cursor: next ? Number(next) : null,
  };
}

/* ── Platform issues (Prometheus intake triage — AdminV2 tab) ─────── */

export type PlatformIssueStatus = 'open' | 'triaged' | 'resolved' | 'closed';
export type PlatformIssueClassification = 'bug' | 'capability_gap' | 'ux';

export interface PlatformIssueRow {
  id: number;
  title: string;
  expected_behavior: string;
  actual_behavior: string;
  severity: 'low' | 'medium' | 'high';
  project_id: number | null;
  correlation_id: string | null;
  reported_by: string;
  source_agent: string | null;
  status: PlatformIssueStatus;
  created_at: string | null;
  thread_id: string | null;
  classification: PlatformIssueClassification | null;
  recommended_fix: string | null;
  github_issue_number: number | null;
  github_issue_url: string | null;
  github_pr_number: number | null;
  github_pr_url: string | null;
  updated_at: string | null;
}

export interface PlatformIssuesPayload {
  items: PlatformIssueRow[];
  status_counts: Partial<Record<PlatformIssueStatus, number>>;
  sync_pending: number;
}

export async function adminPlatformIssuesList(
  token: string | null,
  filters: {
    status?: PlatformIssueStatus;
    classification?: PlatformIssueClassification;
    severity?: 'low' | 'medium' | 'high';
    limit?: number;
  } = {}
): Promise<PlatformIssuesPayload> {
  const wrap = await call<ToolResponse<PlatformIssuesPayload>>(
    '/tools/platform/admin/issues/list',
    token,
    filters
  );
  return (
    wrap.data?.[0] ?? { items: [], status_counts: {}, sync_pending: 0 }
  );
}

export async function adminPlatformIssuesSetStatus(
  token: string | null,
  params: { issue_ids: number[]; status: PlatformIssueStatus }
): Promise<number> {
  const wrap = await call<ToolResponse<{ changed: number }>>(
    '/tools/platform/admin/issues/set-status',
    token,
    params
  );
  return wrap.data?.[0]?.changed ?? 0;
}

export interface GithubSyncRetrySummary {
  claimed: number;
  filed: number;
  failed: number;
  skipped_flag_off: boolean;
}

export async function adminPlatformIssuesRetrySync(
  token: string | null
): Promise<GithubSyncRetrySummary> {
  const wrap = await call<ToolResponse<GithubSyncRetrySummary>>(
    '/tools/platform/admin/issues/retry-github-sync',
    token,
    {}
  );
  return (
    wrap.data?.[0] ?? { claimed: 0, filed: 0, failed: 0, skipped_flag_off: true }
  );
}

export interface RetentionRow {
  entity_type: string;
  retention_days: number | null;
  updated_at: string | null;
  updated_by: string | null;
}

export async function adminRetentionConfig(token: string | null): Promise<RetentionRow[]> {
  const wrap = await call<ToolResponse<RetentionRow>>(
    '/tools/admin/retention-config/get',
    token,
    {}
  );
  return wrap.data ?? [];
}

export interface DomainDeploymentRow {
  domain: string;
  foundry_agent_id: string | null;
  deployment_name: string | null;
  model_version: string | null;
  active: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export async function adminDomainDeployments(
  token: string | null
): Promise<DomainDeploymentRow[]> {
  const wrap = await call<ToolResponse<DomainDeploymentRow>>(
    '/tools/admin/domain-deployments/get',
    token,
    { active_only: false }
  );
  return wrap.data ?? [];
}

/* ---------------- Admin: Procore sync ---------------- */

/** Result envelope from POST /tools/admin/sync/procore-project-users.
 *  Mirrors the dict shape from tools_service/connectors/procore/sync.py
 *  (sync_projects_from_procore + sync_project_users_from_procore). */
export interface ProcoreSyncResult {
  synced: boolean;
  projects: {
    procore_returned: number;
    upserted: number;
    /** Inactive in Procore AND unknown to us — no row created. */
    skipped_inactive: number;
    /** Tracked rows this run flipped active -> archived. */
    archived: number;
    /** Tracked rows this run flipped archived -> active. */
    reactivated: number;
    errors: string[];
  };
  users: {
    projects_scanned: number;
    projects_with_users: number;
    users_upserted: number;
    syncon_emails_seen: number;
    emails_unresolved: string[];
    errors: string[];
  };
}

/** Freshness probe — POST /tools/admin/procore-sync-status. */
export interface ProcoreSyncStatus {
  last_synced_at: string | null;
  project_count: number;
}

/** Trigger a Procore -> SQL sync. SPA caller must be in
 *  user_platform_roles with role='admin' (the scheduled Container
 *  Apps Job bypasses the check via the absent X-User-OID branch).
 *  Returns the per-step counts from sync_projects_from_procore +
 *  sync_project_users_from_procore. */
export async function adminSyncProcoreProjectUsers(
  token: string | null,
  params: {
    limit_projects?: number;
    include_inactive?: boolean;
    sync_projects?: boolean;
  } = {}
): Promise<ProcoreSyncResult> {
  return call<ProcoreSyncResult>(
    '/tools/admin/sync/procore-project-users',
    token,
    params,
  );
}

/** Read-only — last_synced_at + project_count. */
export async function adminProcoreSyncStatus(
  token: string | null
): Promise<ProcoreSyncStatus> {
  return call<ProcoreSyncStatus>(
    '/tools/admin/procore-sync-status',
    token,
    {},
  );
}

/* ---------------- Admin: Data sources ▸ committed-cost source ---------------- */
/* routers/admin_data_sources.py — platform-admin only, read-only, live. The
   budget review's committed rebuild run for every job
   (db/queries/committed_source_health.py); the nightly Spectrum sync logs the
   same check for the ops alert. */

export type CommittedSourceStatus = 'ok' | 'warn' | 'alert';

export interface CommittedFallbackJob {
  job_number: string;
  description: string | null;
  status_code: string | null;
  projects: { project_id: number; name: string; status: string | null }[];
  outcome: 'fallback' | 'not_attempted';
  reason: string;
  /* absent when outcome is not_attempted: there was nothing to rebuild */
  trusted_total?: number;
  rebuilt_total?: number;
  delta?: number;
  po_open?: number;
}

export interface OrphanSubcontract {
  job_number: string;
  vendor_code: string;
  subcontract_number: string;
  open_amount: number;
  linked: boolean;
  /* the review reads subcontract lines only on the fallback path */
  review_affected: boolean;
}

export interface CommittedSourceHealth {
  status: CommittedSourceStatus;
  reasons: string[];
  checked_at: string;
  flag_enabled: boolean;
  jobs: { checked: number; reconciled: number; fallback: number; not_attempted: number };
  linked_active: { checked: number; reconciled: number; fallback: number; not_attempted: number };
  fallback_jobs: CommittedFallbackJob[];
  linked_not_attempted: CommittedFallbackJob[];
  mirror: {
    rows: number;
    fresh_rows: number;
    oldest_synced_at: string | null;
    newest_synced_at: string | null;
    left_behind_rows: number;
    left_behind_with_committed: number;
    job_summary_synced_at: string | null;
  };
  orphan_subcontracts: {
    count: number;
    open_total: number;
    linked_open_total: number;
    review_affected_total: number;
    items: OrphanSubcontract[];
  };
  thresholds: {
    reconcile_tolerance: number;
    min_mirror_rows: number;
    stale_after_hours: number;
    mirror_lag_alert_hours: number;
    max_linked_fallback: number;
  };
}

export async function adminCommittedSourceHealth(
  token: string | null,
): Promise<CommittedSourceHealth | null> {
  const wrap = await call<ToolResponse<CommittedSourceHealth>>(
    '/tools/admin/data-sources/committed-health', token, {});
  return wrap.data?.[0] ?? null;
}

/* ---------------- Admin: People & roles ---------------- */
/* routers/admin_people.py — platform-admin only. PMs are not granted here:
   the PM of a job is Spectrum's and a nightly pass keeps the marker on them
   (docs/PM_OF_RECORD.md). This screen shows where that pass needs a human,
   and holds the roles Spectrum knows nothing about. */

export type AdminAccountStatus = 'active' | 'disabled' | 'missing';

export interface AdminPersonDepartment {
  code: string | null;
  is_head: boolean;
}

export interface AdminPersonPmJob {
  project_id: number;
  project_number: string | null;
  project_name: string;
  is_pm_of_record: boolean;
}

export interface AdminPerson {
  user_entra_id: string;
  email: string | null;
  display_name: string | null;
  in_directory: boolean;
  platform_roles: string[];
  departments: AdminPersonDepartment[];
  pm_jobs: AdminPersonPmJob[];
  active_team_count: number;
  account: AdminAccountStatus;
}

export interface AdminPeoplePayload {
  people: AdminPerson[];
  department_codes: string[];
  platform_roles: string[];
  /** The signed-in admin's own OID: their row is read-only on this screen. */
  viewer: string;
}

export interface PmReviewReport {
  kind: string;
  [detail: string]: unknown;
}

export interface PmReviewProject {
  project_id: number;
  project_number: string | null;
  project_name: string;
  /** The Spectrum job number the next run links this project to, before it plans. */
  spectrum_link_tonight: string | null;
  pm_of_record: {
    user_entra_id: string;
    email: string | null;
    source: 'spectrum' | 'roster';
    pm_code: string | null;
  } | null;
  next_run: { change: string; user_entra_id: string; email: string | null }[];
  reports: PmReviewReport[];
  extra_markers: { user_entra_id: string; email: string | null }[];
}

export interface PmReviewSharedNumber {
  project_number: string;
  projects: {
    project_id: number;
    project_name: string;
    status: string;
    spectrum_job_id: string | null;
    /** Spectrum's own description of the job with this number. */
    spectrum_description: string | null;
  }[];
}

export interface PmReviewPayload {
  projects: PmReviewProject[];
  shared_numbers: PmReviewSharedNumber[];
}

/* ---------------- Admin: the pause switch ---------------- */
/* tools_service routers/admin_agent_pause.py. Admin only; every change is
   recorded in the audit log with who and why. docs/AGENT_PAUSE.md. */

export interface AgentPauseSetting {
  chat: boolean;
  read_only: boolean;
  domains: string[];
  tools: string[];
  /** One more than the setting it replaced; a change names the one it was
   *  made from (409 when someone changed it meanwhile). */
  rev?: number;
  by?: string | null;
  reason?: string | null;
  at?: string | null;
}

export interface AgentPauseChangeRecord {
  by: string;
  at: string;
  reason: string | null;
  setting: Partial<AgentPauseSetting>;
}

export interface AgentPausePayload {
  setting: AgentPauseSetting;
  /** Redis holds the setting shown, so it is what the agents obey. */
  in_force: boolean;
  redis_ok: boolean;
  specialists: string[];
  /** Every tool an agent can call: the names that can be turned off. */
  known_tools: string[];
  history: AgentPauseChangeRecord[];
}

export async function adminAgentPauseState(token: string | null): Promise<AgentPausePayload> {
  const wrap = await call<ToolResponse<AgentPausePayload>>('/tools/admin/agent-pause/state', token, {});
  const data = wrap.data?.[0];
  if (!data) throw new ApiError('agent-pause state returned no data', 500);
  return data;
}

export async function adminAgentPauseSet(
  token: string | null,
  change: {
    chat: boolean;
    read_only: boolean;
    domains: string[];
    tools: string[];
    reason: string;
    based_on_rev: number;
  },
): Promise<AgentPauseSetting> {
  const wrap = await call<ToolResponse<{ setting: AgentPauseSetting }>>(
    '/tools/admin/agent-pause/set', token, change,
  );
  const data = wrap.data?.[0];
  if (!data) throw new ApiError('agent-pause set returned no data', 500);
  return data.setting;
}

export async function adminPeopleList(token: string | null): Promise<AdminPeoplePayload> {
  const wrap = await call<ToolResponse<AdminPeoplePayload>>('/tools/admin/people/list', token, {});
  return wrap.data?.[0] ?? { people: [], department_codes: [], platform_roles: [], viewer: '' };
}

export async function adminPmReview(token: string | null): Promise<PmReviewPayload> {
  const wrap = await call<ToolResponse<PmReviewPayload>>('/tools/admin/people/pm-review', token, {});
  return wrap.data?.[0] ?? { projects: [], shared_numbers: [] };
}

export async function adminSetPlatformRole(
  token: string | null,
  body: { user_entra_id: string; role: string; grant: boolean },
): Promise<boolean> {
  const wrap = await call<ToolResponse<{ changed: boolean }>>('/tools/admin/people/platform-role', token, body);
  return Boolean(wrap.data?.[0]?.changed);
}

export async function adminSetDepartment(
  token: string | null,
  body: { user_entra_id: string; department_code: string; member: boolean; is_head?: boolean },
): Promise<boolean> {
  const wrap = await call<ToolResponse<{ changed: boolean }>>('/tools/admin/people/department', token, body);
  return Boolean(wrap.data?.[0]?.changed);
}

export async function adminRemoveExtraPmMarker(
  token: string | null,
  body: { user_entra_id: string; project_id: number },
): Promise<boolean> {
  const wrap = await call<ToolResponse<{ changed: boolean }>>('/tools/admin/people/pm-marker/remove', token, body);
  return Boolean(wrap.data?.[0]?.changed);
}

export async function adminLinkSpectrum(
  token: string | null,
  body: { project_id: number },
): Promise<boolean> {
  const wrap = await call<ToolResponse<{ changed: boolean }>>('/tools/admin/people/spectrum-link', token, body);
  return Boolean(wrap.data?.[0]?.changed);
}

/* ---------------- Projects ---------------- */

export interface ProjectRow {
  project_id?: number | string;
  id?: number | string;
  project_name?: string;
  name?: string;
  // Tolerant type — pm_queries.get_my_projects returns whatever the
  // backend shapes it as; the scene reads both to render.
  [key: string]: unknown;
}

/* ---------------- Whoami / role-aware landing ---------------- */

export interface Whoami {
  user_entra_id: string;
  is_executive: boolean;
  is_platform_admin: boolean;
  is_accounting_staff: boolean;
  is_accounting_controller: boolean;
  /** Executive or the payroll_401k role (Arlene, William) — may see the 401(k) Reports. */
  can_payroll_401k?: boolean;
  pm_project_count: number;
  buildr_assignment_count: number | null;
  recommended_landing: '/portfolio' | '/projects' | '/bids' | '/home';
}

export async function whoami(token: string | null): Promise<Whoami | null> {
  const wrap = await call<ToolResponse<Whoami>>(
    '/tools/agents/whoami',
    token,
    {}
  );
  return wrap.data?.[0] ?? null;
}

/* ---------------- Accounting hub — GL anomaly review ---------------- */

export interface GlAnomalyReason { factor: string; detail?: string; z?: number }
export interface GlAnomalyFlag {
  id: number;
  source_key: string;
  company_code: string;
  gl_account: string;
  account_description: string | null;
  fiscal_year: string;
  fiscal_period: string;
  entry_date: string | null;
  amount: number | null;
  job_number: string | null;
  phase_code: string | null;
  cost_type: string | null;
  source_journal: string | null;
  description: string | null;
  anomaly_score: number | null;
  score_rank: number | null;
  reason: { top_factors?: GlAnomalyReason[] };
  model_version: string;
  scored_at: string | null;
  review_status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
}
export interface GlAnomalyWorklist {
  as_of: string | null;
  summary: Record<string, number>;
  returned: number;
  truncated: boolean;
  flags: GlAnomalyFlag[];
}

/** GL anomaly worklist. Uncached: a review action must be reflected on the
    next fetch without a cache-bust dance. */
export async function getGlAnomalies(
  token: string | null,
  opts: { status?: string | null; fiscalYear?: string; fiscalPeriod?: string; limit?: number; includeAll?: boolean } = {},
): Promise<GlAnomalyWorklist | null> {
  const body: Record<string, unknown> = {};
  if (opts.includeAll) body.include_all_statuses = true;
  else if (opts.status !== undefined) body.status = opts.status;
  if (opts.fiscalYear) body.fiscal_year = opts.fiscalYear;
  if (opts.fiscalPeriod) body.fiscal_period = opts.fiscalPeriod;
  if (opts.limit) body.limit = opts.limit;
  const wrap = await call<ToolResponse<GlAnomalyWorklist>>('/tools/accounting/gl-anomalies/list', token, body);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load GL anomalies', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export async function reviewGlAnomaly(
  token: string | null, flagId: number, status: string, note?: string,
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/accounting/gl-anomalies/review', token, {
    flag_id: flagId, status, note: note ?? null,
  });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'review failed', 400, wrap.error_code ?? undefined);
}

export interface GlSuppressionRule {
  id: number; rule_name: string; description: string | null;
  match: Record<string, unknown>; created_by: string | null;
  created_at: string | null; enabled: boolean;
}
export async function listGlSuppressionRules(token: string | null): Promise<GlSuppressionRule[]> {
  const wrap = await call<ToolResponse<GlSuppressionRule>>('/tools/accounting/gl-anomalies/suppression/list', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load rules', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}
export async function createGlSuppressionRule(
  token: string | null, ruleName: string, match: Record<string, unknown>, description?: string,
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/accounting/gl-anomalies/suppression/create', token, {
    rule_name: ruleName, match, description: description ?? null,
  });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'create failed', 400, wrap.error_code ?? undefined);
}
export async function setGlSuppressionRuleEnabled(
  token: string | null, ruleId: number, enabled: boolean,
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/accounting/gl-anomalies/suppression/set-enabled', token, {
    rule_id: ruleId, enabled,
  });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'update failed', 400, wrap.error_code ?? undefined);
}

/* ---------------- Accounting hub — EAC / fade risk ---------------- */

/** eac-margin-v3.3+: a driver is read from the model — `contribution_pts` is
 *  how many more points the model puts at risk because of this reading,
 *  against a typical one: what the model reacts to, not what fixing the
 *  reading would save. `percentile` is where the job sits in the training
 *  population (0-1), context only. `z` exists on v3.0–v3.2 rows only. */
export interface EacRiskDriver {
  factor: string;
  detail?: string;
  /** eac-margin-v3.3+: points the model puts at risk because of this reading,
   *  against a typical one (the model's own sensitivity). Null on the
   *  `margin_earned` lead and the `profile` fallback. */
  contribution_pts?: number | null;
  /** Where the job sits in the training population (context only since v3.3). */
  percentile?: number | null;
  /** v3.0–v3.2 rows only. */
  z?: number | null;
}
/** One fiscal period of the job's trajectory: what it billed and what it
 *  cost. `earned` is deliberately absent — Spectrum reports Earned_Revenue as
 *  zero on every job's period rows, so the v1/v2 key was a data claim the ERP
 *  never made. */
export interface EacSeriesPoint { y: number; p: number; billed: number; cost: number }
export interface EacRiskScore {
  id: number;
  company_code: string | null;
  job_number: string;
  description: string | null;
  project_manager: string | null;
  customer_name: string | null;
  contract_value: number | null;
  /** CO-inclusive contract — the denominator realized margin is measured on. */
  revised_contract: number | null;
  original_est_cost: number | null;
  jtd_cost: number | null;
  progress_pct: number | null;
  /** Always null since eac-margin-v3; see EacSeriesPoint. */
  earned_to_date: number | null;
  billed_to_date: number | null;
  labor_to_date: number | null;
  /** Actual cost booked to date (JC_ACTIVITY_BY_PERIOD). */
  cost_to_date: number | null;
  /** Expected margin on the ORIGINAL contract amount:
   *  (original contract - original cost estimate) / original contract. */
  original_margin_pct: number | null;
  /** Margin on revenue billed so far: (billed - cost) / billed. Equivalently
   *  the percent-complete EAC projection of the final margin. Replaces the
   *  v3 `margin_to_date_pct`, which divided by the CONTRACT and so reported
   *  the unspent share rather than a margin (a job 17% billed read "81.9%"). */
  current_margin_pct: number | null;
  /** 'firm' | 'provisional' (<40% billed — 15.6 pts of median error against
   *  the eventual margin at 30%, so do not extrapolate) | 'too_early' (<10%)
   *  | 'implausible' (outside the ±60% envelope of finished outcomes). The
   *  last two carry a null current_margin_pct. */
  margin_basis: string | null;
  /** The model's forecast of the FINAL margin, stated as a margin: expected
   *  at award minus predicted fade, blended with the accounting identity so
   *  a nearly-billed-out job cannot be forecast somewhere its remaining
   *  billings could never take it. p25 is the worse end of the band. */
  forecast_margin_p25: number | null;
  forecast_margin_p50: number | null;
  forecast_margin_p75: number | null;
  /** Cost booked in the 3 calendar months up to scored_at. On a job billed
   *  past its contract this is pure margin erosion — nothing left to bill it
   *  against — and is what separates "billed out, winding down" from
   *  "billed out, still burning". */
  recent_cost_3p: number | null;
  /** The model's RAW trajectory-fade prediction in points of gross margin
   *  (0.05 = five points), positive = eroded. Drives the run metrics; since
   *  eac-margin-v3.3 the band reads `band_vs_award_pts` instead. NOT
   *  reconciled with money already earned — late in a job it can
   *  disagree (even in sign) with `original_margin_pct −
   *  forecast_margin_p50`, which is the coherent quantity every user-facing
   *  Δ must derive from. Do not render fade_p* as "award minus completion". */
  fade_p25: number;
  fade_p50: number;
  fade_p75: number;
  risk_band: 'low' | 'watch' | 'elevated' | 'high' | string;
  /** The figure the band and the rank read, in points against the award
   *  margin (positive = above it): the model's worse end (calibrated across
   *  jobs since v3.4), or the margin the job has already earned when it is
   *  billed out or the model's view is out of reach (`band_reads`). Null on
   *  rows scored before eac-margin-v3.3. */
  band_vs_award_pts?: number | null;
  band_reads?: 'model' | 'margin_earned' | string | null;
  score_rank: number;
  /** Daedalus project id when the Spectrum job maps to a platform project
   *  (projects.spectrum_job_id); null = unmapped, render plain text. */
  project_id: number | null;
  /** PM display name for the job, resolved from `project_manager` (Spectrum's
   *  own payroll code) through Spectrum's employee lookup. The mapped
   *  project's Procore roster is NOT consulted: it named a different human
   *  than Spectrum on 4 of 26 scored jobs and left 7 more blank, until
   *  2026-09-15. Null only when no source can name the code — render
   *  `pm_code` then, and note an unmapped job still names its PM. */
  pm_name?: string | null;
  /** The Spectrum code behind `pm_name`, echoing `project_manager` in the
   *  shape every other board uses. */
  pm_code?: string | null;
  drivers: { top_factors?: EacRiskDriver[] };
  series: EacSeriesPoint[];
  model_version: string;
  scored_at: string | null;
  /** Durable human verdict (migration 114); null = open worklist item. */
  disposition: EacDisposition | null;
}
export interface EacDisposition {
  status: 'acknowledged' | 'explained' | string;
  note: string | null;
  band_at_disposition: string | null;
  set_by: string | null;
  set_at: string | null;
}
export interface EacRiskRun {
  scored_at: string | null;
  model_version: string;
  /** Which quantity fade_* holds: 'margin_fade' (v3) or
   *  'cost_vs_original_est' (v1/v2 — a different scale entirely). */
  label_kind: string | null;
  train_jobs: number;
  train_rows: number;
  cv_jobs: number | null;
  cv_rows: number | null;
  cv_mae: number | null;
  cv_within_5pts: number | null;
  /** Leave-one-job-out error of the trivial constant predictor, and whether
   *  the model actually beat it. null = a legacy run that never measured it —
   *  render as unknown, never as passed. */
  baseline_mae: number | null;
  baseline_within_5pts: number | null;
  beats_baseline: boolean | null;
  /** Share of training rows that had a realized-cost curve. */
  cost_coverage: number | null;
  scored_jobs: number;
  skipped_reason: string | null;
}
/** Are the stored scores output from an earlier model? Between a deploy that
 *  changes the model and the next scoring run, eac_risk_scores still holds the
 *  OLD model's output. `note` says what that means for this pair of versions:
 *  within eac-margin-v3.x the figures keep their scale and only the band, rank
 *  and reasons may differ. Fixed by a re-score, which no longer means waiting
 *  for the nightly sync. */
export interface EacModelState {
  stale: boolean;
  current_model_version: string;
  current_label_kind: string;
  stored_model_versions: string[];
  note?: string;
}
export interface EacRiskList {
  run: EacRiskRun | null;
  model: EacModelState;
  bands: Record<string, number>;
  returned: number;
  truncated: boolean;
  scores: EacRiskScore[];
}

/** Ranked fade-risk worklist. Uncached — nightly data, cheap read. */
export async function getEacRisk(
  token: string | null,
  opts: { riskBand?: string | null; limit?: number } = {},
): Promise<EacRiskList | null> {
  const body: Record<string, unknown> = {};
  if (opts.riskBand) body.risk_band = opts.riskBand;
  if (opts.limit) body.limit = opts.limit;
  const wrap = await call<ToolResponse<EacRiskList>>('/tools/accounting/eac-risk/list', token, body);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load fade risk', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Set or clear a job's fade-risk disposition (accounting staff / exec).
 *  status: 'acknowledged' | 'explained' | 'clear'. */
export async function reviewEacRisk(
  token: string | null, jobNumber: string, status: string, note?: string,
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/accounting/eac-risk/review', token, {
    job_number: jobNumber, status, note: note ?? null,
  });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'review failed', 400, wrap.error_code ?? undefined);
}

/** One project's fade-risk read (no period series) — the ProjectView chip.
 *  Gate admits accounting/exec/the project's own PM. Returns null for the
 *  NORMAL absences (unmapped project, job not currently scored) so the chip
 *  simply doesn't render; throws only on real failures. */
export type ProjectFadeRisk = Omit<EacRiskScore, 'series'> & {
  project_name: string | null;
  run: EacRiskRun | null;
};
export async function getProjectFadeRisk(
  token: string | null, projectId: number,
): Promise<ProjectFadeRisk | null> {
  const wrap = await call<ToolResponse<ProjectFadeRisk>>(
    '/tools/accounting/eac-risk/for-project', token, { project_id: projectId });
  if (!wrap.success) {
    if (wrap.error_code === 'not_mapped' || wrap.error_code === 'not_found') return null;
    throw new ApiError(wrap.error ?? 'failed to load fade risk', 500, wrap.error_code ?? undefined);
  }
  return wrap.data?.[0] ?? null;
}

export async function listMyProjects(token: string | null): Promise<ProjectRow[]> {
  // Pull a wide page so the client-side Active/Archived filter has real
  // history to work with. 100 (backend default) misses too many archived
  // rows on the exec/unrestricted path.
  const wrap = await cachedCall<ToolResponse<ProjectRow>>(
    '/tools/projects/list',
    token,
    { limit: 500 }
  );
  return wrap.data ?? [];
}

/* ---------------- Buildr (estimator) ---------------- */

export interface BuildrProjectRow {
  id?: number | string;
  name?: string;
  /** Present only on rows the server synthesized from a bid_users assignment
   *  (source === 'syncon_assignment'). These bids were granted by the precon
   *  dept-head via /tools/bids/mgmt/assign and do NOT come back from Buildr's
   *  assigned_to filter. When set, the card navigates straight to the bid
   *  workspace by this id — no resolveBidFromBuildr round-trip. */
  syncon_bid_id?: number;
  /** 'syncon_assignment' for the rows above; absent for live Buildr rows. */
  source?: string;
  // Tolerant — Buildr's /projects payload is wide; the scene reads what
  // it needs and ignores the rest.
  [key: string]: unknown;
}

export type BuildrScope = 'assigned' | 'unrestricted';

export interface ListMyBuildrProjectsResult {
  rows: BuildrProjectRow[];
  /** Server-decided scope. ``unrestricted`` means executives /
      precon-department-heads received every Buildr project; ``assigned``
      means the result was filtered by the caller's ``assigned_to_id``. */
  scope: BuildrScope;
  /** Free-text hint shown when the user has no Buildr identity. */
  hint: string | null;
}

export async function listMyBuildrProjects(
  token: string | null
): Promise<ListMyBuildrProjectsResult> {
  const wrap = await cachedCall<ToolResponse<BuildrProjectRow>>(
    '/tools/buildr/my-projects',
    token,
    {}
  );
  const tags = wrap.suggested_alternatives ?? [];
  const scope: BuildrScope =
    tags.includes('scope:unrestricted') ? 'unrestricted' : 'assigned';
  const hint = tags.find((t) => !t.startsWith('scope:')) ?? null;
  return {
    rows: wrap.data ?? [],
    scope,
    hint,
  };
}

/** Result of POST /tools/bids/resolve-from-buildr — mirrors the dict
 *  built in tools_service/connectors/buildr/sync.py::resolve_bid_from_buildr. */
export interface ResolveBidFromBuildrResult {
  bid_id: number;
  buildr_project_id: string;
  name: string;
  status: string;
  buildr_status_raw: string;
  mapped_status: string;
  /** True when the existing bids row had a terminal status (awarded /
   *  lost / withdrawn / on_hold) and the resolver intentionally did
   *  NOT overwrite it. ``status`` carries the preserved value, not
   *  ``mapped_status``. */
  status_preserved: boolean;
  was_created: boolean;
  was_idempotent: boolean;
  bid_user_added: boolean;
  bid_user_role: string | null;
}

/** Error envelope returned (HTTP 200) when the resolver could not
 *  produce a bid_id but the request was well-formed. Mirrors the
 *  router's ToolResponse(success=false, error_code=...) branches. The
 *  SPA branches on ``error_code`` to render the appropriate hint. */
export interface ResolveBidFromBuildrErrorEnvelope {
  ok: false;
  error_code:
    | 'bids_resolve_disabled'
    | 'bids_resolve_unmappable_status'
    | 'bids_resolve_empty_payload'
    | 'identity_lookup_failed'
    | 'buildr_not_found'
    | 'buildr_rate_limit'
    | string;
  error: string;
}

/* ---------------- Previz (Janus sheet index) ---------------- */

/** One row from previz_sheets — per-page title-block metadata produced
 *  by the previz-service extractor, plus the Slice A raster descriptors.
 *  Blob paths never reach the SPA: images are addressed by ``sheet_id``
 *  through GET /tools/previz/sheet-image, which re-derives bid scope
 *  from the row server-side. ``rasterized_at_unix`` feeds the ?v=
 *  cache-buster (blob paths are reused across re-renders). */
export interface PrevizSheet {
  sheet_id: number;
  page_idx: number;
  sheet_number: string | null;
  title: string | null;
  scale: string | null;
  discipline: string | null;
  revision: string | null;
  has_image: boolean;
  image_width: number | null;
  image_height: number | null;
  rasterized_at_unix: number | null;
}

/** One PDF candidate from previz_list_bid_drawings — produced by the
 *  drawings-likelihood scorer in tools_service/db/queries/previz_queries.py.
 *  Score is a heuristic sum of name-pattern matches, folder-pattern
 *  matches, and a large-file bonus; ``looks_like_drawing_set`` is the
 *  precomputed >=25 threshold flag the SPA uses to auto-select the top
 *  row in the Tools tile's auto-recognize card. */
export interface PrevizDrawingCandidate {
  drive_id: string;
  drive_item_id: string;
  name: string;
  path: string;
  size_bytes: number;
  web_url: string | null;
  modified_at: string | null;
  score: number;
  looks_like_drawing_set: boolean;
}

/** POST /tools/previz/list-bid-drawings — enumerate PDFs in the bid's
 *  SharePoint folder with a drawings-likelihood score. Returns rows
 *  sorted by score DESC then size DESC. Errors (drive unresolved,
 *  folder not found, graph 5xx) come back as a non-200 ToolResponse;
 *  the wrapper throws ApiError carrying the structured error_code.
 *
 *  Sends the user's delegated Graph token (X-Graph-Token): a drive or
 *  folder the caller names is walked AS the user, never with the
 *  platform's app token, and answers delegated_token_required without it.
 *  Without the token only the bid's mirrored files, and its folder when the
 *  platform trusts the record, are listed, and only where the user's
 *  SharePoint libraries include it; anything else needs the token. */
export async function previzListBidDrawings(
  token: string | null,
  params: {
    bid_id: number;
    drive_id?: string;
    subfolder?: string;
    max_results?: number;
  }
): Promise<PrevizDrawingCandidate[]> {
  const wrap = await call<ToolResponse<PrevizDrawingCandidate>>(
    '/tools/previz/list-bid-drawings',
    token,
    params,
    await graphTokenHeader()
  );
  if (!wrap.success) {
    throw new ApiError(
      wrap.error ?? 'list-bid-drawings failed',
      500,
      wrap.error_code ?? undefined,
    );
  }
  return wrap.data ?? [];
}

/** Status-poll response for a sheet set — mirrors the
 *  ArtifactJobStatus contract from inference_service/foundry/artifacts.py. */
export interface SheetSetStatusResponse {
  set_id: number;
  bid_id: number;
  pages_total: number | null;
  pending_job: ArtifactJobStatus;
}

/** Terminal-data response for a `ready` sheet set. */
export interface SheetSetResponse {
  set_id: number;
  bid_id: number;
  status: string;
  pages_total: number | null;
  drive_id: string | null;
  drive_item_id: string | null;
  completed_at_unix: number | null;
  sheets: PrevizSheet[];
}

/** POST /tools/previz/start-sheet-index — enqueue a Buildr+SharePoint
 *  PDF for the previz-service worker. Returns an ArtifactRef whose
 *  ``pending_job`` carries the polling contract. The SPA pushes this
 *  ref onto the artifact panel stack; the panel handles polling and
 *  swaps the rendered view to the terminal artifact when the worker
 *  reports status='ready'.
 *
 *  Idempotent on (bid_id, drive_item_id) — clicking the same SP file
 *  on the same bid returns the existing set's current ArtifactRef.
 *
 *  Sends the user's delegated Graph token (X-Graph-Token): a file outside
 *  the bid's mirrored corpus is checked AS the user before anything is
 *  enqueued (the worker reads it with the platform's app token), and is
 *  refused (item_not_accessible / delegated_token_required) otherwise. */
export async function startSheetIndex(
  token: string | null,
  params: { bid_id: number; drive_id: string; drive_item_id: string }
): Promise<ArtifactRef> {
  const wrap = await call<ToolResponse<ArtifactRef>>(
    '/tools/previz/start-sheet-index',
    token,
    params,
    await graphTokenHeader()
  );
  if (!wrap.success || !wrap.data?.length) {
    throw new ApiError(
      wrap.error ?? 'start-sheet-index returned no data',
      500,
      wrap.error_code ?? undefined
    );
  }
  return wrap.data[0];
}

/** POST /tools/previz/sheet-set-status — single-shot poll. Returns the
 *  current ArtifactJobStatus for a previz set, including the
 *  ``terminal_artifact`` once status='ready'. The SPA fires this on
 *  the cadence advertised by ``pending_job.poll_after_ms``. */
export async function getSheetSetStatus(
  token: string | null,
  params: { set_id: number }
): Promise<SheetSetStatusResponse> {
  const wrap = await call<ToolResponse<SheetSetStatusResponse>>(
    '/tools/previz/sheet-set-status',
    token,
    params
  );
  if (!wrap.success || !wrap.data?.length) {
    throw new ApiError(
      wrap.error ?? 'sheet-set-status returned no data',
      500,
      wrap.error_code ?? undefined
    );
  }
  return wrap.data[0];
}

/** POST /tools/previz/sheet-set — terminal-data fetch. Returns the
 *  per-page sheet metadata once the set is 'ready'. For non-ready sets
 *  ``sheets`` is empty; the caller should poll status first. */
export async function getSheetSet(
  token: string | null,
  params: { set_id: number }
): Promise<SheetSetResponse> {
  const wrap = await call<ToolResponse<SheetSetResponse>>(
    '/tools/previz/sheet-set',
    token,
    params
  );
  if (!wrap.success || !wrap.data?.length) {
    throw new ApiError(
      wrap.error ?? 'sheet-set returned no data',
      500,
      wrap.error_code ?? undefined
    );
  }
  return wrap.data[0];
}

/** One sheet set from GET /tools/previz/sheet-sets — powers the
 *  BidScene Drawings tab. ``file_name`` is recovered from the bid
 *  corpus when the walked PDF is mirrored there (NULL for
 *  manual-browse picks outside the corpus — render "Sheet set #id").
 *  ``sheets_with_images`` = 0 on pre-Slice-A sets awaiting backfill. */
export interface PrevizSheetSetListItem {
  set_id: number;
  status: string;
  pages_total: number | null;
  sheets_with_images: number;
  file_name: string | null;
  drive_id: string | null;
  drive_item_id: string | null;
  created_at_unix: number | null;
  completed_at_unix: number | null;
  error_message: string | null;
  /** Migration 214 — the set this one supersedes (human-set via
   *  /revision/link; null = unlinked, which is most sets). */
  supersedes_set_id: number | null;
}

/** GET /tools/previz/sheet-sets?bid_id= — every sheet set for a bid,
 *  newest first. SPA-direct only (not a Foundry tool). 403 (not 404)
 *  on no bid access, matching the tab's sibling calls so BidScene's
 *  access-denied card fires consistently. */
export async function listSheetSets(
  token: string | null,
  params: { bid_id: number }
): Promise<PrevizSheetSetListItem[]> {
  const wrap = await call<ToolResponse<PrevizSheetSetListItem>>(
    `/tools/previz/sheet-sets?bid_id=${encodeURIComponent(params.bid_id)}`,
    token,
    undefined,
    undefined,
    { method: 'GET' }
  );
  if (!wrap.success) {
    throw new ApiError(
      wrap.error ?? 'sheet-sets failed',
      500,
      wrap.error_code ?? undefined
    );
  }
  return wrap.data ?? [];
}

/** Build the sheet-image URL for a PrevizSheet. The ?v= cache-buster
 *  comes from rasterized_at_unix — a re-rendered page gets a new URL
 *  and skips the 1h private-cache entry for the old bytes. */
export function sheetImageUrl(
  sheet: Pick<PrevizSheet, 'sheet_id' | 'rasterized_at_unix'>,
  kind: 'full' | 'thumb'
): string {
  const v = sheet.rasterized_at_unix ?? 0;
  return `${APIM}/tools/previz/sheet-image?sheet_id=${sheet.sheet_id}&kind=${kind}&v=${v}`;
}

/** GET /tools/previz/sheet-image — fetch one rendered sheet WebP as a
 *  Blob (caller creates/revokes the objectURL). Bearer-in-header only.
 *
 *  429-aware: APIM enforces 100 calls/min/user, and a large drawing
 *  set's thumb rail can brush that ceiling even with viewport-lazy
 *  loading — so a 429 retries with exponential backoff (2s/4s/8s)
 *  instead of surfacing an error chip for a self-healing condition. */
export async function fetchSheetImageBlob(
  token: string | null,
  sheet: Pick<PrevizSheet, 'sheet_id' | 'rasterized_at_unix'>,
  kind: 'full' | 'thumb'
): Promise<Blob> {
  const url = sheetImageUrl(sheet, kind);
  let lastStatus = 0;
  for (let attempt = 0; attempt < 4; attempt++) {
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && token !== 'mock-token' ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (res.ok) return await res.blob();
    lastStatus = res.status;
    if (res.status === 429 && attempt < 3) {
      await new Promise((r) => setTimeout(r, 2000 * 2 ** attempt));
      continue;
    }
    let msg = res.statusText;
    try {
      const err = await res.json();
      msg = errorText(err?.detail ?? err?.error?.message, msg);
    } catch {
      /* binary/empty error body — keep statusText */
    }
    throw new ApiError(msg, res.status);
  }
  throw new ApiError('sheet image rate-limited', lastStatus || 429);
}

/* ---------------- Previz Slice B — 3D scenes ---------------- */

/** The ORIGINATING generation request of a scene, parsed back out of the
 *  stored params_json server-side: the picked pages plus the full config
 *  (wall/door/room overrides included). The SceneViewer seeds its edit
 *  baselines from `config` so a second regeneration carries the first
 *  round's corrections forward instead of silently dropping them. */
export interface PrevizSceneParams {
  page_idxs: number[];
  config: Record<string, unknown>;
}

/** One row from POST /tools/previz/scene-manifest — every 3D scene for
 *  a bid, newest first. `page_idxs` are the floor-plan pages the scene
 *  was generated from (selection order = stacking order). */
export interface PrevizSceneManifestItem {
  scene_id: number;
  set_id: number;
  status: string;
  levels_total: number | null;
  page_idxs: number[];
  /** Optional only for older cached responses — the live backend always
   *  sends it. */
  params?: PrevizSceneParams;
  scene_version: number | null;
  file_name: string | null;
  created_at_unix: number | null;
  completed_at_unix: number | null;
  error_message: string | null;
}

export interface SceneStatusResponse {
  scene_id: number;
  set_id: number;
  bid_id: number;
  levels_total: number | null;
  scene_version: number | null;
  params?: PrevizSceneParams;
  pending_job: ArtifactJobStatus;
}

/** scene.json v1 mirror (previz_service/scene_builder.py). Units FEET;
 *  origin page top-left, x right, y down; the viewer maps plan (x,y) →
 *  world (x, elevation, −y). NO blob paths — ground textures are
 *  addressed by sheet_id via fetchSheetImageBlob; overlays via
 *  sceneFileUrl(kind='overlay'). */
export interface SceneWall {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  thickness_ft: number;
  confidence: number;
  source: 'vector_pair' | 'vector_fill' | 'raster' | 'room_boundary' | 'semantic';
  /** v3.6 — walls authored as ONE object (estimator arc walls) share a
   *  group id; the edit surface treats the group as a unit. */
  group?: string | null;
  /** v3 (semantic route) — what the wall IS; null/absent on v1/v2 routes. */
  role?:
    | 'envelope'
    | 'corridor'
    | 'partition'
    | 'interior'
    | 'core'
    | 'gallery_wall'
    | null;
}

/** v3 (semantic route) — a walkway/gallery floor slab. */
export interface SceneSlab {
  id: string;
  kind: string;
  polygon: [number, number][];
}

/** v2 (space-first) — a detected room. Polygon is the exterior ring in
 *  feet (plan x-right, y-down), winding-normalized; holes are shafts /
 *  stair cores kept as voids. */
export interface SceneRoom {
  id: string;
  label: string | null;
  polygon: [number, number][];
  holes: [number, number][][];
  area_sqft: number;
  leak_suspected: boolean;
}

/** v2 — a doorway (a deliberate gap between two rooms). room_b null =
 *  exterior door. */
export interface SceneOpening {
  id: string;
  room_a: string;
  room_b: string | null;
  x: number;
  y: number;
  width_ft: number;
  door_key: string | null;
  /** v3 semantic — what the opening IS: a drawn swing, or a door-width
   *  gap in a continuous run with no swing (cased/pocket opening).
   *  Absent/null on v2 routes. */
  kind?: 'door_swing' | 'cased_gap' | null;
}

/** v2 — the correction-review surface: a detected door swing. */
export interface SceneDoor {
  key: string;
  x: number;
  y: number;
  radius_ft: number;
  status: 'sealed' | 'removed' | 'added';
}

export interface SceneLevel {
  level_index: number;
  page_idx: number;
  sheet_id: number | null;
  sheet_number: string | null;
  title: string | null;
  status: 'ok' | 'failed';
  error: string | null;
  scale: {
    raw: string | null;
    points_per_foot: number;
    method: string;
    confidence: number;
  } | null;
  page_size_ft: { w: number; h: number } | null;
  raster: {
    width_px: number;
    height_px: number;
    px_per_ft: number | null;
    rasterized_at_unix: number | null;
  } | null;
  level_elevation_ft: number;
  height_ft: number | null;
  walls: SceneWall[];
  // v2 additive — absent (or []) on v1 / wall-fallback levels.
  rooms?: SceneRoom[];
  openings?: SceneOpening[];
  doors?: SceneDoor[];
  // v3 additive — absent (or []) off the semantic route.
  slabs?: SceneSlab[];
  // v3 additive — per-level build stats; only what the UI reads is typed.
  stats?: {
    route?: string;
    semantic?: {
      jamb_stub_drops?: unknown[];
      jambs_restored?: unknown[];
      fill_cuts?: unknown[];
    } | null;
  } | null;
}

export interface SceneFile {
  schema: string;
  version: 1 | 2 | 3;
  generator_version: string;
  scene_id: number;
  set_id: number;
  bid_id: number;
  units: string;
  default_height_ft: number;
  levels: SceneLevel[];
}

/** @deprecated use SceneFile (v1|v2 union). Kept for import stability. */
export type SceneFileV1 = SceneFile;

/** POST /tools/previz/generate-scene — enqueue 3D scene generation.
 *  Returns an ArtifactRef with pending_job (poll via getSceneStatus).
 *  Idempotent per (set_id, identical selection); a previously failed
 *  identical request is requeued in place. */
export async function generateScene(
  token: string | null,
  params: { set_id: number; page_idxs: number[]; config?: Record<string, unknown> }
): Promise<ArtifactRef> {
  const wrap = await call<ToolResponse<ArtifactRef>>(
    '/tools/previz/generate-scene',
    token,
    params
  );
  if (!wrap.success || !wrap.data?.length) {
    throw new ApiError(
      wrap.error ?? 'generate-scene failed',
      500,
      wrap.error_code ?? undefined
    );
  }
  return wrap.data[0];
}

/** POST /tools/previz/scene-status — single-shot poll. */
export async function getSceneStatus(
  token: string | null,
  params: { scene_id: number }
): Promise<SceneStatusResponse> {
  const wrap = await call<ToolResponse<SceneStatusResponse>>(
    '/tools/previz/scene-status',
    token,
    params
  );
  if (!wrap.success || !wrap.data?.length) {
    throw new ApiError(
      wrap.error ?? 'scene-status returned no data',
      500,
      wrap.error_code ?? undefined
    );
  }
  return wrap.data[0];
}

/** POST /tools/previz/scene-manifest — all 3D scenes for a bid. */
export async function listSceneManifest(
  token: string | null,
  params: { bid_id: number }
): Promise<PrevizSceneManifestItem[]> {
  const wrap = await call<ToolResponse<PrevizSceneManifestItem>>(
    '/tools/previz/scene-manifest',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(
      wrap.error ?? 'scene-manifest failed',
      500,
      wrap.error_code ?? undefined
    );
  }
  return wrap.data ?? [];
}

/** Scene-file URL builder. `?v=<completed_at_unix>` cache-busts — the
 *  blob is rewritten only by the requeue-on-failure path, which bumps
 *  completed_at, so re-generations get fresh URLs past the 1h private
 *  cache. */
export function sceneFileUrl(
  scene: { scene_id: number; completed_at_unix: number | null },
  kind: 'scene' | 'overlay' = 'scene',
  pageIdx?: number
): string {
  const v = scene.completed_at_unix ?? 0;
  const overlay = kind === 'overlay' && pageIdx != null ? `&page_idx=${pageIdx}` : '';
  return `${APIM}/tools/previz/scene-file?scene_id=${scene.scene_id}&kind=${kind}${overlay}&v=${v}`;
}

/** GET /tools/previz/scene-file — fetch + parse scene.json with the
 *  same Bearer/429-ladder transport as fetchSheetImageBlob, then a
 *  hard version gate (unknown scene.json version → typed error the
 *  caller renders as "viewer update required", never a crash). */
export async function fetchSceneFile(
  token: string | null,
  scene: { scene_id: number; completed_at_unix: number | null }
): Promise<SceneFile> {
  const url = sceneFileUrl(scene, 'scene');
  let lastStatus = 0;
  for (let attempt = 0; attempt < 4; attempt++) {
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && token !== 'mock-token' ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (res.ok) {
      const doc = (await res.json()) as SceneFile;
      if (doc.version !== 1 && doc.version !== 2 && doc.version !== 3) {
        throw new ApiError(
          `scene version ${doc.version} requires a viewer update`,
          409,
          'previz_scene_version_unsupported'
        );
      }
      return doc;
    }
    lastStatus = res.status;
    if (res.status === 429 && attempt < 3) {
      await new Promise((r) => setTimeout(r, 2000 * 2 ** attempt));
      continue;
    }
    let msg = res.statusText;
    try {
      const err = await res.json();
      msg = errorText(err?.detail ?? err?.error?.message, msg);
    } catch {
      /* empty error body — keep statusText */
    }
    throw new ApiError(msg, res.status);
  }
  throw new ApiError('scene file rate-limited', lastStatus || 429);
}

/** POST /tools/bids/resolve-from-buildr — lazy resolver that turns a
 *  Buildr project id into a Syncon bid_id (and seeds bid_users with
 *  the caller on first resolve). Gated behind
 *  ``BIDS_BUILDR_RESOLVE_ENABLED`` on both Container Apps; when the
 *  tools-side flag is off the SPA gets ``error_code='bids_resolve_disabled'``.
 *
 *  Returns ``ok: true`` with the resolved data on success, ``ok: false``
 *  with the structured error envelope on a 200-but-no-data response.
 *  Throws ApiError on transport-layer failures (403 cohort gate, 502
 *  Buildr upstream, etc.) so the caller can handle network errors
 *  separately from domain failures. */
export async function resolveBidFromBuildr(
  token: string | null,
  params: { buildr_project_id: string }
): Promise<
  | ({ ok: true } & ResolveBidFromBuildrResult)
  | ResolveBidFromBuildrErrorEnvelope
> {
  const wrap = await call<ToolResponse<ResolveBidFromBuildrResult>>(
    '/tools/bids/resolve-from-buildr',
    token,
    params
  );
  if (wrap.success && wrap.data?.length) {
    return { ok: true, ...wrap.data[0] };
  }
  return {
    ok: false,
    error_code: wrap.error_code ?? 'unknown',
    error: wrap.error ?? 'Unknown resolver failure',
  };
}

export async function getProjectSummary(
  token: string | null,
  params: { project_id?: number; project_number?: string; name_pattern?: string }
): Promise<ProjectRow | null> {
  const wrap = await call<ToolResponse<ProjectRow>>(
    '/tools/projects/summary',
    token,
    params
  );
  return wrap.data?.[0] ?? null;
}

/* ----- Project workspace panels (team + activity) --------------------- */

/** One row from pm_queries.get_project_team (POST /tools/projects/team).
 *  SQL-scoped to the caller's project membership (non-executives must be
 *  on the project; see tools_service/db/queries/pm_queries.py). */
export interface ProjectTeamMember {
  user_email: string;
  /** Procore job title (e.g. "Project Executive", "Superintendent") — the
   *  human-facing role, added by migration 061. This is what the team roster
   *  displays. Distinct from `role`, which is the internal access marker
   *  ('pm', set on bid award) and must not be shown as a job title. */
  job_title: string | null;
  role: string | null;
  is_department_head: boolean | null;
  assigned_at: string | null;
}

export async function getProjectTeam(
  token: string | null,
  params: { project_id: number }
): Promise<ProjectTeamMember[]> {
  const wrap = await call<ToolResponse<ProjectTeamMember>>(
    '/tools/projects/team',
    token,
    params
  );
  // get_project_team returns a 200 with success=false on access-denied /
  // internal errors. Mirror the house pattern (previzListBidDrawings,
  // startSheetIndex, getSheetSet) and throw so the caller renders an error
  // state, not a misleading empty roster.
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load team', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

// ── Per-job financials (Spectrum WIP mirror) ────────────────────────────────
/** Access is an ALLOW-list enforced server-side: executives + PMs assigned to
 *  THIS project. Everyone else gets a 403 — callers use that as the signal to
 *  hide the financials surface entirely (PreconHub probe pattern). */
export interface ProjectFinancials {
  linked: boolean;
  project_id: number;
  project_name: string | null;
  as_of: string | null;
  // Everything below is present only when linked=true.
  job_number?: string;
  description?: string | null;
  status_code?: string | null;
  customer_name?: string | null;
  /** Spectrum's raw payroll code, kept for existing consumers. Show `pm_name`. */
  project_manager?: string | null;
  pm_code?: string | null;
  /** The PM of the job, named from Spectrum (db.queries.spectrum_pm). */
  pm_name?: string | null;
  original_contract?: number;
  revised_contract?: number;
  change_orders?: number;
  pending_cr_amount?: number;
  pending_cr_count?: number;
  jtd_cost?: number;
  billed?: number;
  committed_cost?: number;
  phase_est_cost?: number | null;
  phase_original_est_cost?: number | null;
  cash_received?: number;
  open_ar?: number;
  pct_billed?: number | null;
  pct_budget_consumed?: number | null;
  buyout_pct?: number | null;
  /* ── The three margins. Each pairs a cost with the contract of its own
     era; mixing them is what made the old `margin_at_bid` unusable. ── */

  /** EXPECTED at award: (original contract − original estimate) / original
   *  contract. Both terms pre-change-order. */
  original_margin?: number | null;
  original_margin_pct?: number | null;
  /** CURRENT, from actuals: (billed − cost) / billed. Both terms actuals, so
   *  it needs nobody to maintain an estimate. Null when `margin_basis` is
   *  'too_early'. Same measure the EAC board shows, so the two agree. */
  current_margin?: number | null;
  current_margin_pct?: number | null;
  /** 'too_early' (<10% billed) | 'implausible' (ratio outside anything the
   *  company has ever finished at — the denominator, not the performance)
   *  | 'provisional' (<40% — show with a caveat) | 'firm'. The first two
   *  carry a null value: render the state, never a number. */
  margin_basis?: string | null;
  /** Where the job lands on the FORECAST — both terms post-CO. Projected
   *  cost = this project's card forecast, cost to date + cost to complete per
   *  code (ADR-003 D16), the same figure the WIP schedule and the Accounting
   *  boards print. `estimate_stale` = Spectrum's current estimate, job-wide,
   *  sits below the cost already booked. */
  estimate_margin?: number | null;
  estimate_margin_pct?: number | null;
  estimate_stale?: boolean | null;
  /** Same figures under the WIP schedule's names. */
  projected_margin?: number | null;
  projected_margin_pct?: number | null;
  projected_cost?: number | null;
  projection_basis?: ProjectionBasis | null;
  /** False when the caller asked without it (the SPA never prints it here). */
  projection_included?: boolean;
  no_projection_reason?: NoProjectionReason | null;
  forecast_flags?: ProjectionFlag[];
  /** How far the current estimate has grown past the at-bid budget — a pure
   *  cost delta. > 0 = estimate has grown. */
  cost_fade?: number | null;
  backlog?: number;
  est_complete_date?: string | null;
  flags?: { key: string; label: string }[];
  /** Per-month billed, on ACCOUNTING periods ('01'..'13' — period 13 is an
   *  adjustment period with no calendar month). This is what the staleness
   *  flags are computed from; leave its semantics alone. */
  billing?: { month: string; billed: number }[];
  /** Billed AND cost on ONE calendar-month grain, both read from the same WIP
   *  snapshot, for the billed-vs-cost comparison.
   *
   *  Separate from `billing` on purpose: cost only exists as a job-to-date
   *  running total on the snapshots, so a month's cost is a delta between
   *  month-ends. Plotting that calendar-dated cost against fiscal-period
   *  billed would put two different time axes on one chart.
   *
   *  Empty when the job has fewer than two month-end snapshots to difference
   *  — the chart then falls back to single-series `billing`. A `cost` may be
   *  NEGATIVE on a retroactive posting; that is a real correction. */
  billing_vs_cost?: { month: string; billed: number; cost: number }[];
}
export async function getProjectFinancials(
  token: string | null,
  params: { project_id: number }
): Promise<ProjectFinancials | null> {
  // The probe and the Overview/Drift/CO tabs never print the projection, so
  // they ask without it: a page view never pays for a forecast (the card
  // loads its own from /financials/job-forecast).
  const wrap = await call<ToolResponse<ProjectFinancials>>(
    '/tools/executive/financials/job',
    token,
    { ...params, include_projection: false }
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load financials', 500, wrap.error_code ?? undefined);
  }
  return wrap.data?.[0] ?? null;
}

/** Project card forecast (db/queries/job_forecast.py): cost to date + cost to
 *  complete per (phase, cost type) code. Same allow-list gate as the job card.
 *  A separate call so the access probe above stays cheap.
 *
 *  Each code's cost to complete has one `basis`:
 *    committed            bought sub / PO: its open commitment
 *    unbought_budget      sub code nobody has bought: remaining budget
 *    gc_run_rate          Div 01: weekly spend × weeks to the finish
 *    gc_remaining_budget  Div 01 not started / no history: remaining budget
 *    gc_past_finish       Div 01, finish passed, still posting: 13 weeks of spend
 *    gc_finished          Div 01, finish passed, nothing posted lately: $0
 *    remaining_budget     materials / equipment / other: remaining budget
 *    budget_review        the PM's number from the latest finished review
 *  cost_to_date + Σ buckets === forecast_cost. */
export type ForecastBasis =
  | 'committed' | 'unbought_budget' | 'gc_run_rate' | 'gc_remaining_budget'
  | 'gc_past_finish' | 'gc_finished' | 'remaining_budget' | 'budget_review';
export interface ForecastCode {
  phase_code: string; cost_type: string; description: string | null;
  is_subcontract: boolean; bought: boolean;
  new_budget: number; cost_to_date: number; open_committed: number;
  /** Calendar weekly average over the two closed months; null = no history. */
  weekly_spend: number | null;
  cost_to_complete: number; basis: ForecastBasis; from_review: boolean;
  forecast: number;
  /** new_budget − forecast: negative = forecast over budget. */
  variance: number;
}
export interface ForecastFlag {
  key: 'confirm_buyout' | 'finish_passed' | 'no_finish' | 'schedule_stale' | 'finish_disagrees'
    | 'estimate_below_cost' | 'ctd_differs_from_jtd' | 'review_ignored' | 'review_stale'
    | 'review_basis_changed' | 'bought_since_review' | 'cost_moved_since_review'
    /** The WIP schedule, boards and risk board read this job through another
     *  linked project (its schedule and review), so their figure can differ. */
    | 'portfolio_project_differs';
  label: string;
  /** Dollars — except finish_passed, where it is $/wk still posting. */
  amount: number | null;
  codes: string[];
}

/** The card's flags that say part of a projected cost rests on a judgment a
 *  person should confirm, as every portfolio surface carries them
 *  (spectrum_projection.forecast_flags, assessment EAC-2). `amount` is always
 *  the dollars of the projected cost the flag concerns: the buyout savings
 *  counted, or the 13 weeks of general conditions carried past the finish
 *  (`weekly_spend` × `weeks`). A per-job payload may also carry
 *  'portfolio_project_differs' (no amount). */
export interface ProjectionFlag {
  key: 'confirm_buyout' | 'finish_passed' | 'portfolio_project_differs';
  label: string;
  /** Dollars of the projected cost — NOT the card's $/wk for finish_passed. */
  amount: number | null;
  /** 'usd' on every portfolio flag (absent on 'portfolio_project_differs'). */
  unit?: 'usd';
  codes: string[];
  weekly_spend?: number | null;
  weeks?: number | null;
}
/** Per flag: the jobs carrying it and the dollars of the projected total it
 *  concerns (summed server-side). */
export type ProjectionFlagTotals = Partial<Record<'confirm_buyout' | 'finish_passed', { jobs: string[]; amount: number }>>;
/** Why a row carries no projected cost: its forecast could not be computed
 *  ('unavailable'), Spectrum's job record carries a phase budget but no phase
 *  line is mirrored ('no_phase_lines'), or there is no budget ('no_budget'). */
export type NoProjectionReason = 'unavailable' | 'no_phase_lines' | 'no_budget';
export interface JobForecast {
  linked: boolean; project_id: number; project_name: string | null;
  job_number?: string; as_of?: string | null;
  revised_contract?: number; jtd_cost?: number; billed_pct?: number | null;
  budget?: number; cost_to_date?: number;
  /** null when the job carries no budget (no_forecast_reason 'no_budget'). */
  forecast_cost?: number | null; forecast_margin?: number | null; forecast_margin_pct?: number | null;
  no_forecast_reason?: 'no_budget' | null;
  buckets?: { committed: number; unbought_budget: number; gc_to_finish: number;
              other_remaining: number; per_review: number };
  /** The share of cost to complete already under contract: Σ per code of
   *  min(open committed, cost to complete) — whatever rule produced the code's
   *  figure (a reviewed code's open subcontract still counts). The bar's
   *  "open commitments" segment. */
  committed_to_complete?: number;
  finish?: { date: string | null; source: 'schedule' | 'procore' | null; weeks: number | null;
             /** Exact days to the finish — the per-code arithmetic shown on screen. */
             days?: number | null;
             schedule_finish: string | null; procore_completion: string | null;
             schedule_status_date: string | null };
  review?: { review_id: number; reviewed_at: string | null; reviewer: string | null;
             completion_date: string | null; codes: number } | null;
  committed_basis?: 'phase' | 'subcontract_plus_job_po';
  rate_months?: string[];
  flags?: ForecastFlag[];
  codes?: ForecastCode[];
}
export async function getJobForecast(
  token: string | null,
  params: { project_id: number }
): Promise<JobForecast | null> {
  const wrap = await call<ToolResponse<JobForecast>>(
    '/tools/executive/financials/job-forecast',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load forecast', 500, wrap.error_code ?? undefined);
  }
  return wrap.data?.[0] ?? null;
}

/** Trade-package buyout per phase. Same allow-list gate as the job card. */
export interface BuyoutVendor {
  vendor_code: string; vendor_name: string | null; subcontract_number: string;
  trade: string | null; amount: number; billed_to_date: number;
}
export interface BuyoutPhase {
  phase_code: string; description: string | null; budget: number;
  committed_subs: number; sub_billed: number; buyout_pct: number | null;
  remaining_budget: number; vendors: BuyoutVendor[];
}
export interface JobBuyout {
  linked: boolean; project_id: number; project_name: string | null;
  job_number?: string; as_of: string | null;
  rows?: BuyoutPhase[];
  totals?: { budget: number; committed_subs: number; sub_billed: number;
             phases_bought: number; phases_unbought: number };
}
export async function getProjectBuyout(
  token: string | null,
  params: { project_id: number }
): Promise<JobBuyout | null> {
  const wrap = await call<ToolResponse<JobBuyout>>(
    '/tools/executive/financials/job-buyout',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load buyout', 500, wrap.error_code ?? undefined);
  }
  return wrap.data?.[0] ?? null;
}

/** Budget vs actual per phase (Z_SYNCON_EXCEL_BUDGET mirror). Same allow-list
 *  gate as the job card. pct_consumed = cost_to_date ÷ current_estimate;
 *  variance = current_estimate − (original_budget + budget_changes).
 *  open_committed_cost = the OPEN balance of the phase's commitments (ordered
 *  less received) — not total committed; open_committed_basis says so. */
export interface BudgetPhase {
  phase_code: string; description: string | null;
  original_budget: number; budget_changes: number; current_estimate: number;
  cost_to_date: number; open_committed_cost: number;
  pct_consumed: number | null; variance: number;
}
export interface JobBudgetActual {
  linked: boolean; project_id: number; project_name: string | null;
  job_number?: string; as_of: string | null;
  rows?: BudgetPhase[];
  totals?: { original_budget: number; budget_changes: number; current_estimate: number;
             cost_to_date: number; open_committed_cost: number; variance: number;
             pct_consumed: number | null };
  open_committed_basis?: string;
}
export async function getProjectBudgetActual(
  token: string | null,
  params: { project_id: number }
): Promise<JobBudgetActual | null> {
  const wrap = await call<ToolResponse<JobBudgetActual>>(
    '/tools/executive/financials/job-budget-actual',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load budget', 500, wrap.error_code ?? undefined);
  }
  return wrap.data?.[0] ?? null;
}

/** Budget Review (financials → review). Phase + COST-TYPE grain — unlike
 *  BudgetPhase, cost types are NOT rolled up, because Subcontract rows and
 *  general-conditions rows are projected on different bases. Same allow-list
 *  gate as the job card. */
export interface BudgetReviewRow {
  phase_code: string; cost_type: string; is_subcontract: boolean;
  description: string | null;
  /** Budgets from the phase master (JC_PHASE_MASTER): new budget =
   *  maintained est cost; previous changes = growth over original. CTD from
   *  the by-period mirror; committed = OPEN commitments (revised − billed),
   *  so sub COs — deductive or additive — are honored.
   *
   *  What the committed number covers depends on
   *  BudgetReviewData.committed_basis. On 'phase' it is every open
   *  commitment on this cost code, purchase orders included, and there is
   *  no "PO" row. On 'subcontract_plus_job_po' it is subcontract balances
   *  only (reconciled per subcontract against the header mirror), and the
   *  trailing "PO" pseudo row carries job-wide open purchase-order
   *  committed — job grain is all the stock Spectrum views expose. */
  original_budget: number; previous_budget_changes: number; new_budget: number;
  cost_to_date: number; committed_costs: number; remaining_budget: number;
  /** This phase's own spend over the last two closed months
   *  (JC_ACTIVITY_BY_PERIOD mirror), as a weekly figure — normalized to a
   *  40-hour week by posted payroll hours when the phase has them
   *  (payroll posts whole weeks; a calendar divisor mis-weeks staffed
   *  phases). $0 means the phase really posted nothing; null only when the
   *  job has no by-period history mirrored at all. Can be negative on a
   *  retroactive correction — display honestly; clamp only the suggestion. */
  recent_weekly_cost: number | null;
  /** The spend behind the rate, aligned with BudgetReviewData.rate_months —
   *  so the UI can show the math, not just the answer. Null with the rate. */
  recent_month_costs: number[] | null;
  /** Payroll hours behind each month's spend (0 where none posted) — the
   *  hours leg of the rate math. Null with the rate. */
  recent_month_hours: number[] | null;
}
export interface BudgetReviewData {
  linked: boolean; project_id: number; project_name: string | null;
  job_number?: string; as_of: string | null;
  est_complete_date?: string | null;
  /** The completion input's default: the current schedule's finish, else
   *  Procore's completion date — whichever is first in the future (the same
   *  finish the project card forecasts to). Null when neither is: the screen
   *  then falls back to est_complete_date as before. Spectrum's own date was
   *  past or blank on 17 of 21 linked jobs (2026-09-30). */
  default_completion_date?: string | null;
  completion_source?: 'schedule' | 'procore' | null;
  /** The finished review the project card is using, when one is in force —
   *  shown so the reviewer knows finishing replaces it. Informational: the
   *  suggestions on the review screen are its own (controller-validated) rule. */
  review?: { reviewed_at: string | null; reviewer: string | null;
             completion_date: string | null; codes: number } | null;
  rows?: BudgetReviewRow[];
  totals?: { original_budget: number; previous_budget_changes: number; new_budget: number;
             cost_to_date: number; committed_costs: number; remaining_budget: number;
             nonsub_cost_to_date: number };
  /** The two closed months behind every recent_weekly_cost, e.g.
   *  ["2026-06", "2026-07"] — for honest display next to the rates. */
  rate_months?: string[];
  /** Where the committed column came from. 'phase' = every commitment on
   *  its own cost code, purchase orders included. 'subcontract_plus_job_po'
   *  = subcontract balances per phase, with POs pooled into the trailing
   *  `PO` row because the phase-grain rebuild did not tie out to the
   *  job-grain total. The table says which, so a $0 committed on a cost
   *  code that has a PO reads as a known limit rather than a fact. */
  committed_basis?: 'phase' | 'subcontract_plus_job_po';
}
export async function getBudgetReviewData(
  token: string | null,
  params: { project_id: number }
): Promise<BudgetReviewData | null> {
  const wrap = await call<ToolResponse<BudgetReviewData>>(
    '/tools/budget-review/data',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load budget review', 500, wrap.error_code ?? undefined);
  }
  return wrap.data?.[0] ?? null;
}

export interface BudgetReviewFinishResult {
  project_id: number; job_number: string;
  document_title: string; completion_date: string; sharepoint_path: string;
  document_filename: string; document_file_url: string | null;
  spreadsheet_filename: string; spreadsheet_file_url: string | null;
  totals: Record<string, number>;
  /** The review was kept for the project card's forecast. False = the
   *  documents landed but the card still shows the automatic forecast. */
  saved_for_forecast?: boolean;
}
/** Renders the review package (narrative docx + workbook xlsx) to SharePoint.
 *  Carries ONLY the reviewer's judgment — the server re-reads Spectrum
 *  figures from the mirror, so the client can never alter them. */
export async function finishBudgetReview(
  token: string | null,
  params: {
    project_id: number;
    completion_date: string;                       // YYYY-MM-DD
    /** zeroed = set to $0 with "Zero"; carried = set to the remaining budget
     *  with "Carry remaining budget". The narrative names those rows. */
    etc_rows: {
      phase_code: string; cost_type: string; estimated_cost_to_complete: number; note?: string;
      zeroed?: boolean; carried?: boolean;
    }[];
    monthly_projection: { month: string; pct_complete: number }[];
    notes?: string;
    avg_weekly_cost?: number;
  }
): Promise<BudgetReviewFinishResult> {
  const wrap = await call<ToolResponse<BudgetReviewFinishResult>>(
    '/tools/budget-review/finish',
    token,
    params
  );
  if (!wrap.success || !wrap.data?.[0]) {
    throw new ApiError(wrap.error ?? 'failed to generate the review package', 500, wrap.error_code ?? undefined);
  }
  // A review kept for the card steers the projected cost every portfolio
  // read prints (WIP, boards, risk): drop this tab's cached copies so the
  // next read shows it. The server clears its own memos on this replica.
  if (wrap.data[0].saved_for_forecast) {
    invalidateCachedCalls('/tools/executive/');
    // Accounting ▸ Projections reports the latest finished review.
    invalidateCachedCalls('/tools/accounting/projections');
  }
  return wrap.data[0];
}

// ── Accounting ▸ Projections ─────────────────────────────────────────────────
// Mirrors tools_service/db/queries/accounting_projections.py. Projected %: per
// linked active job, the cumulative % complete projected for each coming
// month — the controller's adjustment, else the latest finished budget
// review's curve, else what the review screen projects today.

const PROJECTIONS = '/tools/accounting/projections';

export interface ProjectedPctReview {
  review_id: number;
  /** Company-calendar date the review was finished. */
  reviewed_on: string;
  reviewer: string | null;
  completion_date: string | null;
  /** False: finished before the monthly % was kept, so its straight line. */
  curve_saved: boolean;
}

export interface ProjectedPctJob {
  job_number: string;
  project_id: number;
  project_name: string | null;
  /** review = latest finished budget review; screen = what the review screen
   *  projects today; unavailable = the projection could not be built. */
  source: 'review' | 'screen' | 'unavailable';
  review: ProjectedPctReview | null;
  completion_date: string | null;
  completion_source: 'review' | 'schedule' | 'procore' | 'spectrum' | 'six_months' | null;
  projected_final: number | null;
  cost_to_date: number | null;
  /** Today's cost to date ÷ the projected final in force, 0–100. */
  actual_pct: number | null;
  start_pct: number | null;
  /** Aligned with ProjectedPct.months: what the table shows. */
  values: (number | null)[];
  /** The review / screen figure under any adjustment. */
  base: (number | null)[];
  overridden: boolean[];
  /** The latest shown adjustment: who, and the company-calendar day. */
  edited: { by: string | null; on: string } | null;
  /** The review in force, waiting for his choice because a shown adjustment
   *  was typed without it on the row (decided by which review he saw, not by
   *  time); `values` aligned with months. */
  pending_review: (ProjectedPctReview & { values: (number | null)[] }) | null;
  flags: { key: string; label: string }[];
}

export interface ProjectedPct {
  today: string;
  as_of: string | null;
  /** 'YYYY-MM', from next month. */
  months: string[];
  jobs: ProjectedPctJob[];
  /** Active Spectrum jobs no Daedalus project links to — not on the table. */
  unlinked_jobs: string[];
}

export async function getProjectedPct(token: string | null): Promise<ProjectedPct | null> {
  const wrap = await cachedCall<ToolResponse<ProjectedPct>>(`${PROJECTIONS}/projected-pct`, token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load projected %', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Set one job-month (pct_complete null clears it back to the review /
 *  screen figure). `seen_review_id` is the review the row showed (null: none)
 *  — a newer review stays waiting beside his figures until he chooses.
 *  Controller or executive. */
export async function setProjectedPctCell(
  token: string | null,
  body: { job_number: string; month: string; pct_complete: number | null; seen_review_id: number | null },
): Promise<void> {
  // Cleared whatever the outcome: a refused write (409 / 404) means the
  // table moved, and the next read must not be the cached one.
  try {
    const wrap = await call<ToolResponse<unknown>>(`${PROJECTIONS}/projected-pct/cell`, token, body);
    if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to save the projection', 500, wrap.error_code ?? undefined);
  } finally {
    invalidateCachedCalls(PROJECTIONS);
  }
}

/** The controller's answer to a newer review on a job he adjusted. */
export async function chooseProjectedPctReview(
  token: string | null,
  body: { job_number: string; review_id: number; choice: 'review' | 'mine' },
): Promise<void> {
  try {
    const wrap = await call<ToolResponse<unknown>>(`${PROJECTIONS}/projected-pct/review-choice`, token, body);
    if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to save the choice', 500, wrap.error_code ?? undefined);
  } finally {
    invalidateCachedCalls(PROJECTIONS);
  }
}

// ── Field labor (manpower) ───────────────────────────────────────────────────
export interface LaborByVendor { vendor: string | null; man_hours: number; worker_days: number; is_self_perform: boolean; }
export interface LaborByCostCode { cost_code: string | null; man_hours: number; }
export interface LaborByMonth { month: string; man_hours: number; worker_days: number; }
export interface LaborRecent {
  log_date: string | null; vendor: string | null; num_workers: number | null;
  man_hours: number; cost_code: string | null; trade: string | null; notes: string | null;
}
/** Envelope from labor_queries.get_project_labor (POST /tools/projects/labor) —
 *  the Procore daily-manpower rollup. Null when the project logs no manpower
 *  (count=0; many projects don't) — the tab renders an empty state, not an error. */
export interface LaborSummary {
  project_id: number;
  total_man_hours: number;
  total_worker_days: number;
  distinct_days_logged: number;
  first_log_date: string | null;
  last_log_date: string | null;
  self_perform_man_hours: number;
  subcontractor_man_hours: number;
  self_perform_pct: number | null;
  by_vendor: LaborByVendor[];
  by_cost_code: LaborByCostCode[];
  by_month: LaborByMonth[];
  recent: LaborRecent[];
}

export async function getProjectLabor(
  token: string | null,
  params: { project_id: number }
): Promise<LaborSummary | null> {
  const wrap = await cachedCall<ToolResponse<LaborSummary>>('/tools/projects/labor', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load labor', 500, wrap.error_code ?? undefined);
  }
  return wrap.data?.[0] ?? null;
}

/** One mentionable teammate for the Activity composer's @-picker
 *  (POST /tools/projects/activity/mentionable). A project member who has a
 *  directory @handle; users without a handle are absent (can't be mentioned). */
export interface MentionableUser {
  username: string;
  display_name: string | null;
  email: string;
  job_title: string | null;
}

export async function getMentionableUsers(
  token: string | null,
  params: { project_id: number }
): Promise<MentionableUser[]> {
  const wrap = await call<ToolResponse<MentionableUser>>(
    '/tools/projects/activity/mentionable',
    token,
    params
  );
  // A success=false envelope (access-denied / internal error) is non-fatal for
  // the composer — degrade to "no suggestions" rather than blocking posting.
  if (!wrap.success) return [];
  return wrap.data ?? [];
}

/** One platform-directory user for the email-recipient picker
 *  (POST /tools/directory/search). A name<->email map row with no scope —
 *  the picker resolves a typed name to the internal `email` before sending. */
export interface DirectoryUser {
  username: string;
  display_name: string | null;
  email: string;
}

/** Type-ahead search of the platform user directory (name / @handle / email)
 *  for the email-recipient picker. An empty query returns the first `limit`
 *  users alphabetically so the picker can show options on focus. A failure
 *  degrades to "no suggestions" — the user can still type a raw address. */
export async function searchDirectory(
  token: string | null,
  params: { query?: string; limit?: number }
): Promise<DirectoryUser[]> {
  const wrap = await call<ToolResponse<DirectoryUser>>(
    '/tools/directory/search',
    token,
    { query: params.query ?? '', limit: params.limit ?? 8 }
  );
  if (!wrap.success) return [];
  return wrap.data ?? [];
}

// ── Preconstruction management (dept-head-only bid assignment) ────────────────
//
// Backs the Preconstruction → Management tab. Every call is gated on
// is_preconstruction_department_head server-side (403 otherwise) — see
// tools_service/routers/bids.py /tools/bids/mgmt/*.

/** A precon/estimating staff member the head can assign to bids
 *  (POST /tools/bids/mgmt/staff). is_department_head flags the head himself. */
export interface PreconStaffMember {
  user_entra_id: string;
  user_email: string;
  display_name: string | null;
  role: string | null;
  is_department_head: boolean | null;
}

/** An assignable (active) Syncon bid (POST /tools/bids/mgmt/assignable-bids). */
export interface AssignableBid {
  id: number;
  name: string;
  bid_number: string | null;
  status: string | null;
  due_date: string | null;
}

/** One of a staffer's current bid_users rows (POST /tools/bids/mgmt/assignments).
 *  role 'lead_estimator' or is_department_head render as locked in the UI —
 *  the Management surface never strips those. */
export interface StaffBidAssignment {
  bid_id: number;
  role: string | null;
  is_department_head: boolean | null;
}

/** Fetch the precon staff pool. ALSO the dept-head ACCESS PROBE: an ApiError
 *  with status 403 means the caller isn't the precon head, so PreconHub keeps
 *  the Management tab hidden. Throws on any non-success. */
export async function getPreconStaff(token: string | null): Promise<PreconStaffMember[]> {
  const wrap = await cachedCall<ToolResponse<PreconStaffMember>>('/tools/bids/mgmt/staff', token, {});
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load precon staff', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

export async function getAssignableBids(token: string | null): Promise<AssignableBid[]> {
  const wrap = await call<ToolResponse<AssignableBid>>('/tools/bids/mgmt/assignable-bids', token, {});
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load bids', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

export async function getStaffAssignments(
  token: string | null,
  params: { user_entra_id: string }
): Promise<StaffBidAssignment[]> {
  const wrap = await call<ToolResponse<StaffBidAssignment>>('/tools/bids/mgmt/assignments', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load assignments', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

export async function assignBidUser(
  token: string | null,
  params: { bid_id: number; user_entra_id: string }
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/bids/mgmt/assign', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to assign', 500, wrap.error_code ?? undefined);
  }
}

export async function unassignBidUser(
  token: string | null,
  params: { bid_id: number; user_entra_id: string }
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/bids/mgmt/unassign', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to unassign', 500, wrap.error_code ?? undefined);
  }
}

/** One row from pm_queries.get_project_status_history
 *  (POST /tools/projects/status-history). Ordered changed_at DESC by the
 *  backend, so the SPA renders it newest-first as an activity timeline. */
export interface ProjectStatusHistoryEntry {
  from_status: string | null;
  to_status: string | null;
  changed_at: string | null;
  changed_by: string | null;
  notes: string | null;
}

export async function getProjectStatusHistory(
  token: string | null,
  params: { project_id: number }
): Promise<ProjectStatusHistoryEntry[]> {
  const wrap = await call<ToolResponse<ProjectStatusHistoryEntry>>(
    '/tools/projects/status-history',
    token,
    params
  );
  // Surface a success=false envelope (access-denied / internal error) as a
  // thrown error rather than an empty timeline — same rationale as
  // getProjectTeam above.
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load activity', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/* ----- Project workspace: Activity feed -------------------------------------
   The Activity tab renders the merged feed: project_activity events (manual
   posts + agent-shared RFI drafts / meeting summaries / generated docs) UNION
   the status-history log, newest-first. The backend computes can_edit /
   can_delete per row so the SPA needs no role logic of its own. id is unique
   only within a source, so rows are keyed by (is_system, id). */
export type ActivityEventType =
  | 'manual_post'
  | 'rfi_draft_shared'
  | 'meeting_summary_shared'
  | 'document_shared'
  | 'status_changed'
  | 'procore_event';

export interface ProjectActivityEntry {
  id: number;
  event_type: ActivityEventType;
  actor_name: string | null;
  summary: string;
  body: string | null;
  /** JSON string of typed refs (rfi_id/draft_id, meeting_id, doc_id/sharepoint_url). */
  payload_json: string | null;
  created_at: string | null;
  edited_at: string | null;
  /** True for status-history rows: read-only, never editable/deletable. */
  is_system: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export async function getProjectActivity(
  token: string | null,
  params: { project_id: number; limit?: number; offset?: number; include_routine?: boolean }
): Promise<ProjectActivityEntry[]> {
  const wrap = await call<ToolResponse<ProjectActivityEntry>>('/tools/projects/activity', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load activity', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

export async function postProjectActivity(
  token: string | null,
  params: { project_id: number; summary: string; body?: string | null }
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/projects/activity/post', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to post update', 500, wrap.error_code ?? undefined);
  }
}

/** Atomic post-with-attachment: single multipart request — the backend
 *  stages the file to the project's SharePoint Activity folder, then inserts
 *  the manual_post row whose payload carries the attachment refs. Same error
 *  contract as postProjectActivity (throws ApiError on a failure envelope;
 *  callMultipart already throws on non-2xx statuses like 403/413/415). */
export async function postProjectActivityWithFile(
  token: string | null,
  params: { project_id: number; summary: string; body?: string | null; file: File }
): Promise<void> {
  const form = new FormData();
  form.append('project_id', String(params.project_id));
  form.append('summary', params.summary);
  if (params.body) form.append('body', params.body);
  form.append('file', params.file);
  const wrap = await callMultipart<ToolResponse<unknown>>(
    '/tools/projects/activity/post-with-file',
    token,
    form
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to post update', 500, wrap.error_code ?? undefined);
  }
}

export async function editProjectActivity(
  token: string | null,
  params: { activity_id: number; summary: string; body?: string | null }
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/projects/activity/edit', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to edit update', 500, wrap.error_code ?? undefined);
  }
}

export async function deleteProjectActivity(
  token: string | null,
  params: { activity_id: number }
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/projects/activity/delete', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to delete update', 500, wrap.error_code ?? undefined);
  }
}

/* ----- Project workspace: Operations tab (RFIs / submittals / CEs / risk) ---
   All four read the numeric project_id and are membership-scoped in the SQL
   layer (the caller has already passed the /summary access gate). Each follows
   the house pattern: throw on a success=false envelope so a real failure
   renders an error state, not a misleading empty panel. */

/** One row from pm_queries.get_open_rfis (POST /tools/rfis/open). The SQL
 *  `rfis` table is backed by a live-Procore fallback, so this returns data
 *  even when SQL is empty for a Procore-linked project. cost_impact /
 *  schedule_impact arrive as SQL BIT (0/1) — render with a truthiness check. */
export interface RfiRow {
  number: string | number | null;
  title: string | null;
  status: string | null;
  assignee: string | null;
  days_open: number | null;
  cost_impact: number | boolean | null;
  schedule_impact: number | boolean | null;
  due_date: string | null;
  ball_in_court: string | null;
  /** The company behind `ball_in_court` — the PARTY an item sits with, as
   *  opposed to the person. Procore returns ball_in_court as an object with a
   *  nested company; the sync now keeps both (migration 154).
   *
   *  NULL on every row synced before that migration, and the SPA falls back to
   *  the person's name, so nothing regresses while the mirror catches up. */
  ball_in_court_company?: string | null;
  /** Parsed "Estimate Cost of RFI" Procore custom field (null = no value). */
  estimated_cost: number | string | null;
}

export async function getOpenRfis(
  token: string | null,
  params: { project_id: number }
): Promise<RfiRow[]> {
  // The Operations-tab reads (RFIs/submittals/risk + the four live Procore
  // panels) go through the read-through cache: the tab fires all seven in
  // parallel on EVERY mount, and tab-flapping re-ran the full backend work
  // each time against a DTU-constrained cross-region DB. 60s staleness is
  // safe here — no write on this surface needs read-back, the SQL mirrors
  // sync nightly, and the live Procore reads sit on a 10-min server-side
  // Redis cache anyway.
  const wrap = await cachedCall<ToolResponse<RfiRow>>('/tools/rfis/open', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load RFIs', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

// ── Schedule (MS Project .mpp / .xer parsed into project_schedule_tasks) ──

/** Current-schedule overview (POST /tools/schedule/get -> 0 or 1 row). */
export interface ScheduleOverview {
  id: number;
  name: string | null;
  source_file_name: string | null;
  schedule_format: string | null;
  start_date: string | null;
  finish_date: string | null;
  /** In-file schedule status date. UNRELIABLE as a freshness signal — Syncon's
   *  schedulers leave it frozen across re-issues. Prefer source_updated_at. */
  data_date: string | null;
  /** Procore-modified date of the backing .mpp/.xer — the reliable recency
   *  signal (what the resolver orders by to pick the current schedule). */
  source_updated_at: string | null;
  task_count: number | null;
  milestone_count: number | null;
  total_cost: number | null;
  baseline_total_cost: number | null;
  actual_total_cost: number | null;
  activity_count: number | null;
  /** Project-level baseline vs forecast finish — the last LEAF activity to
   *  finish in each, which is the project's own finish by definition.
   *  Null when the schedule carries no baseline (common on an early upload). */
  baseline_finish?: string | null;
  forecast_finish?: string | null;
  /** Whole days of slip, forecast − baseline. Null (not 0) when there is no
   *  baseline to compare against — 0 would read as "on time", not "unknown".
   *
   *  Deliberately NOT max(ScheduleDelay.slip_days): a 40-day slip on an
   *  activity with 60 days of float moves nothing, so the largest activity
   *  slip routinely overstates the project's. */
  slip_days?: number | null;
  /** Slip on the PREVIOUS re-issue of this schedule, so the tab can say
   *  whether the number is growing or being recovered. Migration 078 keeps
   *  every weekly upload precisely so this is answerable.
   *
   *  Null (not 0) when this is the first upload or the predecessor carried no
   *  baseline — 0 would render as "was on time" about a schedule nobody can
   *  see. */
  prior_slip_days?: number | null;
  /** 0–100 average over leaf activities (null = no data). */
  percent_complete: number | null;
  completed_activities: number | null;
  in_progress_activities: number | null;
  not_started_activities: number | null;
  delayed_activities: number | null;
  /** false when the schedule carries no cost loading (total_cost == 0). */
  is_cost_loaded: boolean | null;
}

export interface ScheduleMilestone {
  wbs: string | null;
  name: string | null;
  finish_date: string | null;
  baseline_finish: string | null;
  percent_complete: number | null;
  /** 'complete' | 'overdue' | 'upcoming' */
  status: string | null;
}

export interface ScheduleLookaheadItem {
  wbs: string | null;
  name: string | null;
  start_date: string | null;
  finish_date: string | null;
  percent_complete: number | null;
  is_critical: number | boolean | null;
}

export interface ScheduleDelay {
  wbs: string | null;
  name: string | null;
  baseline_finish: string | null;
  finish_date: string | null;
  percent_complete: number | null;
  is_critical: number | boolean | null;
  slip_days: number | null;
  /** Total slack in days (migration 155). NULL on any schedule parsed before
   *  that migration — slack is computed by the scheduling engine from the
   *  activity network, and we store tasks rather than the network, so it
   *  cannot be recomputed locally. Rows fill in as projects post their next
   *  re-issue.
   *
   *  NEGATIVE is meaningful: the activity is behind what the critical path
   *  requires, i.e. the sequence is already infeasible. Never clamp it. */
  total_float_days?: number | null;
}

/** null when the project has no ingested schedule yet. */
export async function getProjectScheduleOverview(
  token: string | null,
  params: { project_id: number }
): Promise<ScheduleOverview | null> {
  const wrap = await call<ToolResponse<ScheduleOverview>>('/tools/schedule/get', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load schedule', 500, wrap.error_code ?? undefined);
  }
  return wrap.data?.[0] ?? null;
}

export async function getScheduleMilestones(
  token: string | null,
  params: { project_id: number }
): Promise<ScheduleMilestone[]> {
  const wrap = await call<ToolResponse<ScheduleMilestone>>('/tools/schedule/milestones', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load milestones', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

export async function getScheduleLookahead(
  token: string | null,
  params: { project_id: number; days?: number }
): Promise<ScheduleLookaheadItem[]> {
  const wrap = await call<ToolResponse<ScheduleLookaheadItem>>('/tools/schedule/lookahead', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load look-ahead', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

export async function getScheduleDelays(
  token: string | null,
  params: { project_id: number; limit?: number; min_slip_days?: number }
): Promise<ScheduleDelay[]> {
  const wrap = await call<ToolResponse<ScheduleDelay>>('/tools/schedule/delays', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load delays', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** One schedule re-issue where the projected finish moved (Change Log source).
 *  `delta_days` is signed: + slipped later, − recovered. */
export interface ScheduleReissue {
  schedule_id: number;
  reissued_at: string;
  forecast_finish: string | null;
  prior_forecast_finish: string | null;
  delta_days: number;
}

export async function getScheduleReissues(
  token: string | null,
  params: { project_id: number }
): Promise<ScheduleReissue[]> {
  const wrap = await call<ToolResponse<ScheduleReissue>>('/tools/schedule/reissues', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load schedule re-issues', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** One dated revised-contract step for a job (Change Log source). `delta` is the
 *  signed dollar change. `linked:false` (no rows) when the project has no
 *  Spectrum job mapping. Financials-gated server-side — a non-privileged caller
 *  gets a 403 (surfaced here as an ApiError the feed swallows), so these dollars
 *  never reach an unauthorized SPA. */
export interface ContractRevision {
  month: string;
  revised_at: string | null;
  revised_contract: number;
  prior_revised_contract: number;
  delta: number;
}

export async function getContractRevisions(
  token: string | null,
  params: { project_id: number }
): Promise<ContractRevision[]> {
  const wrap = await call<ToolResponse<ContractRevision | { linked: false }>>(
    '/tools/executive/financials/contract-revisions',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load contract revisions', 500, wrap.error_code ?? undefined);
  }
  // Drop the `linked:false` sentinel row — a real revision always carries a delta.
  return (wrap.data ?? []).filter((r): r is ContractRevision => 'delta' in r);
}

/** P4 Drift 2 — POST /tools/drift/baseline-exposure. The tool returns a
 *  single-row data[]; unwrap to the one DriftBaselineExposure (falling back to
 *  'not_computed' if the sweep hasn't produced a row yet). The server already
 *  tier-trims (serves the general-tier rollup to non-leadership), so the SPA
 *  renders whatever it gets verbatim. */
export async function getBaselineExposure(
  token: string | null,
  params: { project_id: number }
): Promise<DriftBaselineExposure> {
  const wrap = await call<ToolResponse<DriftBaselineExposure>>(
    '/tools/drift/baseline-exposure',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load drift', 500, wrap.error_code ?? undefined);
  }
  return (wrap.data ?? [])[0] ?? { capability_state: 'not_computed', drift2_available: false };
}

/** P5 Drift-1 — POST /tools/drift/bid-to-contract (enqueue). Sibling of
 *  getBaselineExposure; this is the bid-package → executed-contract drift
 *  surface, NOT the working → impact one.
 *
 *  The tool returns a single-row data[]. Two shapes ride under data[0]:
 *    - A graceful BidToContractDrift carrying a non-ok capability_state
 *      (estimation_baseline_absent / bid_unlinked / estimation_unstructured /
 *      no_working_baseline / disabled / not_computed / unavailable). No job
 *      was enqueued; the SPA renders an info card, never an error toast.
 *    - An ArtifactRef-shaped pending stub carrying a `pending_job`
 *      (ArtifactJobStatus, invariant #12) when a compute job was enqueued.
 *
 *  We normalize both into a BidToContractDrift so the SPA state machine has
 *  one shape to branch on: when the stub carries a `pending_job` we surface
 *  it under `capability_state:'not_computed'` + `pending_job`, mirroring
 *  OverviewTab's enqueue→poll handoff. The server already tier-trims the
 *  terminal report (general vs full); the SPA renders verbatim. */
export async function getBidToContractDrift(
  token: string | null,
  params: { project_id: number }
): Promise<BidToContractDrift> {
  const wrap = await call<ToolResponse<BidToContractDrift>>(
    '/tools/drift/bid-to-contract',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load bid-to-contract drift', 500, wrap.error_code ?? undefined);
  }
  const row = (wrap.data ?? [])[0];
  if (!row) {
    return { capability_state: 'not_computed', drift1_available: false };
  }
  // The enqueue pending stub is an ArtifactRef whose top-level shape lacks a
  // capability_state but carries a `pending_job`. Coerce it into the
  // BidToContractDrift shape the DriftTab state machine polls against.
  if (row.pending_job && !row.capability_state) {
    return {
      capability_state: 'not_computed',
      drift1_available: false,
      pending_job: row.pending_job,
    };
  }
  return row;
}

/** P5 Drift-1 — POST /tools/drift/bid-to-contract-status (poll). Mirrors
 *  getBaselineExposure's single-row unwrap. The status endpoint folds the
 *  report into the poll response via `terminal_artifact`:
 *    - non-terminal → data[0] carries a `pending_job` (queued / processing).
 *    - ready        → `pending_job.terminal_artifact` carries the
 *      tier-selected BidToContractDrift (result_json.general or .full,
 *      chosen server-side by can_see_restricted; the SPA never redacts).
 *    - graceful     → terminal_artifact carries a graceful capability_state
 *      + message.
 *
 *  We normalize to a single BidToContractDrift: a ready/graceful
 *  terminal_artifact is returned as-is (it already conforms); a still-pending
 *  poll surfaces `capability_state:'not_computed'` + `pending_job`. The
 *  server applies mirror-on-read TTL coercion (a non-terminal job whose
 *  expires_at is past reports 'expired'); the SPA also coerces defensively. */
export async function getBidToContractDriftStatus(
  token: string | null,
  params: { project_id: number; job_id: string }
): Promise<BidToContractDrift> {
  const wrap = await call<ToolResponse<BidToContractDrift>>(
    '/tools/drift/bid-to-contract-status',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to poll bid-to-contract drift', 500, wrap.error_code ?? undefined);
  }
  const row = (wrap.data ?? [])[0];
  if (!row) {
    return { capability_state: 'not_computed', drift1_available: false };
  }
  // On a ready/graceful poll the report rides inside pending_job.terminal_artifact.
  // The worker pre-shapes that artifact as a BidToContractDrift, so return it
  // verbatim. While still pending, surface the pending_job for the poll loop.
  const terminal = row.pending_job?.terminal_artifact as unknown as
    | BidToContractDrift
    | null
    | undefined;
  if (terminal && terminal.capability_state) {
    return terminal;
  }
  if (row.pending_job && !row.capability_state) {
    return {
      capability_state: 'not_computed',
      drift1_available: false,
      pending_job: row.pending_job,
    };
  }
  return row;
}

/** One row from pm_queries.get_overdue_submittals (POST /tools/submittals/overdue).
 *  SQL-only (no live fallback) — empty until Procore submittals are synced. */
export interface SubmittalRow {
  number: string | number | null;
  title: string | null;
  status: string | null;
  spec_section: string | null;
  required_on_site_date: string | null;
  days_overdue: number | null;
  responsible_contractor: string | null;
  ball_in_court: string | null;
  /** The company behind `ball_in_court` — the PARTY an item sits with, as
   *  opposed to the person. Procore returns ball_in_court as an object with a
   *  nested company; the sync now keeps both (migration 154).
   *
   *  NULL on every row synced before that migration, and the SPA falls back to
   *  the person's name, so nothing regresses while the mirror catches up. */
  ball_in_court_company?: string | null;
}

export async function getOverdueSubmittals(
  token: string | null,
  params: { project_id: number }
): Promise<SubmittalRow[]> {
  const wrap = await call<ToolResponse<SubmittalRow>>('/tools/submittals/overdue', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load submittals', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** One row from pm_queries.get_change_events (POST /tools/change-events).
 *  SQL-only. estimated_cost_impact is summed client-side for the exposure KPI. */
export interface ChangeEventRow {
  number: string | number | null;
  title: string | null;
  status: string | null;
  estimated_cost_impact: number | null;
  estimated_schedule_impact: number | null;
  origin: string | null;
  description: string | null;
}

export async function getChangeEvents(
  token: string | null,
  params: { project_id: number }
): Promise<ChangeEventRow[]> {
  const wrap = await call<ToolResponse<ChangeEventRow>>('/tools/change-events', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load change events', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** Live risk-model output from POST /tools/risks/score — one object, not a
 *  list. Shape mirrors scoring/risk_model.score_project_risks. */
export interface RiskScore {
  project_id: number;
  model_version: string;
  overall_risk_level: 'Low' | 'Medium' | 'High' | string;
  total_score: number;
  component_scores: { rfis: number; submittals: number; change_events: number };
  scored_items: Array<{
    category: string;
    identifier: string;
    title: string;
    score: number;
    risk_level: string;
    factors: Record<string, unknown>;
  }>;
}

export async function getProjectRiskScore(
  token: string | null,
  params: { project_id: number }
): Promise<RiskScore> {
  const wrap = await cachedCall<ToolResponse<RiskScore>>('/tools/risks/score', token, params);
  if (!wrap.success || !wrap.data?.length) {
    throw new ApiError(
      wrap.error ?? 'failed to score risk',
      500,
      wrap.error_code ?? undefined
    );
  }
  return wrap.data[0];
}

/* ----- Operations tab: open submittals + live Procore connector reads ------

   The Operations tab is hybrid (see OperationsTab.tsx): RFIs and submittals
   come from the SQL mirror (fast, synced nightly, richer derived fields);
   change orders / observations / inspections / punch come LIVE from the
   Procore connector because they aren't mirrored. All connector endpoints are
   project-access-gated in tools_service/routers/procore.py.

   Note: Procore's list endpoints don't reliably honor a bare `status` query
   param, so these fetch broadly and the component filters open/overdue
   client-side (same lesson as procore_get_rfis). */

/** Open submittals from the SQL mirror (POST /tools/submittals/open). Same row
 *  shape as getOverdueSubmittals; days_overdue > 0 flags the overdue ones. */
export async function getOpenSubmittals(
  token: string | null,
  params: { project_id: number }
): Promise<SubmittalRow[]> {
  const wrap = await cachedCall<ToolResponse<SubmittalRow>>('/tools/submittals/open', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load submittals', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** Potential Change Order — live, from connectors/procore/tools/change_orders.get_potential_change_orders
 *  (POST /tools/procore/change-orders/potential). The trackable change-order
 *  surface for this DMSA (change events / CO requests / packages all 403). */
export interface ChangeOrderRow {
  id: number | null;
  number: string | number | null;
  title: string | null;
  status: string | null;
  estimated_cost: number | string | null;
  estimated_schedule_impact: number | null;
  origin: string | null;
  created_at: string | null;
}

export async function getPotentialChangeOrders(
  token: string | null,
  params: { project_id: number; status?: string }
): Promise<ChangeOrderRow[]> {
  const wrap = await cachedCall<ToolResponse<ChangeOrderRow>>('/tools/procore/change-orders/potential', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load change orders', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** Observation — live (POST /tools/procore/observations). days_overdue is
 *  computed server-side from due_date vs today. */
export interface ObservationRow {
  id: number | null;
  number: string | number | null;
  title: string | null;
  status: string | null;
  observation_type: string | null;
  assignee: string | null;
  /** The assignee's COMPANY — the party an item sits with, as opposed to the
   *  person. Mirrored since migration 115 and returned by both the mirror
   *  (`procore_ops_mirror_queries.py`) and the live connector
   *  (`connectors/procore/tools/observations.py`), it was simply never
   *  declared here, so the SPA has been discarding it at the type boundary.
   *
   *  RFIs and submittals have no equivalent yet — their sync keeps only the
   *  person's name off the same Procore object — so an item's party is known
   *  for some types and not others. */
  assignee_company: string | null;
  due_date: string | null;
  days_overdue: number | null;
  location: string | null;
  trade: string | null;
}

export async function getObservations(
  token: string | null,
  params: { project_id: number; status?: string }
): Promise<ObservationRow[]> {
  const wrap = await cachedCall<ToolResponse<ObservationRow>>('/tools/procore/observations', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load observations', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** Inspection (checklist list) — live (POST /tools/procore/inspections).
 *  failed_item_count surfaces QA/QC problems. */
export interface InspectionRow {
  id: number | null;
  name: string | null;
  status: string | null;
  inspection_type: string | null;
  inspector: string | null;
  due_at: string | null;
  completed_at: string | null;
  location: string | null;
  item_count: number | null;
  failed_item_count: number | null;
}

export async function getInspections(
  token: string | null,
  params: { project_id: number; status?: string }
): Promise<InspectionRow[]> {
  const wrap = await cachedCall<ToolResponse<InspectionRow>>('/tools/procore/inspections', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load inspections', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** Punch item — live (POST /tools/procore/punch-items). days_overdue computed
 *  server-side; overdue_only filters server-side when set. */
export interface PunchItemRow {
  id: number | null;
  number: string | number | null;
  title: string | null;
  status: string | null;
  priority: string | null;
  due_date: string | null;
  days_overdue: number | null;
  location: string | null;
  assignee: string | null;
}

export async function getPunchItems(
  token: string | null,
  params: { project_id: number; status?: string; overdue_only?: boolean }
): Promise<PunchItemRow[]> {
  const wrap = await cachedCall<ToolResponse<PunchItemRow>>('/tools/procore/punch-items', token, params);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load punch items', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/* ----- Project workspace: Meetings tab (Teams channel transcripts) --------
   Channel binding is a SPA-only Tools Service surface (never an agent tool) —
   see tools_service/routers/agents_meeting_recaps.py. View of recap metadata is
   gated server-side on actual Teams channel membership. */

export interface MeetingRecap {
  recap_file_id: string;
  name: string;
  created_at: string | null;
  web_url: string | null;
  size_bytes: number | null;
}

/** One-call state for the Meetings tab. `state` drives the rendered UI:
 *  unconfigured | unreachable | not_member | no_recordings | ready. */
export interface MeetingChannelStatus {
  project_id: number;
  state: 'unconfigured' | 'unreachable' | 'not_member' | 'no_recordings' | 'ready' | string;
  can_set?: boolean;
  channel_name?: string;
  channel_id?: string;
  recordings_count?: number;
  recaps?: MeetingRecap[];
}

export async function getMeetingChannelStatus(
  token: string | null,
  params: { project_id: number }
): Promise<MeetingChannelStatus> {
  const wrap = await call<ToolResponse<MeetingChannelStatus>>(
    '/tools/agents/recaps/channel/status',
    token,
    params
  );
  if (!wrap.success || !wrap.data?.length) {
    throw new ApiError(wrap.error ?? 'failed to load meeting channel status', 500, wrap.error_code ?? undefined);
  }
  return wrap.data[0];
}

export interface SetMeetingChannelResult {
  project_id: number;
  channel_name: string;
  membership_type: string;
  recordings_found: number | null;
  rebind: boolean;
}

/** Connect a project's Teams meeting channel from a pasted channel link.
 *  The backend gates this on role + the setter's own channel membership and
 *  validates the channel before saving; failures surface as ApiError (400 bad
 *  link, 403 permission / not-a-member, 422 unreachable) with a message. */
export async function setMeetingChannel(
  token: string | null,
  params: { project_id: number; channel_link: string }
): Promise<SetMeetingChannelResult> {
  const wrap = await call<ToolResponse<SetMeetingChannelResult>>(
    '/tools/agents/recaps/channel/set',
    token,
    params
  );
  if (!wrap.success || !wrap.data?.length) {
    throw new ApiError(wrap.error ?? 'failed to connect channel', 500, wrap.error_code ?? undefined);
  }
  return wrap.data[0];
}

export async function disconnectMeetingChannel(
  token: string | null,
  params: { project_id: number }
): Promise<void> {
  const wrap = await call<ToolResponse<{ disconnected: boolean }>>(
    '/tools/agents/recaps/channel/disconnect',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to disconnect channel', 500, wrap.error_code ?? undefined);
  }
}

/** Structured meeting-summary payload (the `summary_json` string parsed).
 *  Bullet fields are newline-joined "• …" strings; MeetingTitle / Summary
 *  are prose. Mirrors _MEETING_SUMMARY_FIELDS in
 *  previz_service/meeting_summary_worker.py — keep in lockstep. */
export interface MeetingSummaryJson {
  MeetingTitle: string;
  Attendees: string;
  KeyDiscussion: string;
  Decisions: string;
  ActionItems: string;
  OpenIssues: string;
  Summary: string;
}

/** One precomputed meeting summary row from get_meeting_summaries
 *  (tools_service/db/queries/meeting_summary_read_queries.py). */
export interface MeetingSummary {
  summary_id: number;
  project_id: number;
  recording_item_id: string;
  meeting_title: string | null;
  meeting_start: string | null;
  meeting_end: string | null;
  recording_web_url: string | null;
  content: string;
  summary_json: string | null;
  model: string | null;
  created_at: string | null;
}

/** Most-recent precomputed meeting summaries for a project, newest first.
 *  An empty array is legitimate (no meetings summarized yet). Throws
 *  ApiError on a backend failure — including 403 when the caller isn't a
 *  member of the project's Teams meeting channel. */
export async function getMeetingSummaries(
  token: string | null,
  params: { project_id: number; limit?: number }
): Promise<MeetingSummary[]> {
  const wrap = await call<ToolResponse<MeetingSummary>>(
    '/tools/agents/recaps/summaries',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load meeting summaries', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** One transcript-passage hit from search_meeting_transcripts, carrying a
 *  meeting citation so the UI can link back to the source recording. */
export interface MeetingTranscriptHit {
  recording_item_id: string;
  chunk_idx: number;
  content: string;
  start_ms: number | null;
  end_ms: number | null;
  meeting_title: string | null;
  meeting_start: string | null;
  recording_web_url: string | null;
  score: number;
}

/** Cosine search across a project's meeting transcripts. Returns the
 *  top-k passages with citations; an empty array means the project has no
 *  embedded transcripts yet. Throws ApiError on a query-rejected /
 *  provider error (error_code: meeting_search_query_rejected /
 *  meeting_search_embedding_provider_error) or a 403 access failure. */
export async function searchMeetingTranscripts(
  token: string | null,
  params: { project_id: number; query: string; top_k?: number }
): Promise<MeetingTranscriptHit[]> {
  const wrap = await call<ToolResponse<MeetingTranscriptHit>>(
    '/tools/agents/recaps/search',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'meeting search failed', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** One meeting in the project's Meetings tab — every recording the worker
 *  has processed or queued, with its derived status. DB-driven (no Graph),
 *  so it shows pending/failed states and honors the 14-day window.
 *  `status` is the single chip the UI renders: 'ready' when a live summary
 *  exists, else the latest job status ('processing'|'queued'|'failed'|...). */
export interface ProjectMeeting {
  recording_item_id: string;
  meeting_title: string | null;
  meeting_start: string | null;
  meeting_end: string | null;
  recording_web_url: string | null;
  status: string;
  job_status: string | null;
  error_message: string | null;
  summary_id: number | null;
  summary_json: string | null;
  summary_content: string | null;
  has_transcript: boolean;
  duration_ms: number | null;
}

/** Every meeting the worker has processed/queued for a project, newest
 *  first. Throws ApiError on 403 (not a channel member). */
export async function getProjectMeetings(
  token: string | null,
  params: { project_id: number }
): Promise<ProjectMeeting[]> {
  const wrap = await call<ToolResponse<ProjectMeeting>>(
    '/tools/agents/recaps/meetings',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load meetings', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** One marked meeting action item (migrations 156 + 225). Keyed on the
 *  recording plus a hash of the normalized line — the items themselves live
 *  inside a summary_json string and have no id of their own. */
export interface MeetingActionItemState {
  recording_item_id: string;
  item_hash: string;
  item_text: string;
  marked_by: string;
  marked_at: string | null;
  /** 'done' — the commitment happened; 'dismissed' — not a real action item.
   *  Optional so a backend predating migration 225 still parses; readers
   *  default a missing value to 'done'. */
  state?: 'done' | 'dismissed';
}

export async function getMeetingActionItemState(
  token: string | null,
  params: { project_id: number }
): Promise<MeetingActionItemState[]> {
  const wrap = await call<ToolResponse<MeetingActionItemState>>(
    '/tools/agents/recaps/action-items',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to load action-item state', 500, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** Set or clear one action item's mark. Send the RAW rendered line — the
 *  server normalizes and hashes it, so the key can never drift between the
 *  two. `done: true` writes the mark named by `state` (default 'done');
 *  `done: false` clears whatever mark exists. */
export async function setMeetingActionItemDone(
  token: string | null,
  params: {
    project_id: number;
    recording_item_id: string;
    item_text: string;
    done: boolean;
    state?: 'done' | 'dismissed';
  }
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>(
    '/tools/agents/recaps/action-items/set',
    token,
    params
  );
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'failed to update the action item', 500, wrap.error_code ?? undefined);
  }
}

/** The full diarized transcript of one meeting recording (for the viewer +
 *  download). */
export interface MeetingTranscript {
  recording_item_id: string;
  transcript: string;
  duration_ms: number | null;
  engine: string | null;
  created_at: string | null;
  meeting_title: string | null;
  meeting_start: string | null;
  meeting_end: string | null;
  recording_web_url: string | null;
}

/** Fetch one meeting's full transcript. Throws ApiError with
 *  error_code='meeting_transcript_not_found' when the recording has no
 *  cached transcript (not transcribed yet, oversized, or wrong project). */
export async function getMeetingTranscript(
  token: string | null,
  params: { project_id: number; recording_item_id: string }
): Promise<MeetingTranscript> {
  const wrap = await call<ToolResponse<MeetingTranscript>>(
    '/tools/agents/recaps/transcript',
    token,
    params
  );
  if (!wrap.success || !wrap.data?.length) {
    throw new ApiError(wrap.error ?? 'transcript not found', 404, wrap.error_code ?? undefined);
  }
  return wrap.data[0];
}

/* ---------------- Executive portfolio ---------------- */

export interface PortfolioRow {
  id?: number;
  name?: string;
  project_number?: string;
  status?: string;
  start_date?: string | null;
  completion_date?: string | null;
  city?: string | null;
  state?: string | null;
  [key: string]: unknown;
}

export interface BehindScheduleRow {
  id?: number;
  name?: string;
  project_number?: string | null;
  status?: string;
  /** Where the behind-schedule signal came from. */
  source?: 'schedule' | 'completion_date';
  has_schedule?: boolean;
  /** Which signal drives the collapsed-row chip. */
  headline_kind?: 'slip' | 'past_finish' | 'late_tasks' | 'completion_date';
  headline_days?: number | null;
  headline_count?: number | null;
  // schedule-sourced detail (present when source === 'schedule')
  schedule_finish?: string | null;
  baseline_finish?: string | null;
  slip_days?: number | null;
  days_past_planned_finish?: number | null;
  late_activity_count?: number | null;
  percent_complete?: number | null;
  source_file_name?: string | null;
  source_updated_at?: string | null;
  // completion_date fallback detail (present when source === 'completion_date')
  completion_date?: string | null;
  days_past_completion?: number | null;
  [key: string]: unknown;
}

export async function getPortfolioSummary(token: string | null): Promise<PortfolioRow[]> {
  const wrap = await cachedCall<ToolResponse<PortfolioRow>>(
    '/tools/executive/portfolio/summary',
    token,
    {}
  );
  return wrap.data ?? [];
}

export async function getPortfolioBehindSchedule(
  token: string | null
): Promise<BehindScheduleRow[]> {
  const wrap = await cachedCall<ToolResponse<BehindScheduleRow>>(
    '/tools/executive/portfolio/behind-schedule',
    token,
    {}
  );
  return wrap.data ?? [];
}

/* ----- Portfolio Operations dashboard (exec-gated) ----- */

/** Per-PM rollup — get_pm_performance (POST /tools/executive/pm-performance).
 *  PM = project_users role 'Project Manager - Internal'. */
export interface PmPerformanceRow {
  pm_entra_id: string | null;
  pm_email: string | null;
  active_projects: number;
  behind_schedule: number;
  open_rfis: number;
  overdue_submittals: number;
}
// ── PM Ops dashboard (Portfolio Operations › Project Management redesign) ────
// Types + helpers for the /tools/executive/pmops/* endpoints (Tier-1 KPIs +
// attention + risk board, and the lens feeds). Shapes mirror the query layer in
// tools_service/db/queries/{portfolio_risk_queries,executive_kpi,pmops_queries}.py
// and executive_financials.get_change_order_aging.

export interface PmOpsKpiTile {
  key: string;
  label: string;
  value: number | null;
  unit: 'count' | 'usd' | 'days';
  delta: number | null;
  delta_pct: number | null;
  delta_basis: 'live_timestamps' | 'snapshot';
  improve_direction: 'up' | 'down' | 'neutral';
  sparkline: number[];
  sparkline_partial: boolean;
  /** A caveat the tile shows under its value (e.g. projects scored without
   *  their forecast margin today: the at-risk count may be understated). */
  hint?: string | null;
}
export interface PmOpsKpiHeadline {
  tiles: PmOpsKpiTile[];
  history_weeks_available: number;
  history_note: string | null;
}

export interface RiskDriver {
  component: string;
  subscore: number;
  weight: number;
  contribution: number;
  detail: string;
  /** This project's own arithmetic for the sub-score (model v2+). */
  formula?: string;
}
export interface RiskBoardRow {
  project_id: number;
  name: string | null;
  project_number: string | null;
  /** PM attribution. ``pm_name`` is Spectrum's, resolved from the job
   *  master's Project_Manager code — NOT the Procore project roster, which
   *  named a different human than Spectrum on 4 of 26 scored jobs before
   *  2026-09-15. ``pm_key`` is the stable identity to filter and group on
   *  (`code:<n>`, or `roster:<oid>` for a project the ERP has no job for);
   *  never match on the display name. ``pm_source`` says which spoke. */
  pm_name: string | null;
  pm_key: string | null;
  pm_code: string | null;
  pm_source: 'spectrum' | 'roster' | null;
  composite_score: number;
  risk_band: string;
  schedule_score: number;
  commercial_score: number;
  rfi_score: number;
  submittal_score: number;
  drivers: RiskDriver[];
  has_schedule: boolean;
  spectrum_mapped: boolean;
  contract_value: number | null;
  slip_days: number | null;
  percent_complete: number | null;
  open_rfi_count: number;
  overdue_submittal_count: number;
  pending_cr_amount: number | null;
  rfi_turnaround_days: number | null;
  fade_p50: number | null;
  fade_band: string | null;
  /** Model v2+ margin inputs, all fractions, positive fade = margin lost vs
   *  bid. ``board_*`` are the Contract Margin board's figures; ``realized_*``
   *  the margin earned to date (used once ``billed_pct`` reaches 98%);
   *  ``eac_margin_fade`` the EAC model's central forecast. */
  board_original_margin?: number | null;
  board_projected_margin?: number | null;
  board_margin_fade?: number | null;
  billed_pct?: number | null;
  realized_margin?: number | null;
  realized_margin_fade?: number | null;
  eac_margin_fade?: number | null;
  /** The board's projection is MISSING (the job's forecast raised) — not "no
   *  budget"; the commercial driver says so. */
  board_margin_unavailable?: boolean;
  /** The card's "confirm buyout" / "finish passed" flags behind the board
   *  projection (the commercial driver's detail names them). */
  board_forecast_flags?: ProjectionFlag[];
  /** Another linked project whose schedule and review the board's forecast
   *  of this job reads (two projects on one job). */
  board_projection_other_project?: number | null;
  /** The commercial score is missing its board margin input today. */
  commercial_margin_unavailable?: boolean;
  composite_formula?: string;
  model_version?: string;
  schedule_finish: string | null;
  baseline_finish: string | null;
  rank: number;
}
export interface RiskBoard {
  projects: RiskBoardRow[];
  at_risk_count: number;
  total_active: number;
  /** Active Spectrum jobs with no active Procore project: the board cannot
   *  rank them (schedule, RFIs and submittals come from Procore). Not
   *  rendered yet; the agent reads it. */
  not_on_board?: {
    note: string;
    jobs: {
      job_number: string;
      description: string | null;
      customer_name: string | null;
      contract_value: number;
      billed_pct: number | null;
      pending_cr_amount: number;
      eac_margin_fade: number | null;
      reason: string;
    }[];
  };
}

export interface AttentionItem {
  rank: number;
  project_id: number;
  name: string | null;
  project_number: string | null;
  pm_name: string | null;
  pm_key: string | null;
  pm_code: string | null;
  pm_source: 'spectrum' | 'roster' | null;
  composite_score: number;
  risk_band: string;
  /** Scored without its forecast margin today (that job's forecast could not
   *  be computed): the commercial part lacks an input. */
  commercial_margin_unavailable?: boolean;
  no_schedule: boolean;
  reason: string;
  drivers: RiskDriver[];
  /** All KPI metrics per item so the SPA re-ranks client-side on a tile click. */
  metrics: Record<string, number>;
  details: { label: string; value: unknown }[];
}
export interface AttentionEnvelope {
  items: AttentionItem[];
  rank_by: string;
  total_active: number;
}

export interface AgingBucket {
  label: string;
  count: number;
}
export interface AgingBuckets {
  buckets: AgingBucket[];
  total: number;
}
export interface RfiTurnaround {
  avg_days: number | null;
  median_days: number | null;
  p90_days: number | null;
  closed_count: number;
  weekly: { week: string; avg_days: number; closed: number }[];
}
export interface PmEnrichedRow {
  /** Bucket identity: the Spectrum PM code (`code:<n>`), or `roster:<oid>` for
   *  a project the ERP has no job for. Group and filter on this, never on the
   *  name — bucketing on the roster OID and naming off the first project in
   *  the bucket is what put "Chase Fulghum" beside sean.foreman@ on the live
   *  People lens. */
  pm_key: string;
  pm_code: string | null;
  pm_source: 'spectrum' | 'roster' | null;
  /** Bridged from the Spectrum name for review dwell only; null when no
   *  directory entry matches or more than one does. A null costs the row its
   *  dwell figure and nothing else. */
  pm_entra_id: string | null;
  pm_email: string | null;
  pm_name: string | null;
  active_projects: number;
  at_risk: number;
  composite_risk_avg: number;
  composite_risk_max: number;
  open_rfis: number;
  overdue_submittals: number;
  contract_value_managed: number | null;
  fade_weighted_p50: number | null;
  dwell: Record<string, unknown> | null;
}
export interface RiskMover {
  project_id: number;
  name: string | null;
  from: number;
  to: number;
  delta: number;
  why: string | null;
  series: number[];
}
export interface RiskMovers {
  deteriorating: RiskMover[];
  improving: RiskMover[];
  weeks_available: number;
  note: string | null;
  /** Projects scored without their forecast margin on a night this week —
   *  not comparable, so out of the movers (the note names them). */
  excluded_margin_unavailable?: { project_id: number; name: string | null; snapshot_date: string | null }[];
}
export interface CloseoutStats {
  median_days_to_bill: number | null;
  avg_days_to_bill: number | null;
  completed_unbilled: number;
  held_in_closeout: number;
  note: string | null;
}
export interface CoAgingRow {
  job_number: string;
  project_name: string | null;
  submitted_amount: number;
  origination_date: string | null;
  age_days: number | null;
}
export interface CoAging {
  buckets: { label: string; count: number; amount: number }[];
  oldest: CoAgingRow[];
  total_count: number;
  total_amount: number;
  amount_past_60: number;
  as_of: string;
  note: string;
}

export async function getPmOpsKpis(token: string | null): Promise<PmOpsKpiHeadline | null> {
  const wrap = await cachedCall<ToolResponse<PmOpsKpiHeadline>>('/tools/executive/pmops/kpis', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load KPI headline', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPmOpsRiskBoard(token: string | null): Promise<RiskBoard | null> {
  const wrap = await cachedCall<ToolResponse<RiskBoard>>('/tools/executive/pmops/risk-board', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load risk board', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPmOpsAttention(
  token: string | null, opts: { rankBy?: string; limit?: number } = {},
): Promise<AttentionEnvelope | null> {
  const body: Record<string, unknown> = {};
  if (opts.rankBy) body.rank_by = opts.rankBy;
  if (opts.limit) body.limit = opts.limit;
  const wrap = await cachedCall<ToolResponse<AttentionEnvelope>>('/tools/executive/pmops/attention', token, body);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load attention list', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPmOpsRfiAging(token: string | null): Promise<AgingBuckets | null> {
  const wrap = await cachedCall<ToolResponse<AgingBuckets>>('/tools/executive/pmops/rfi-aging', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load RFI aging', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPmOpsSubmittalAging(token: string | null): Promise<AgingBuckets | null> {
  const wrap = await cachedCall<ToolResponse<AgingBuckets>>('/tools/executive/pmops/submittal-aging', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load submittal aging', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPmOpsRfiTurnaround(token: string | null): Promise<RfiTurnaround | null> {
  const wrap = await cachedCall<ToolResponse<RfiTurnaround>>('/tools/executive/pmops/rfi-turnaround', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load RFI turnaround', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPmOpsPmPerformance(token: string | null): Promise<PmEnrichedRow[]> {
  const wrap = await cachedCall<ToolResponse<PmEnrichedRow>>('/tools/executive/pmops/pm-performance', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load PM performance', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}
/** PM billing curves (POST /tools/executive/pmops/pm-curves, scoring/pm_curves.py).
 *  Fractions throughout (0.05 = 5 margin points). `cp` is the share of the
 *  contract billed (0.1 … 1.0). */
export interface PmCurvePoint {
  cp: number;
  month: string;
  margin_to_date: number | null;
  /** Margin to date minus the award margin (+ = ahead of award); at 1.0 on a
   *  closed job, exactly minus its EAC fade. */
  vs_award: number | null;
  /** Share billed minus share of cost spent; positive = billing ahead of the
   *  work. Null at 1.0 on a closed job (1 - 1 by construction). */
  billing_position: number | null;
  /** The PM of record at the end of `month`. */
  pm_code: string | null;
  /** True when that is inferred: before the PM record began, every step is
   *  credited to the PM the record started with (on a closed job, the PM at
   *  close); also for a row from a mass-change night not yet reviewed. */
  inferred: boolean;
}
export interface PmCurveJob {
  job_number: string;
  description: string | null;
  customer: string | null;
  status: 'closed' | 'active';
  contract: number | null;
  award_margin: number | null;
  margin_to_date: number | null;
  /** Margin to date minus award now (+ = ahead); null below 10% billed. */
  vs_award_now: number | null;
  progress: number | null;
  /** Closed jobs' cost share is against final cost; active jobs' against the
   *  current estimate (null without one). */
  cost_basis: 'final' | 'estimate' | null;
  first_month: string;
  last_month: string;
  /** Closed: the PM at close. Active: the PM of record now. */
  pm_code: string | null;
  pm_inferred: boolean;
  points: PmCurvePoint[];
}
export interface PmCurveQuartiles { p25: number; p50: number; p75: number }
export interface PmCurveBand {
  cp: number;
  n_vs_award: number;
  n_billing_position: number;
  vs_award: PmCurveQuartiles | null;
  billing_position: PmCurveQuartiles | null;
}
export interface PmCurveEac { forecast_margin: number; forecast_vs_award: number; risk_band: string | null }
export interface PmCurvePm {
  pm_code: string;
  pm_name: string | null;
  closed_jobs: number;
  active_jobs: { job_number: string; progress: number | null; eac: PmCurveEac | null }[];
  finish_vs_award: { n: number; median: number | null };
  /** Medians are null below five jobs at that checkpoint, per measure. */
  checkpoints: {
    cp: number; n_vs_award: number; n_billing_position: number;
    median_vs_award: number | null; median_billing_position: number | null;
  }[];
  customers: { name: string; jobs: number; share: number }[];
  first_year: string | null;
  last_year: string | null;
  inferred_share: number | null;
}
/** `share` is eta squared; `chance_share` what a random grouping of the same
 *  sizes accounts for on average (the share means something only above it). */
export interface PmCurveExplained { share: number; chance_share: number; p: number; groups: number; jobs: number }
export interface PmCurves {
  as_of: string | null;
  checkpoints: number[];
  band: PmCurveBand[];
  jobs: PmCurveJob[];
  pms: PmCurvePm[];
  spread_explained: {
    measure: string;
    min_group_jobs: number;
    by_pm: PmCurveExplained | null;
    by_customer: PmCurveExplained | null;
  };
  excluded: Record<string, number>;
  history_rows: number;
  /** When the PM record began; null before its first night. */
  record_started: string | null;
}
export async function getPmOpsPmCurves(token: string | null): Promise<PmCurves | null> {
  const wrap = await cachedCall<ToolResponse<PmCurves>>('/tools/executive/pmops/pm-curves', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load PM billing curves', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPmOpsRiskMovers(token: string | null): Promise<RiskMovers | null> {
  const wrap = await cachedCall<ToolResponse<RiskMovers>>('/tools/executive/pmops/risk-movers', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load risk movers', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPmOpsCloseoutStats(token: string | null): Promise<CloseoutStats | null> {
  const wrap = await cachedCall<ToolResponse<CloseoutStats>>('/tools/executive/pmops/closeout-stats', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load closeout stats', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPmOpsCoAging(token: string | null): Promise<CoAging | null> {
  const wrap = await cachedCall<ToolResponse<CoAging>>('/tools/executive/pmops/co-aging', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load CO aging', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

// ── Subs lens + Manpower (Phase 3 — real data where it exists, honest gaps) ──
export interface SubScorecard {
  vendor_code: string;
  vendor_name: string;
  trades: string[];
  job_count: number;
  contract_value: number;
  compliance_gaps: number;
  compliance_score: number;
  schedule_score: number | null;
  quality_score: number | null;
  safety_score: number | null;
  composite: number | null;
  provisional: boolean;
  data_gap: string[];
  schedule_matched: boolean;
  submittal_total: number;
  submittal_overdue: number;
}
export interface SubScorecards {
  subs: SubScorecard[];
  unblock: Record<string, string>;
  note: string;
}
export interface ManpowerPlan {
  actuals_weekly: { week_start: string; total_hours: number; self_hours: number }[];
  demand_weekly: { week_start: string; demand_hours: number }[];
  self_perform_share: number | null;
  coverage: { projects_with_resource_loading: number; total_active: number };
  available_headcount: null;
  trade_gaps: null;
  data_gap: string[];
  note: string;
}
export async function getPmOpsSubScorecards(token: string | null): Promise<SubScorecards | null> {
  const wrap = await cachedCall<ToolResponse<SubScorecards>>('/tools/executive/pmops/sub-scorecards', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load sub scorecards', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPmOpsManpower(token: string | null): Promise<ManpowerPlan | null> {
  const wrap = await cachedCall<ToolResponse<ManpowerPlan>>('/tools/executive/pmops/manpower', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load manpower plan', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

// ── Accounting ▸ Performance ▸ PM Performance (dollar made per dollar spent) ──
// Mirrors tools_service/db/queries/executive_performance.py. The to-date ratio
// is billed/jtd gated by margin_basis (earned revenue is dead in Spectrum);
// aggregates are Σbilled/Σcost over measured jobs, never a mean of ratios.
export interface PerfMonth {
  ym: string;
  billed: number;
  cost: number;
  labor_cost: number;
  hours: number;
  ratio_ttm: number | null;
}
export interface PerfAggregate {
  job_count: number;
  measured_jobs: number;
  excluded: Record<string, number>;
  measured_billed: number;
  measured_cost: number;
  total_billed: number;
  total_cost: number;
  dollar_ratio: number | null;
  gross_profit: number;
  current_margin_pct: number | null;
}
export interface PerfForecast {
  forecast_margin_pct: number | null;
  forecast_ratio: number | null;
  scored_jobs: number;
  contract_coverage: number | null;
}
export interface PerfLeverage {
  ttm_months: number;
  ttm_billed?: number;
  ttm_cost?: number;
  ttm_labor_cost?: number;
  ttm_hours?: number;
  ttm_gross_profit?: number;
  gp_per_labor_dollar: number | null;
  gp_per_hour: number | null;
}
export interface PerfModelBlock {
  stale: boolean;
  current_model_version: string;
  current_label_kind: string;
  stored_model_versions: string[];
  note?: string;
}
/** Historical norm from CLOSED jobs — the realized $-made-per-$ at close. */
export interface PerfBaseline {
  jobs: number;
  excluded: number;
  ratio_median: number | null;
  ratio_p25: number | null;
  ratio_p75: number | null;
  margin_median: number | null;
}
export interface PerfTrend {
  as_of: string | null;
  months: PerfMonth[];
  portfolio: PerfAggregate;
  forecast: PerfForecast;
  leverage: PerfLeverage;
  baseline: PerfBaseline;
  model: PerfModelBlock;
}
/** One rollup row = one RESOLVED PERSON (Spectrum codes are not
 *  person-stable — one code can span several PMs and one PM several codes),
 *  or an honest nameless bucket for an unresolvable code / uncoded jobs. */
export interface PerfPmRow extends PerfAggregate {
  pm_key: string;
  pm_name: string | null;
  pm_code: string | null;
  pm_codes: string[];
  share_of_cost: number | null;
  forecast_margin_pct: number | null;
  forecast_ratio: number | null;
}
export interface PerfPmRollup {
  as_of: string | null;
  pms: PerfPmRow[];
  portfolio: PerfAggregate;
  model: PerfModelBlock;
}
export interface PerfJobRow {
  job_number: string;
  description: string | null;
  customer_name: string | null;
  project_id: number | null;
  project_name: string | null;
  pm_code: string | null;
  pm_name: string | null;
  revised_contract: number;
  billed: number;
  jtd_cost: number;
  progress: number | null;
  margin_basis: string;
  current_margin_pct: number | null;
  dollar_ratio: number | null;
  measurable: boolean;
  forecast_margin_p50: number | null;
  forecast_ratio: number | null;
  risk_band: string | null;
  labor_budget: number | null;
  labor_budget_original: number | null;
  labor_cost_to_date: number;
  labor_budget_stale: boolean;
  labor_burn_index: number | null;
  self_perform_share: number | null;
}
export interface PerfJobs {
  as_of: string | null;
  jobs: PerfJobRow[];
  portfolio: PerfAggregate;
  model: PerfModelBlock;
}
export async function getPerformanceTrend(token: string | null, months?: number): Promise<PerfTrend | null> {
  const body: Record<string, unknown> = {};
  if (months) body.months = months;
  const wrap = await cachedCall<ToolResponse<PerfTrend>>('/tools/executive/performance/trend', token, body);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load performance trend', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPerformancePmRollup(token: string | null): Promise<PerfPmRollup | null> {
  const wrap = await cachedCall<ToolResponse<PerfPmRollup>>('/tools/executive/performance/pm-rollup', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load PM rollup', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getPerformanceJobs(token: string | null): Promise<PerfJobs | null> {
  const wrap = await cachedCall<ToolResponse<PerfJobs>>('/tools/executive/performance/jobs', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load performance jobs', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

// ── Accounting ▸ Performance ▸ Change Order Capture ──────────────────────────
// Mirrors tools_service/db/queries/executive_co_capture.py. Every dollar comes
// from the contract ledger (revised − original = change_orders, penny-exact on
// every job); the change-request detail is read only for counts and a
// reconciliation flag, because 2,076 of 2,351 CR rows carry no amount at all
// (docs/CHANGE_ORDER_DATA_FINDINGS.md). Net-of-subs figures are gated on
// net_basis_*: a job with no mirrored subcontract value has an UNKNOWN
// sub-side, not a zero one, and is excluded from the net rather than counted
// as pure capture.
export type CoCaptureScope = 'all' | 'active' | 'closed';

export interface CoCaptureAggregate {
  n_jobs: number;
  thin: boolean;
  original_contract: number;
  owner_co: number;
  co_growth_pct: number | null;
  sub_co: number;
  net_co: number;
  net_co_growth_pct: number | null;
  sub_co_share: number | null;
  net_basis_jobs: number;
  net_basis_original: number;
  net_basis_share: number | null;
  pending_amount: number;
  pending_count: number;
  pending_vs_executed: number | null;
  jobs_with_co: number;
  jobs_cr_reconciling: number;
  cr_detail_coverage: number | null;
  cr_rows: number;
  cr_open: number;
  cr_priced: number;

  /** What happened on the sub side, so a group is never reduced to a dash.
   *  'captured' — owner side net positive; read sub_co_share.
   *  'absorbed' — owner side net zero-or-negative with money moving; read
   *               absorbed_amount (what the group is out of pocket) and, when
   *               there was a credit, pass_through_share (how much of it was
   *               handed down rather than eaten).
   *  'none'     — nothing moved either way; a dash is the honest answer. */
  net_state: 'captured' | 'absorbed' | 'none';
  absorbed_amount: number | null;
  pass_through_share: number | null;

  // Movement — dates and day counts, never dollars. These exist precisely
  // where the dollar metrics cannot: the CR dates are entered even on the
  // rows whose amounts never were.
  cr_executed: number;
  median_days_to_execute: number | null;
  cycle_sample: number;
  conversion_rate: number | null;
  days_requested: number;
  days_approved: number;
  /** granted ÷ requested. On a GC the gap is absorbed overhead. */
  days_capture: number | null;
  cr_with_days: number;

  // Outcome — contract-weighted over jobs whose margin is fit to read.
  margin_jobs: number;
  current_margin_pct: number | null;
  fade_jobs: number;
  /** points of gross margin lost against the bid. Positive = eroded. */
  fade_pts: number | null;
}
export interface CoCaptureGroup extends CoCaptureAggregate {
  key: string;
  label: string;
  pm_code?: string | null;
  pm_name?: string | null;
}
export interface CoCaptureJobRow {
  job_number: string;
  description: string | null;
  status_code: string | null;
  customer_code: string | null;
  customer_name: string | null;
  segment: string;
  segment_is_override: boolean;
  pm_code: string | null;
  pm_name: string | null;
  size_band: string;
  original_contract: number;
  revised_contract: number;
  owner_co: number;
  co_growth_pct: number | null;
  sub_original: number;
  /** null when the job has no mirrored subcontract value — unknown, not zero. */
  sub_co: number | null;
  sub_covered: boolean;
  net_co: number | null;
  sub_count: number;
  pending_amount: number;
  pending_count: number;
  cr_rows: number;
  cr_open: number;
  cr_priced: number;
  cr_executed: number;
  cr_reconciles: boolean;
  median_days_to_execute: number | null;
  days_requested: number;
  days_approved: number;
  cr_with_days: number;
  progress: number | null;
  /** 'too_early' | 'implausible' | 'provisional' | 'firm' — the same gate the
   *  PM Performance and EAC boards use. Only provisional/firm carry a number. */
  margin_basis: string;
  expected_margin_pct: number | null;
  current_margin_pct: number | null;
  fade_pts: number | null;
}
export interface CoCaptureUnmapped {
  customer_code: string | null;
  customer_name: string | null;
  n_jobs: number;
  original_contract: number;
}
export interface CoCapture {
  as_of: string | null;
  scope: CoCaptureScope;
  portfolio: CoCaptureAggregate;
  segments: CoCaptureGroup[];
  dod_rollup: CoCaptureAggregate & { label: string };
  non_dod_rollup: CoCaptureAggregate & { label: string };
  pms: CoCaptureGroup[];
  size_bands: CoCaptureGroup[];
  unmapped_customers: CoCaptureUnmapped[];
  segment_labels: Record<string, string>;
}
export interface CoCaptureJobs {
  as_of: string | null;
  scope: CoCaptureScope;
  jobs: CoCaptureJobRow[];
  portfolio: CoCaptureAggregate;
  segment_labels: Record<string, string>;
}
export async function getCoCapture(token: string | null, scope: CoCaptureScope = 'all'): Promise<CoCapture | null> {
  const wrap = await cachedCall<ToolResponse<CoCapture>>('/tools/executive/performance/co-capture', token, { scope });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load change order capture', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export interface CoCaptureVendorRow {
  vendor_code: string | null;
  /** null when Spectrum's vendor lookup cannot name the code — an honest
   *  nameless bucket, never a borrowed name. */
  vendor_name: string | null;
  label: string;
  n_subcontracts: number;
  n_jobs: number;
  original_amount: number;
  change_orders: number;
  revised_amount: number;
  growth_pct: number | null;
  billed: number;
  /** bought-out value under the floor — a big percentage on a small package. */
  thin: boolean;
}
export type CoCaptureVendorSort = 'value' | 'growth';
export interface CoCaptureVendors {
  as_of: string | null;
  scope: CoCaptureScope;
  sort: CoCaptureVendorSort;
  vendors: CoCaptureVendorRow[];
  totals: {
    n_vendors: number;
    n_shown: number;
    original_amount: number;
    change_orders: number;
    growth_pct: number | null;
  };
}
export async function getCoCaptureVendors(token: string | null, scope: CoCaptureScope = 'all', sort: CoCaptureVendorSort = 'value'): Promise<CoCaptureVendors | null> {
  const wrap = await cachedCall<ToolResponse<CoCaptureVendors>>('/tools/executive/performance/co-capture/vendors', token, { scope, sort });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load subcontractor change orders', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getCoCaptureJobs(token: string | null, scope: CoCaptureScope = 'all'): Promise<CoCaptureJobs | null> {
  const wrap = await cachedCall<ToolResponse<CoCaptureJobs>>('/tools/executive/performance/co-capture/jobs', token, { scope });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load change order capture jobs', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
/** Correct the segment derived for one Spectrum customer. Not cached — a
 *  write, and the server drops the rollup memo so the next read reflects it. */
export async function setCustomerSegment(
  token: string | null,
  body: { company_code: string; customer_code: string; segment: string; note?: string },
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/executive/performance/customer-segment', token, body);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to set customer segment', 500, wrap.error_code ?? undefined);
}

// ── Accounting ▸ Performance job boards ──────────────────────────────────────
// Mirrors tools_service/db/queries/executive_job_boards.py. One row per active
// job carrying every column of the Job Profitability, Contract Margin and Cash
// Flow by Job boards; the SPA picks columns per board. Earned revenue (and the
// GP columns that divide by it) is null until the controller's calculation is
// loaded (spectrum_job_earned_revenue) — never a Spectrum figure.
export interface JobBoardRow {
  job_number: string;
  description: string | null;
  customer_name: string | null;
  project_id: number | null;
  project_name: string | null;
  pm_code: string | null;
  pm_name: string | null;
  // contract margin
  original_contract: number;
  revised_contract: number;
  change_orders: number;
  original_budget: number | null;
  original_gross_margin: number | null;
  projected_cost: number | null;
  /** 'forecast' = the project card's forecast, cost to date + cost to complete
   *  (ADR-003 D16); 'budget_review' = the same, with a finished budget review
   *  steering some codes; 'what-if' = the controller's override. */
  projected_cost_basis: 'forecast' | 'budget_review' | 'what-if' | null;
  /** The job's forecast could not be computed today: its margin is MISSING,
   *  not "no budget" (out of every projected total). */
  projection_unavailable?: boolean;
  no_projection_reason?: NoProjectionReason | null;
  forecast_flags?: ProjectionFlag[];
  projected_margin: number | null;
  /** projected margin − original gross margin, as a ratio (−0.032 = −3.2 pts); null unless both exist. */
  margin_variance: number | null;
  // job profitability — from the controller's monthly close workbook,
  // never Spectrum (whose Earned_Revenue is 0 on every job). Always render
  // earned_close_label beside these: they are a close-month vintage next to
  // a nightly mirror.
  earned_revenue: number | null;
  earned_basis: string | null;
  earned_as_of: string | null;
  /** 'June 2026' — the close these figures are as of. */
  earned_close_label: string | null;
  jtd_cost: number;
  /** Cost to date WITH indirects, the basis the controller's gross profit
   *  nets. Null on rows loaded by the older CSV path. */
  cost_to_date_with_indirects: number | null;
  gross_profit: number | null;
  gross_profit_pct: number | null;
  /** Which cost basis gross_profit actually used. 'jtd_cost' means the row
   *  predates the close load and omits the indirect allocation. */
  gross_profit_cost_basis: 'cost_with_indirects' | 'jtd_cost' | null;
  /** Contract less EARNED revenue. NOT the WIP schedule's backlog
   *  (revised less BILLED) — the gap between them is over/under-billing. */
  earned_backlog: number | null;
  // cash flow by job
  billed: number;
  cash_received: number;
  cash_paid: number;
  net_cash_flow: number;
  last_bill_date: string | null;
  ar_due: number;
  ap_due: number;
  billed_pct: number | null;
}
export interface JobBoardTotals {
  job_count: number;
  original_contract: number;
  revised_contract: number;
  change_orders: number;
  jtd_cost: number;
  billed: number;
  cash_received: number;
  cash_paid: number;
  net_cash_flow: number;
  ar_due: number;
  ap_due: number;
  original_budget: number;
  original_gross_margin: number | null;
  original_margin_jobs: number;
  projected_cost: number;
  projected_margin: number | null;
  projected_margin_jobs: number;
  margin_variance: number | null;
  margin_variance_jobs: number;
  earned_revenue: number | null;
  gross_profit: number | null;
  gross_profit_pct: number | null;
  earned_jobs: number;
  earned_backlog: number | null;
  earned_backlog_jobs: number;
  billed_pct: number | null;
  /** Jobs whose forecast could not be computed — out of the projected totals. */
  projection_unavailable_jobs?: string[];
  forecast_flags?: ProjectionFlagTotals;
}
export interface JobBoards {
  as_of: string | null;
  jobs: JobBoardRow[];
  totals: JobBoardTotals;
  earned_revenue: {
    filled_jobs: number; job_count: number; note: string;
    /** 'June 2026' — the close the loaded figures are as of. */
    close_label: string | null;
  };
}
export async function getPerformanceJobBoards(token: string | null): Promise<JobBoards | null> {
  const wrap = await cachedCall<ToolResponse<JobBoards>>('/tools/executive/performance/job-boards', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load job boards', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** What-if (hypothetical situation) review package — narrative docx +
 *  workbook xlsx staged to SharePoint. Carries ONLY the controller's
 *  overrides; the server re-reads every actual from the mirror. */
export interface WhatIfFinishResult {
  document_title: string; as_of: string | null; sharepoint_path: string;
  document_filename: string; document_file_url: string | null;
  spreadsheet_filename: string; spreadsheet_file_url: string | null;
  assumptions: number; jobs_changed: number;
  actual_totals: JobBoardTotals; hypothetical_totals: JobBoardTotals;
}
export async function finishPerformanceWhatIf(
  token: string | null,
  params: {
    title?: string;
    notes?: string;
    overrides: { job_number: string; field: string; value: number }[];
  },
): Promise<WhatIfFinishResult> {
  const wrap = await call<ToolResponse<WhatIfFinishResult>>('/tools/executive/performance/what-if/finish', token, params);
  if (!wrap.success || !wrap.data?.[0]) {
    throw new ApiError(wrap.error ?? 'failed to generate the review package', 500, wrap.error_code ?? undefined);
  }
  return wrap.data[0];
}

// ── Executive-agent structured synthesis (PM Ops rail) ──────────────────────
// Mirrors inference_service/foundry/executive_agent.py:ExecAgentAnswer. The
// rail feeds the endpoint the panel data it already fetched; the agent answers
// ONLY from it and cites the panel each number came from.
export type PmOpsPanelKey =
  | 'kpis' | 'attention' | 'risk_board' | 'risk_movers'
  | 'pm_performance' | 'eac_fade' | 'co_aging' | 'closeout'
  | 'rfi_aging' | 'submittal_aging' | 'rfi_turnaround' | 'ball_in_court' | 'subs';
export type ExecAgentTone = 'neutral' | 'good' | 'warn' | 'danger' | 'info';
export interface ExecEvidenceRow { label: string; value: string; tone: ExecAgentTone; panel: PmOpsPanelKey | null; }
export interface ExecSourceRef { panel: PmOpsPanelKey; label: string; }
export interface ExecScopeDirective { kind: 'kpi' | 'pm' | 'sub' | 'clear'; id: string | null; label: string | null; }
export interface ExecAgentAnswer {
  intent: string;
  lead: string;
  rows: ExecEvidenceRow[];
  note: string | null;
  sources: ExecSourceRef[];
  scope: ExecScopeDirective | null;
  fallback: boolean;
  data_areas: string[] | null;
}
export interface ExecAgentSynthesizeBody {
  session_id: string;
  question: string;
  lens: string;
  scope: string | null;
  panels: Record<string, unknown>;
}
export async function synthesizeExecAgent(token: string | null, body: ExecAgentSynthesizeBody): Promise<ExecAgentAnswer> {
  const res = await call<{ answer: ExecAgentAnswer; usage: unknown }>('/generate/executive-agent/synthesize', token, body);
  return res.answer;
}

// ── Estimating-agent structured synthesis (Estimating rail) ─────────────────
// Mirrors inference_service/foundry/estimating_agent.py:EstAgentAnswer. The same
// endpoint serves two personas by session domain — Odin (executive session) and
// Janus (precon session). The rail feeds it the panel data already on screen; it
// answers ONLY from that and cites the panel each number came from.
export type EstimatingPanelKey =
  | 'kpis' | 'attention'
  | 'pipeline_by_stage' | 'due_load' | 'funnel' | 'hygiene'
  | 'win_loss' | 'loss_reasons' | 'segments' | 'cost_accuracy'
  | 'competitors' | 'sectors' | 'recent'
  | 'estimators' | 'flow' | 'win_prob';
export interface EstEvidenceRow { label: string; value: string; tone: ExecAgentTone; panel: EstimatingPanelKey | null; }
export interface EstSourceRef { panel: EstimatingPanelKey; label: string; }
export interface EstScopeDirective { kind: 'section' | 'clear'; id: string | null; label: string | null; }
export interface EstAgentAnswer {
  intent: string;
  lead: string;
  rows: EstEvidenceRow[];
  note: string | null;
  sources: EstSourceRef[];
  scope: EstScopeDirective | null;
  fallback: boolean;
  data_areas: string[] | null;
}
export interface EstAgentSynthesizeBody {
  session_id: string;
  question: string;
  section: string;
  scope: string | null;
  panels: Record<string, unknown>;
}
export async function synthesizeEstimatingAgent(
  token: string | null,
  body: EstAgentSynthesizeBody,
): Promise<{ answer: EstAgentAnswer; persona: string }> {
  const res = await call<{ answer: EstAgentAnswer; persona: string; usage: unknown }>(
    '/generate/estimating-agent/synthesize', token, body,
  );
  return { answer: res.answer, persona: res.persona };
}

export async function getPmPerformance(token: string | null): Promise<PmPerformanceRow[]> {
  const wrap = await cachedCall<ToolResponse<PmPerformanceRow>>('/tools/executive/pm-performance', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load PM performance', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/** Cost exposure rollup — single object: portfolio total + per-project. */
export interface CostExposureSummary {
  portfolio_total: {
    total_exposure: number | null; projects_with_exposure: number | null;
    // Hard Spectrum change totals (change_events is DMSA-403 sparse, so prefer these):
    pending_cr_total?: number | null; pending_cr_count?: number | null;
    executed_co_total?: number | null; jobs_with_pending?: number | null;
  };
  by_project: Array<{ name: string; cost_exposure: number | null; change_event_count: number | null }>;
}
export async function getPortfolioCostExposure(token: string | null): Promise<CostExposureSummary | null> {
  const wrap = await cachedCall<ToolResponse<CostExposureSummary>>('/tools/executive/portfolio/cost-exposure', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load cost exposure', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Open-RFI count per active project. */
export interface RfiHealthRow { name: string; open_rfi_count: number }
export async function getRfiHealthPortfolio(token: string | null): Promise<RfiHealthRow[]> {
  const wrap = await cachedCall<ToolResponse<RfiHealthRow>>('/tools/executive/health/rfis', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load RFI health', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/** Overdue-submittal count per active project. */
export interface SubmittalHealthRow { name: string; overdue_count: number }
export async function getSubmittalHealthPortfolio(token: string | null): Promise<SubmittalHealthRow[]> {
  const wrap = await cachedCall<ToolResponse<SubmittalHealthRow>>('/tools/executive/health/submittals', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load submittal health', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/* ---------------- Ball-in-court turnaround (custody ledger, mig 133) ----------
   Management-only (admin/exec) analytics over review-handoff turnaround. Dwell is
   in BUSINESS hours; every dwell ships beside a capture-completeness ratio. */
export interface DwellSummary {
  count: number;
  avg_business_hours: number | null;
  median_business_hours: number | null;
  p90_business_hours: number | null;
}
export interface TurnaroundMetrics {
  total_reviews_closed: number;
  dwell_capture_completeness: number | null;
  dwell_capture_note: string;
  overall_dwell: DwellSummary;
  by_item_type: Record<string, DwellSummary>;
  disposition_counts: Record<string, number>;
}
export interface TurnaroundPerson extends DwellSummary {
  reviewer_oid: string;
  reviewer_name: string | null;
  is_oid: boolean;
}
export interface TurnaroundByPerson {
  reviewers: TurnaroundPerson[];
  dwell_capture_completeness: number | null;
  total_reviews_closed: number;
}
export interface OpenBallItem {
  review_key: string;
  item_type: string;
  project_id: number | null;
  holder_oid: string | null;
  holder_name: string | null;
  opened_at: string;
  open_business_hours: number;
  open_calendar_hours: number;
}
export interface TurnaroundFilters { project_id?: number; item_type?: string; limit?: number }

export async function getTurnaroundMetrics(
  token: string | null, filters: TurnaroundFilters = {}
): Promise<TurnaroundMetrics | null> {
  const wrap = await cachedCall<ToolResponse<TurnaroundMetrics>>('/tools/turnaround/metrics', token, filters);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load turnaround metrics', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getTurnaroundByPerson(
  token: string | null, filters: TurnaroundFilters = {}
): Promise<TurnaroundByPerson | null> {
  const wrap = await cachedCall<ToolResponse<TurnaroundByPerson>>('/tools/turnaround/by-person', token, filters);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load turnaround scorecard', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function getTurnaroundOpenBall(
  token: string | null, filters: TurnaroundFilters = {}
): Promise<OpenBallItem[]> {
  const wrap = await cachedCall<ToolResponse<OpenBallItem>>('/tools/turnaround/open-ball', token, filters);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load open-ball worklist', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/* Milestone-pair turnaround: substantial completion → final billing. */
export interface MilestoneProject {
  project_id: number;
  name: string | null;
  project_number: string | null;
  substantial_completion_date: string | null;
  final_billing_date: string | null;
  gap_days: number | null;
  status: 'billed' | 'completed_unbilled' | 'no_completion';
}
export interface MilestoneGaps {
  projects: MilestoneProject[];
  summary: {
    count_with_both: number;
    avg_gap_days: number | null;
    median_gap_days: number | null;
    count_completed_unbilled: number;
    note: string;
  };
}
export async function getTurnaroundMilestoneGaps(
  token: string | null, filters: TurnaroundFilters = {}
): Promise<MilestoneGaps | null> {
  const wrap = await cachedCall<ToolResponse<MilestoneGaps>>('/tools/turnaround/milestone-gaps', token, filters);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load milestone gaps', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Recent project status transitions (last 30 days). */
export interface StatusChangeRow {
  name: string; from_status: string | null; to_status: string | null;
  changed_at: string | null; notes: string | null;
}
export async function getRecentStatusChanges(token: string | null): Promise<StatusChangeRow[]> {
  const wrap = await cachedCall<ToolResponse<StatusChangeRow>>('/tools/executive/portfolio/status-changes', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load status changes', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/** Portfolio bid win rate (single object). value-weighted fields use
 *  awarded_value with estimate_value fallback (migration 092 companion). */
export interface BidWinRate {
  awarded_count: number; lost_count: number; total_decided: number; win_rate_pct: number | null;
  awarded_value_total: number | null; decided_value_total: number | null;
  value_win_rate_pct: number | null;
}
export async function getBidWinRate(token: string | null, filters?: BidDashboardFilters): Promise<BidWinRate | null> {
  const wrap = await cachedCall<ToolResponse<BidWinRate>>('/tools/executive/bids/win-rate', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load win rate', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Shared bid-population filters for the estimating-dashboard rollups.
 *  Mirrors BidMetricsQueryRequest server-side; all optional — an empty
 *  object is the unfiltered portfolio rollup. */
export interface BidDashboardFilters {
  /** Buildr's stage (Pipeline, Estimating, Proposal, Hard Bid, Pending, Not in
   *  Buildr) or outcome (Won, Lost, Did Not Pursue, Cancelled) — the server's
   *  db/queries/bid_stages.py. Replaced phase + category on 2026-10-29. */
  stage?: string;
  year?: number;       // YEAR(due_date)
  quarter?: number;    // 1-4 (of due_date)
  client?: string;     // bids.owner
  estimator?: string;  // lead estimator name
}

/** Distinct filter-bar options from the live bid population (single object). */
export interface BidFilterOptions {
  /** In Buildr's funnel order, then Not in Buildr, then the outcomes. */
  stages: string[]; years: number[]; clients: string[]; estimators: string[];
}
export async function getBidFilterOptions(token: string | null): Promise<BidFilterOptions | null> {
  const wrap = await cachedCall<ToolResponse<BidFilterOptions>>('/tools/executive/bids/filter-options', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load filter options', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Estimate accuracy on awarded bids (single object). */
export interface EstimateAccuracy {
  awarded_bid_count: number; avg_variance_pct: number | null; within_5pct_count: number;
}
export async function getEstimateAccuracy(token: string | null, filters?: BidDashboardFilters): Promise<EstimateAccuracy | null> {
  const wrap = await cachedCall<ToolResponse<EstimateAccuracy>>('/tools/executive/bids/estimate-accuracy', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load estimate accuracy', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Bid pipeline by stage: count + total estimate value per status. */
/** awarded/lost rows come from Buildr's decided record (source 'buildr_decided',
 *  by decision date); active/withdrawn from the bid record (source 'bids'). */
export interface PipelineStageRow {
  /** Buildr's stage or the outcome (see BidDashboardFilters.stage). */
  stage: string;
  /** Buildr's funnel position for an open stage; null otherwise. */
  position: number | null;
  bid_count: number;
  total_value: number | null;
  source?: 'bids' | 'buildr_decided';
  /** Counts toward the active-pipeline tile: the stages past Pipeline, or
   *  every open stage under a stage filter. */
  in_active_pipeline: boolean;
  /** This stage's share (%) of every bid under the other filters. Optional so
   *  a server without it (mid-deploy) reads as unknown. */
  share_pct?: number | null;
  /** A decided stage: how many were decided in the last 90 days. Null for an
   *  open stage (no decision date). */
  recent_90d_count?: number | null;
  /** Rows with an amount: the divisor for an average (a win with no amount
   *  would otherwise pull it down). */
  valued_count?: number;
  ignored_filters?: string[];
}
export async function getBidPipelineByStage(token: string | null, filters?: BidDashboardFilters): Promise<PipelineStageRow[]> {
  const wrap = await cachedCall<ToolResponse<PipelineStageRow>>('/tools/executive/bids/pipeline', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load pipeline', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/** Bids due soon (single object: pipeline value + count within N days). */
export interface DueSoonSummary { pipeline_value: number | null; bid_count: number }
export async function getBidsDueSoon(token: string | null, filters?: BidDashboardFilters): Promise<DueSoonSummary | null> {
  const wrap = await cachedCall<ToolResponse<DueSoonSummary>>('/tools/executive/bids/due-soon', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load bids due soon', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Pipeline hygiene (single object): due-ahead vs past-due-undecided split,
 *  top-10 concentration, and field-coverage counters (migration 092). */
export interface BidPipelineHealth {
  active_count: number; active_value: number;
  due_ahead_count: number; due_ahead_value: number;
  /** Bid date passed and not yet submitted (Estimating / Proposal / Hard Bid).
   *  A Pending bid is never past due. */
  past_due_count: number; past_due_value: number;
  median_days_overdue: number | null;
  /** Pending: submitted, awaiting the award. Stalled once it has sat in
   *  Pending for pending_stalled_days (Buildr's stage age). Optional so a
   *  server without them (mid-deploy) reads as none. */
  pending_count?: number; pending_value?: number;
  pending_stalled_count?: number; pending_stalled_value?: number;
  pending_stalled_days?: number; pending_oldest_days?: number | null;
  /** Open bids with no Buildr stage (Not in Buildr), by what holds them:
   *  SharePoint-only (no Buildr id), holding work (Buildr dropped it, it has
   *  documents: a person decides), held (dropped, no work: waiting out the
   *  settle pass's 30-day re-entry window). Non-zero under that stage only. */
  not_in_buildr_count?: number; not_in_buildr_sharepoint_only?: number;
  not_in_buildr_holding_work?: number; not_in_buildr_held?: number;
  no_due_date_count: number; no_due_date_value: number;
  top10_value: number; top10_value_share_pct: number | null;
  total_bids: number; missing_estimate_value: number; missing_due_date: number;
  missing_owner: number; awarded_missing_value: number;
  decided_missing_decided_at: number; with_lead_estimator: number;
}
export async function getBidPipelineHealth(token: string | null, filters?: BidDashboardFilters): Promise<BidPipelineHealth | null> {
  const wrap = await cachedCall<ToolResponse<BidPipelineHealth>>('/tools/executive/bids/pipeline-health', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load pipeline health', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Win/loss pivot rows across mirror-native dimensions (market_sector | size_band |
 *  client). Sourced from the decided-outcome mirror so per-segment rates reconcile
 *  with the headline; withdrawn_count is always 0 (the mirror is won/lost only). */
export interface BidSegmentRow {
  dimension: string; segment: string;
  bid_count: number; active_count: number; awarded_count: number;
  lost_count: number; withdrawn_count: number; decided_count: number;
  win_rate_pct: number | null; active_value: number;
}
export async function getBidSegments(token: string | null, filters?: BidDashboardFilters): Promise<BidSegmentRow[]> {
  const wrap = await cachedCall<ToolResponse<BidSegmentRow>>('/tools/executive/bids/segments', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load bid segments', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/** Estimating workload forecast: active bids per due week (Monday-keyed). */
export interface BidDueLoadWeek { week_start: string; bid_count: number; total_value: number }
export async function getBidDueLoad(token: string | null, filters?: BidDashboardFilters): Promise<BidDueLoadWeek[]> {
  const wrap = await cachedCall<ToolResponse<BidDueLoadWeek>>('/tools/executive/bids/due-load', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load due load', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/** Status-hygiene cross-checks (single object with two worklists). */
export interface BidReconciliationItem {
  bid_id: number; bid_name: string; status: string; project_id: number; project_name: string;
}
export interface BidReconciliation {
  bids_linked_not_awarded: BidReconciliationItem[];
  projects_missing_awarded_bid: { project_id: number; project_name: string }[];
  issue_count: number;
}
export async function getBidReconciliation(token: string | null): Promise<BidReconciliation | null> {
  const wrap = await cachedCall<ToolResponse<BidReconciliation>>('/tools/executive/bids/reconciliation', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load reconciliation', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Pipeline flow per month: added vs decided (decided accrues from 092 deploy). */
/** decided/awarded are Buildr's decided record by decision month; ignored_filters names any
 *  filter that record cannot apply (category). */
export interface BidFlowMonth {
  month: string;
  added_count: number;
  decided_count: number;
  awarded_count: number;
  ignored_filters?: string[];
}
export async function getBidFlowTrend(token: string | null, filters?: BidDashboardFilters): Promise<BidFlowMonth[]> {
  const wrap = await cachedCall<ToolResponse<BidFlowMonth>>('/tools/executive/bids/flow-trend', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load flow trend', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/** Per-estimator rollup from the Buildr mirrors (migration 120): won/lost
 *  over the FULL decided history (bid_competitor_results — the population
 *  Buildr Insights counts) + open pursuits from bid_win_scores. year/quarter
 *  filter on the DECISION date (award_date); stage is reported in
 *  ignored_filters, never silently applied. Refreshed nightly. */
export interface EstimatorPerfRow {
  estimator: string;
  open_count: number;
  open_value: number;
  won_count: number;
  lost_count: number;
  decided_count: number;
  win_rate_pct: number | null;
  won_value: number;
  /** Spectrum closed-job estimate→final overrun via the Buildr
   *  project_number ↔ job_number link; null when no closed jobs match. */
  spectrum_job_count: number | null;
  spectrum_weighted_overrun_pct: number | null;
  /** Open pursuits assigned to this estimator due within DUE_SOON_DAYS — the
   *  workload signal on the estimator card. Optional: populated once the
   *  backend adds it (see plan Workstream B); undefined renders as "—". */
  open_due_soon_count?: number | null;
}
export interface EstimatorPerformance {
  source: 'buildr_mirror';
  as_of: string | null;
  estimators: EstimatorPerfRow[];
  attributed_decided_count: number;
  unattributed_decided_count: number;
  attributed_open_count: number;
  unattributed_open_count: number;
  ignored_filters: string[];
  notes: string;
}
export async function getEstimatorPerformance(token: string | null, filters?: BidDashboardFilters): Promise<EstimatorPerformance | null> {
  const wrap = await cachedCall<ToolResponse<EstimatorPerformance>>('/tools/executive/bids/estimator-performance', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load estimator performance', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Why we lose — loss_reason rollup over the Buildr decided mirror.
 *  Honors year/quarter (decision date), client, estimator; stage
 *  comes back in ignored_filters. */
export interface LossReasonRow {
  reason: string;                // 'Price', 'Competitor', … or '(unrecorded)'
  lost_count: number;
  lost_value: number;
  share_pct: number | null;      // of all losses in filter scope
}
export interface BidLossReasons {
  reasons: LossReasonRow[];
  total_lost: number;
  with_reason_count: number;
  as_of: string | null;
  ignored_filters: string[];
}
export async function getBidLossReasons(token: string | null, filters?: BidDashboardFilters): Promise<BidLossReasons | null> {
  const wrap = await cachedCall<ToolResponse<BidLossReasons>>('/tools/executive/bids/loss-reasons', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load loss reasons', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Open-pursuit funnel by Buildr project_stage (Pipeline → … → Pending).
 *  Deliberately unfiltered — portfolio-wide, loads once. */
export interface PipelineFunnelStage {
  project_stage: string;         // '(unstaged)' bucket sorts last
  position: number | null;
  count: number;
  nominal_value: number;
  /** Null when the win model is withheld (BID_WIN_MODEL_GATE=enforce and a
   *  failed check): weighted money is model output; counts and nominal are not. */
  weighted_value: number | null;
  median_stage_age_days: number | null;
  max_stage_age_days: number | null;
  stale_count: number;
}
export interface BidPipelineFunnel {
  as_of: string | null;
  total_open: number;
  total_nominal: number;
  total_weighted: number | null;
  stale_threshold_days: number;
  stages: PipelineFunnelStage[];
  /** Why the weighted values are null, when the win model is withheld. */
  model_withheld?: string;
}
/** Open-pursuit funnel; the filter bar narrows its pursuits (none = the whole
 *  pipeline). Cached per filter set — sweep-time data. */
export async function getBidPipelineFunnel(token: string | null, filters?: BidDashboardFilters): Promise<BidPipelineFunnel | null> {
  const wrap = await cachedCall<ToolResponse<BidPipelineFunnel>>('/tools/bids/win-probability/funnel', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load pipeline funnel', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Who beats us, where, and by how much — the decided-pursuit bid-tab rollup
 *  (bid_competitor_results, migration 112). The estimating filter bar applies
 *  (getBidCompetitorHistory); the endpoint's own market_sector/competitor
 *  filters are the agent's and the panel never sends them. NOTE all rates are FRACTIONS
 *  (0.27 = 27%) — ×100 before fmtPct. 'overbid' is our recorded ESTIMATE over
 *  the winning number (Buildr doesn't carry our sealed bid); see `note`. */
export interface CompetitorRow {
  competitor: string;
  losses_to: number;
  sectors: Record<string, number>;      // sector → losses to this bidder, desc
  examples: { name: string | null; bid_due_date: string | null; winning_amount: number | null; our_amount: number | null }[];
  median_overbid_pct: number | null;    // fraction; null when no example carried both numbers
  overbid_sample: number;
}
export interface CompetitorSectorRow {
  market_sector: string;
  decided: number;
  won: number;
  with_bid_tab: number;
  not_low: number;                       // decided pursuits where we bid but weren't low
  win_rate: number | null;               // fraction
  median_placement: number | null;       // 1 = low bidder
  placement_sample: number;
}
export interface CompetitorLossReason {
  reason: string;
  count: number;
  sectors: Record<string, number>;
  by_year: Record<string, number>;
  example_names: string[];
  share_of_lost: number | null;          // fraction
}
export interface CompetitorRecentResult {
  buildr_project_id: number | null;
  name: string | null;
  owner: string | null;
  amount: number | null;                 // our estimate
  market_sector: string | null;
  bid_due_date: string | null;
  outcome: string | null;                // 'won' | 'lost'
  low_bidder: string | null;
  low_amount: number | null;             // the winning number
  placement: number | null;
  bidders: number | null;
  not_low: boolean | null;
  bid_results_raw: string | null;        // hand-typed bid tab, unparsed shapes surface verbatim
  loss_reason: string | null;
  assigned_to_name: string | null;
  award_date: string | null;
  state: string | null;
}
export interface BidCompetitorHistory {
  as_of: string | null;
  decided_total: number;
  won_total: number;
  with_bid_tab: number;
  lost_total: number;
  competitors: CompetitorRow[];          // ranked by losses_to, desc
  sectors: CompetitorSectorRow[];        // ranked by decided, desc
  loss_reasons: CompetitorLossReason[];  // ranked by count, desc
  recent_results: CompetitorRecentResult[];
  note: string;
  /** Filters the decided mirror cannot honor (stage), as on the other
   *  Buildr-mirror panels. */
  ignored_filters?: string[];
}
/** Decided-pursuit competitor rollup. The filter bar applies as on the other
 *  decided-mirror panels (year/quarter on the decision date, client,
 *  estimator; stage reported as ignored). Cached per filter set. */
export async function getBidCompetitorHistory(token: string | null, filters?: BidDashboardFilters): Promise<BidCompetitorHistory | null> {
  const wrap = await cachedCall<ToolResponse<BidCompetitorHistory>>('/tools/bids/win-probability/competitors', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load competitor history', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Weekly bid-KPI trends (migration 161) — 8-week sparklines + WoW deltas for
 *  the five Estimating headline tiles. Values are portfolio-wide; the SPA hides
 *  them whenever a filter is active so a portfolio trend never sits under a
 *  filtered tile. `sparkline_partial` = fewer than 8 weeks accrued yet. */
export interface BidKpiTrendTile {
  key: 'active' | 'pastdue' | 'win' | 'due' | 'acc';
  sparkline: number[];
  delta: number | null;
  delta_pct: number | null;
  delta_basis: string;
  improve_direction: 'up' | 'down' | 'neutral';
  sparkline_partial: boolean;
}
export interface BidKpiTrends {
  tiles: BidKpiTrendTile[];
  history_weeks_available: number;
  history_note: string | null;
}
export async function getBidKpiTrends(token: string | null): Promise<BidKpiTrends | null> {
  const wrap = await cachedCall<ToolResponse<BidKpiTrends>>('/tools/executive/bids/kpi-trends', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load KPI trends', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Precon Labor Optimizer (migration 145) — the recommended estimator↔bid
 *  allocation. Advisory: the SPA renders it; a human applies via the assign
 *  tools. Estimator fit is a bounded ±15% multiplier on the untouched win model. */
export interface LaborFitDetail {
  segment: string; estimator_n: number; estimator_wins: number;
  baseline_win_rate: number; shrunk_win_rate: number; confidence: number;
  fit: number; experience_primed: boolean;
}
export interface LaborCollaborator { estimator_key: string; estimator_name: string | null; estimator_entra_id: string | null }
export interface LaborAssignment {
  bid_id: number | null; buildr_project_id: string | null; bid_name: string | null; owner: string | null;
  amount: number | null; value: number; bid_due_date: string | null;
  estimator_key: string; estimator_name: string | null;
  estimator_entra_id: string | null; status: 'keep' | 'move' | 'new' | 'pinned';
  pinned: boolean; priority: boolean; collaborators: LaborCollaborator[]; team_size: number;
  needs_support: boolean;
  difficulty: number; effort_hours: number; base_win_p: number;
  adjusted_win_p: number; fit: number; competition_factor: number; fit_detail: LaborFitDetail;
  expected_value: number; ev_per_hour: number | null; strategic_mult: number;
  rationale: string;
}
export interface LaborNonCompete { bid_id: number; name: string | null; owner: string | null }
export interface BidPlan {
  bid_id: number; non_compete: boolean; priority: boolean;
  pinned_estimator_entra_id: string | null; collaborators: string[]; updated_at: string | null;
}
export interface LaborDeadlineCrunch {
  estimator_key: string; estimator_name: string | null; week: string;
  load_hours: number; capacity_hours: number; bids: string[];
}
export interface LaborOutcomeDistribution { p10: number; p50: number; p90: number; mean: number; rounds: number }
export interface LaborScenario {
  exclude_estimator_oids?: string[]; drop_buildr_ids?: string[];
  capacity_overrides?: Record<string, number>; add_estimator?: { weekly_hours: number; name?: string };
}
export interface LaborOverflow {
  bid_id: number | null; buildr_project_id: string | null; name: string | null; amount: number | null;
  difficulty: number | null; forgone_ev: number; reason: string;
}
export interface LaborNoGo {
  bid_id: number | null; buildr_project_id: string | null; name: string | null; best_win_p: number; reason: string;
}
export interface LaborEstimatorLoad {
  estimator_key: string; estimator_name: string | null; estimator_entra_id: string | null;
  capacity_hours: number; assigned_hours: number; utilization: number | null;
  reserve_pct?: number; assigned_count: number;
}
export interface LaborCoverage {
  current_bids: number; non_compete_count: number; pursuits_missing_win_p: number;
  pursuits_missing_margin?: number;
  assignable_estimators: number; planning_weeks: number; estimators_with_history: number;
  decided_pursuits: number; unmapped_current_assignments: number; objective: string;
  risk_mode: string; scenario?: Record<string, unknown> | null; notes: string;
}
export interface BoardOption {
  estimator_key: string; estimator_name: string | null; estimator_entra_id: string | null;
  eligible: boolean; fit: number; base_win_p: number; adjusted_win_p: number;
  expected_value: number; expected_value_revenue?: number; expected_value_margin?: number;
  effort_hours: number;
  // How the margin objective valued this bid: own margin, imputed from a
  // comparable segment / the portfolio, or revenue-fallback. Absent in revenue mode.
  margin_source?: 'own' | 'segment' | 'portfolio' | 'revenue';
}
export interface BoardBid {
  bid_id: number | null; buildr_project_id: string | null; name: string | null;
  owner: string | null; amount: number | null; difficulty: number; bid_due_date: string | null;
  priority: boolean; pinned: boolean; recommended_estimator_key: string | null;
  placement: 'seated' | 'overflow' | 'no_go'; options: BoardOption[];
}
export interface BoardEstimator {
  estimator_key: string; estimator_name: string | null; estimator_entra_id: string | null;
  capacity_hours: number;
}
export interface LaborBoard { estimators: BoardEstimator[]; bids: BoardBid[] }
export interface LaborOptimization {
  objective: string; risk_mode: string; objective_weights?: ObjectiveWeights | null;
  total_revenue_ev?: number; total_margin_ev?: number;
  assignments: LaborAssignment[]; overflow: LaborOverflow[]; no_go: LaborNoGo[];
  non_compete: LaborNonCompete[]; board: LaborBoard;
  estimator_load: LaborEstimatorLoad[]; deadline_crunch: LaborDeadlineCrunch[];
  outcome_distribution: LaborOutcomeDistribution; total_expected_value: number;
  seated_count: number; overflow_count: number; no_go_count: number;
  coverage: LaborCoverage;
}

export interface ObjectiveWeights { ev: number; win: number; fit: number; balance: number }
export async function getLaborOptimization(
  token: string | null,
  opts?: { objective?: 'revenue' | 'margin'; risk_mode?: 'expected' | 'conservative';
    scenario?: LaborScenario; weights?: ObjectiveWeights | null },
): Promise<LaborOptimization | null> {
  const wrap = await call<ToolResponse<LaborOptimization>>('/tools/bids/labor/optimize', token, {
    objective: opts?.objective ?? 'revenue',
    risk_mode: opts?.risk_mode ?? 'expected',
    scenario: opts?.scenario ?? null,
    weights: opts?.weights ?? null,
  });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to run labor optimizer', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface EstimatorSegmentRow {
  estimator_name: string | null; estimator_entra_id: string | null;
  market_sector: string; size_band: string; decided: number; won: number;
  win_rate: number | null; baseline_win_rate: number; fit: number; confidence: number;
}
export interface EstimatorSegments {
  overall_baseline: number; rows: EstimatorSegmentRow[];
  segment_baselines: { market_sector: string; size_band: string; baseline_win_rate: number; decided: number }[];
}

export async function getEstimatorSegments(token: string | null): Promise<EstimatorSegments | null> {
  const wrap = await cachedCall<ToolResponse<EstimatorSegments>>('/tools/bids/labor/estimator-segments', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load estimator segments', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

// ── Labor Optimizer: trust + intelligence + config surfaces ──────────────────
export interface LaborBacktest {
  evaluated: number; decided_total: number; overall_win_rate: number | null;
  buckets: { label: string; n: number; won: number; win_rate: number | null }[];
  fit_auc: number | null; top_minus_bottom_win_rate: number | null; notes: string;
  /** Time-ordered replay (2026-10-09): Brier of the fit-adjusted odds beside
   *  the segment baseline alone and a constant, on the same pursuits.
   *  `fit_beats_baselines` is the verdict; null when nothing was scored. */
  method?: 'time_ordered';
  brier_fit?: number | null; brier_segment?: number | null; brier_constant?: number | null;
  fit_beats_baselines?: boolean | null;
}
export async function getLaborBacktest(token: string | null): Promise<LaborBacktest | null> {
  const wrap = await cachedCall<ToolResponse<LaborBacktest>>('/tools/bids/labor/backtest', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load backtest', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface SkillMatrix {
  roster_size: number; min_qualify_threshold: number;
  sectors: { market_sector: string; decided_volume: number; qualified_estimators: string[]; qualified_count: number; any_coverage: string[]; risk: 'single_point' | 'thin' | 'ok' }[];
  key_person_risks: { market_sector: string; decided_volume: number; qualified_estimators: string[]; note: string }[];
}
export async function getPreconSkillMatrix(token: string | null): Promise<SkillMatrix | null> {
  const wrap = await cachedCall<ToolResponse<SkillMatrix>>('/tools/bids/labor/skill-matrix', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load skill matrix', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface BdCalibration {
  n: number; mean_gap_bd_minus_model?: number; bd_optimistic_share?: number;
  bd_pessimistic_share?: number; sharp_divergence_count?: number; sharp_divergence_share?: number;
  by_sector?: { market_sector: string; n: number; mean_gap: number }[];
  pursuits?: { name: string | null; market_sector: string; model_win_p: number; bd_win_p: number; gap: number }[];
  note?: string;
}
export async function getBdModelCalibration(token: string | null): Promise<BdCalibration | null> {
  const wrap = await cachedCall<ToolResponse<BdCalibration>>('/tools/bids/labor/bd-calibration', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load BD calibration', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface BidDiscipline {
  available_leads: number; open_pursuits: number; pursued_share_of_available: number | null;
  decided_pursuits: number; decided_won: number; win_rate_on_pursued: number | null; note: string;
}
export async function getPreconBidDiscipline(token: string | null): Promise<BidDiscipline | null> {
  const wrap = await cachedCall<ToolResponse<BidDiscipline>>('/tools/bids/labor/discipline', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load discipline', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface LaborConfigEstimator {
  user_entra_id: string; user_email: string | null; buildr_estimator_id: string | null;
  weekly_hours: number | null; target_value_min: number | null; target_value_max: number | null;
  dev_weight: number | null; reserve_pct: number | null; max_difficulty: number | null;
  available: boolean; updated_at: string | null;
}
export interface LaborConfig { estimators: LaborConfigEstimator[]; strategic: Record<string, unknown> }
export async function getLaborConfig(token: string | null): Promise<LaborConfig | null> {
  const wrap = await call<ToolResponse<LaborConfig>>('/tools/bids/labor/config/get', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load config', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}
export async function setLaborConfig(token: string | null, cfg: {
  user_entra_id: string; user_email?: string | null; weekly_hours: number;
  target_value_min?: number | null; target_value_max?: number | null;
  dev_weight?: number; reserve_pct?: number; max_difficulty?: number | null; available?: boolean;
}): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/bids/labor/config/set', token, cfg);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to save config', 500, wrap.error_code ?? undefined);
}

// ── Strategy-board per-bid plan (non-compete / priority / pin / collaborators) ─
export async function setBidPlan(token: string | null, plan: {
  bid_id: number; non_compete?: boolean; priority?: boolean;
  pinned_estimator_entra_id?: string | null; collaborators?: string[];
}): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/bids/labor/bid-plan/set', token, plan);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to save bid plan', 500, wrap.error_code ?? undefined);
}
export async function resetBidPlan(token: string | null, bidId: number): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/bids/labor/bid-plan/reset', token, { bid_id: bidId });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to reset bid plan', 500, wrap.error_code ?? undefined);
}

// ── Per-user saved boards (allocation snapshots) ──────────────────────────────
export interface SavedBoardMeta { name: string; updated_at: string | null; seated?: number; unassigned?: number }
export async function listSavedBoards(token: string | null): Promise<SavedBoardMeta[]> {
  const wrap = await call<ToolResponse<SavedBoardMeta>>('/tools/bids/labor/board/list', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to list boards', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}
export async function saveBoard(token: string | null, name: string, board: Record<string, unknown>): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/bids/labor/board/save', token, { name, board });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to save board', 500, wrap.error_code ?? undefined);
}
export async function loadBoard(token: string | null, name: string): Promise<Record<string, unknown> | null> {
  const wrap = await call<ToolResponse<{ name: string; board: Record<string, unknown> }>>('/tools/bids/labor/board/load', token, { name });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load board', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0]?.board ?? null;
}
export async function deleteSavedBoard(token: string | null, name: string): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/bids/labor/board/delete', token, { name });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to delete board', 500, wrap.error_code ?? undefined);
}
export async function setStrategicConfig(token: string | null, updates: Record<string, unknown>): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/bids/labor/strategic/set', token, { updates });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to save strategic config', 500, wrap.error_code ?? undefined);
}

/** Revenue scenario planner (single object). Committed baseline (awarded
 *  backlog) + one candidate per active documented bid, each carrying the
 *  derived numbers AND the raw source text behind them so the UI can be
 *  transparent about the derivation. */
export interface ScenarioCandidate {
  bid_id: number; name: string; due_date: string | null;
  value: number; value_source: 'estimate_value' | 'magnitude_midpoint' | 'manual_entry';
  magnitude_text: string | null;
  duration_months: number; duration_source: string;
  performance_period_text: string;
  start_month: string; start_source: 'due_date_plus_lag' | 'assumed';
  start_idx: number; n_months: number; monthly_value: number;
  /** S-curve-shaped per-month spread (empirical, from closed Spectrum jobs'
   *  billing histories); length n_months. Flat when the mirror is empty. */
  monthly_values: number[];
  /** Win odds (the win model via the bids↔Buildr link) + p10–p90 band: the
   *  full owner record for executives and admins, the odds shown otherwise
   *  (assumptions.win_model.odds_reading); null when this bid has no scored
   *  open Buildr pursuit. */
  model_win_p: number | null;
  model_win_p_lo: number | null;
  model_win_p_hi: number | null;
  /** True for a manually-added pursuit (planner_manual_entries, mig 148) —
   *  isolated from the Buildr pipeline. Manual entries can be edited/deleted;
   *  real bids can only be excluded/restored. `bid_id` is NEGATED (-entry_id)
   *  for manual entries so the numeric selection set stays collision-free. */
  is_manual: boolean;
  /** The planner_manual_entries.id for a manual candidate (null for real bids). */
  manual_entry_id: number | null;
  /** Operator duration override in effect (real bid) or the manual entry's
   *  stated duration; null when the real bid derives from its summary. */
  duration_override_months: number | null;
  /** Owner behind the pursuit, used only for the bondability screen. */
  owner_name?: string | null;
  market_sector?: string | null;
  /** ADVISORY surety screen — "could the surety write this", never "would we
   *  win it". It does not affect value, timing or win odds. */
  bonding?: BondingScreen;
}
/** How the committed backlog bills (tools_service backlog_timing, punch
 *  C-42). The simulation draws from the same per-job plans. Counts and
 *  remaining value by kind: billing (3-month pace), early (under 10% billed,
 *  on the billing curve), quiet (12-month pace), not_started (Spectrum's
 *  estimated start + the delay past jobs showed), stalled (nothing billed in
 *  12 months; not projected). */
export interface BacklogSources {
  jobs: number; fully_billed: number; remaining_total: number;
  billing: number; early: number; quiet: number; not_started: number; stalled: number;
  value: { billing: number; early: number; quiet: number; not_started: number; stalled: number };
  /** Value-weighted months ('YYYY-MM') when not-yet-billing jobs are
   *  expected to bill first; null when none. A null percentile = beyond the
   *  planner's horizon. */
  not_started_first_billing: { p10: string | null; p50: string | null; p90: string | null } | null;
  timing: {
    start_delay_months: { jobs: number; p25: number | null; p50: number | null; p75: number | null; max: number | null };
    duration_overrun: { jobs: number; p50: number | null };
    duration_fit_jobs: number;
    s_curve: boolean;
    /** The timing fit raised; plans used default assumptions. */
    fit_failed?: boolean;
  };
}

export interface RevenueScenario {
  months: string[];
  baseline: number[];
  /** 'spectrum' = ERP truth (each active job's remaining-to-bill, laid out
   *  by its backlog-timing plan); 'bids' = legacy awarded-bid even-spread
   *  (mirror empty). */
  baseline_mode: 'spectrum' | 'bids';
  baseline_sources: BacklogSources | null;
  baseline_scheduled_count: number;
  baseline_unscheduled_count: number;
  baseline_unscheduled_value: number;
  /** Trailing-12-month actual portfolio billings (Spectrum mirror). */
  history: { months: string[]; billed: number[] };
  awarded_bid_count: number;
  awarded_bid_value: number;
  candidates: ScenarioCandidate[];
  excluded: { bid_id: number; name: string; reason: string }[];
  /** Planner-removed rows (excluded real bids + excluded manual entries) so
   *  the UI can offer 'restore'. bid_id is negated for manual entries. */
  removed: { bid_id: number; name: string; value: number; is_manual: boolean; manual_entry_id: number | null }[];
  assumptions: {
    award_lag_days: number; horizon_months: number;
    co_growth_factor: number | null; s_curve_jobs: number;
    spectrum_as_of: string | null;
    /** Latest win-model run (null when the model has never scored).
     *  odds_reading: 'record' = each pursuit's full owner record (executives
     *  and admins), 'display' = the odds shown everywhere else. */
    win_model: {
      scored_at: string; model_version: string; train_n: number;
      won_n: number; base_win_rate: number | null; candidates_scored: number;
      odds_reading?: 'record' | 'display';
    } | null;
    /** Org-wide operator targets (precon_strategic_config, migration 158) that
     *  frame the answer strip + capacity panel. Defaulted server-side (72M/6/4)
     *  when unset; edited via setPlannerTargets. */
    planner_targets: {
      fy_revenue_target: number; superintendent_count: number;
    };
    /** Surety capacity the candidates were screened against. Advisory: no
     *  number in this payload is changed by it. */
    bonding?: BondingCapacity;
  };
}
export async function getRevenueScenario(token: string | null): Promise<RevenueScenario | null> {
  const wrap = await cachedCall<ToolResponse<RevenueScenario>>('/tools/executive/bids/revenue-scenario', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load revenue scenario', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* Revenue-planner write path (migration 148) — add/remove/restore/edit-duration.
 * Exec-or-precon-head gated server-side; each returns void and the caller
 * reloads the scenario. */

export interface ManualEntryInput {
  name: string; estimate_value: number; duration_months: number;
  due_date?: string | null; win_probability?: number | null;
}

/** Add a manual pursuit; resolves to the new planner_manual_entries id. */
export async function addPlannerManualEntry(token: string | null, entry: ManualEntryInput): Promise<number> {
  const wrap = await call<ToolResponse<{ id: number }>>('/tools/executive/bids/planner/manual', token, entry);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to add pursuit', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0]?.id ?? 0;
}

/** Patch a manual entry. Only keys present in `patch` are written. */
export async function updatePlannerManualEntry(
  token: string | null, entryId: number, patch: Partial<ManualEntryInput> & { excluded?: boolean }
): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>(`/tools/executive/bids/planner/manual/${entryId}`, token, patch);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to update pursuit', 500, wrap.error_code ?? undefined);
}

/** Permanently delete a manual entry (POST, not HTTP DELETE — matches the
 *  codebase convention and avoids an APIM DELETE-verb/CORS dependency). */
export async function deletePlannerManualEntry(token: string | null, entryId: number): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>(
    `/tools/executive/bids/planner/manual/${entryId}/delete`, token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to delete pursuit', 500, wrap.error_code ?? undefined);
}

/** Planner-scoped remove (excluded=true) / restore (false) of a real bid. */
export async function setPlannerBidExcluded(token: string | null, bidId: number, excluded: boolean): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>(
    '/tools/executive/bids/planner/exclude', token, { bid_id: bidId, excluded });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to update planner visibility', 500, wrap.error_code ?? undefined);
}

/** Override (duration_months) or clear (null) a real bid's construction duration. */
export async function setPlannerBidDuration(token: string | null, bidId: number, durationMonths: number | null): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>(
    '/tools/executive/bids/planner/duration', token, { bid_id: bidId, duration_months: durationMonths });
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to update duration', 500, wrap.error_code ?? undefined);
}

/* Revenue-planner saved scenarios + operator targets (migration 158). Scenarios
 * are per-user (listed to the owner, readable by id across the cohort so shared
 * ?scenario=<id> links work, written only by the owner). Targets are org-wide. */

export interface PlannerScenario {
  id: number;
  name: string;
  /** Candidate id namespace (negative = manual entry), matching ScenarioCandidate.bid_id. */
  selected_bid_ids: number[];
  /** Opaque assumptions snapshot the SPA stored when saving (or null). */
  assumptions: Record<string, unknown> | null;
  updated_at: string | null;
}

export interface PlannerTargetsInput {
  fy_revenue_target?: number;
  superintendent_count?: number;
  /** Surety limits, negotiated with the bonding agent and movable. An
   *  explicit null CLEARS a key — that is how the private threshold is
   *  unset; omitting the field leaves it unchanged. */
  bonding_aggregate_capacity?: number | null;
  bonding_single_job_limit?: number | null;
  bonding_private_threshold?: number | null;
}

/** List the caller's own saved scenarios (most-recently-updated first). */
export async function listPlannerScenarios(token: string | null): Promise<PlannerScenario[]> {
  const wrap = await call<ToolResponse<PlannerScenario>>('/tools/executive/bids/planner/scenarios', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load scenarios', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/** Read a scenario by id (cohort-readable — resolves a shared link). */
export async function getPlannerScenario(token: string | null, id: number): Promise<PlannerScenario | null> {
  const wrap = await call<ToolResponse<PlannerScenario>>(
    `/tools/executive/bids/planner/scenarios/${id}/get`, token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load scenario', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Save a named scenario for the caller (upsert on same name). */
export async function createPlannerScenario(
  token: string | null,
  input: { name: string; selected_bid_ids: number[]; assumptions?: Record<string, unknown> | null }
): Promise<PlannerScenario | null> {
  const wrap = await call<ToolResponse<PlannerScenario>>(
    '/tools/executive/bids/planner/scenarios/create', token, input);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to save scenario', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Patch one of the caller's own scenarios (owner-only). Only keys present are written. */
export async function updatePlannerScenario(
  token: string | null, id: number,
  patch: Partial<{ name: string; selected_bid_ids: number[]; assumptions: Record<string, unknown> | null }>
): Promise<PlannerScenario | null> {
  const wrap = await call<ToolResponse<PlannerScenario>>(
    `/tools/executive/bids/planner/scenarios/${id}`, token, patch);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to update scenario', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Delete one of the caller's own scenarios (owner-only). */
export async function deletePlannerScenario(token: string | null, id: number): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>(
    `/tools/executive/bids/planner/scenarios/${id}/delete`, token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to delete scenario', 500, wrap.error_code ?? undefined);
}

/** Upsert the org-wide planner targets (FY revenue target / superintendent count). */
export async function setPlannerTargets(token: string | null, targets: PlannerTargetsInput): Promise<void> {
  const wrap = await call<ToolResponse<unknown>>('/tools/executive/bids/planner/targets', token, targets);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to save targets', 500, wrap.error_code ?? undefined);
}

/** Monte Carlo uncertainty bands around the revenue plan. Spectrum-mode only
 *  (needs active-job backlog); a 'mirror_unavailable' error_code means the
 *  caller should keep the deterministic line and hide the bands. */
export interface RevenueScenarioBandRequest {
  /** null = overlay all derived pursuits; [] = backlog only; list = those bids. */
  selected_bid_ids?: number[] | null;
  /** null/omitted = MODEL mode (per-candidate calibrated odds, base-rate
   *  fallback); a number = the manual slider on every candidate. */
  win_probability?: number | null;
  co_growth_on?: boolean;
  common_factor_scale?: number;
  months_ahead?: number;
}
export interface RevenueScenarioBands {
  months: string[];
  history: { months: string[]; billed: number[] };
  /** Face-pace all-win reference line (baseline + selected candidates full value). */
  deterministic: number[];
  p10: number[];
  p50: number[];
  p90: number[];
  mean: number[];
  decomposition: {
    horizon_total: { p10: number; p50: number; p90: number; width: number };
    timing_only: { p10: number; p50: number; p90: number; width: number };
    backlog_with_co: { p10: number; p50: number; p90: number; width: number };
    /** Paired per-iteration CO delta (≥ 0 by construction) — the honest
     *  "of which change-order value" figure. */
    co_contribution: { p50: number; p90: number; mean: number };
    median_monthly_band_width: number;
    /** Surety capacity cap (BONDING_CAP_SIMULATION_ENABLED, default OFF).
     *  When disabled the bands are identical to the pre-flag engine. When
     *  enabled, a simulated win the surety could not bond at its award month
     *  contributes nothing; `uncapped_horizon_total` is the band the same
     *  draws would have produced without the cap, so the cost of the
     *  constraint is readable rather than inferred. */
    bonding_cap?: {
      enabled: boolean;
      aggregate_capacity?: number | null;
      single_job_limit?: number | null;
      /** Share of iterations in which at least one win was refused. */
      binding_iteration_share?: number;
      shed_value_mean?: number;
      shed_value_p50?: number;
      shed_value_p90?: number;
      uncapped_horizon_total?: { p10: number; p50: number; p90: number; width: number };
    };
  };
  assumptions: {
    n_iter: number; seed: number; win_probability: number;
    /** 'model' = per-candidate calibrated odds (win_probability is only the
     *  unscored-candidate fallback); 'manual' = the slider everywhere. */
    win_probability_mode: 'model' | 'manual';
    model_scored_candidates: number;
    win_model: {
      scored_at: string; model_version: string; train_n: number;
      won_n: number; base_win_rate: number | null; candidates_scored: number;
      odds_reading?: 'record' | 'display';
    } | null;
    co_growth_on: boolean; common_factor_scale: number; median_sigma: number;
    co_ratio_sample: number; active_jobs_simulated: number;
    /** How the simulated jobs bill, by backlog-timing kind. */
    backlog_kinds?: Record<string, number>;
    candidates_selected: number; horizon_months: number;
    spectrum_as_of: string | null;
    bonding?: BondingCapacity;
    /** True only when the flag is on AND the limits are actually configured. */
    bonding_cap_on?: boolean;
  };
}
export async function getRevenueScenarioBands(
  token: string | null,
  req: RevenueScenarioBandRequest
): Promise<RevenueScenarioBands | null> {
  const wrap = await cachedCall<ToolResponse<RevenueScenarioBands>>(
    '/tools/executive/bids/revenue-scenario/simulate', token, req);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to simulate revenue bands', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Estimate → final cost accuracy from Spectrum closed jobs: original at-bid
 *  phase budget vs actual final cost. Dual-mounted (Portfolio → Estimating +
 *  PreconHub → Management); executive-or-precon-head gate. */
export interface CostAccuracyRow {
  job_number: string; description: string | null; customer_name: string | null;
  estimator: string | null; original_est_cost: number; final_cost: number;
  overrun_pct: number | null; complete_year: number | null;
}
export interface EstimateCostAccuracy {
  as_of: string | null;
  rows: CostAccuracyRow[];
  summary: {
    job_count: number; original_total: number; final_total: number;
    weighted_overrun_pct: number | null; median_overrun_pct: number | null;
    buckets: { key: string; label: string; count: number }[];
  };
  by_estimator: {
    estimator: string; job_count: number; original_total: number;
    final_total: number; weighted_overrun_pct: number | null;
  }[];
  /** Filters with no route to a closed job (stage). */
  ignored_filters?: string[];
  /** Set when a filter narrowed the jobs: a filter reaches a closed job only
   *  through its Buildr win, so only the linked jobs can match. */
  filter_coverage?: { closed_jobs: number; linked_to_buildr_win: number } | null;
}
export async function getEstimateCostAccuracy(token: string | null, filters?: BidDashboardFilters): Promise<EstimateCostAccuracy | null> {
  const wrap = await cachedCall<ToolResponse<EstimateCostAccuracy>>('/tools/executive/bids/estimate-cost-accuracy', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load cost accuracy', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* ---------------- Bid win-probability (migration 111) ---------------- */

export interface BidWinDriver { factor: string; detail?: string }
export interface BidWinResult {
  low_bidder?: string; low_amount?: number; placement?: number;
  bidders?: number; not_low?: boolean;
}
/** Advisory bondability screen, stamped on every scored pursuit.
 *  Purely additive: no existing field changed, no row removed, ordering
 *  untouched. `hard_refusal` is the ONE field to branch on — true means the
 *  contract exceeds the surety's SINGLE-JOB limit, an absolute refusal. The
 *  aggregate breach is a fact about the PROGRAM, true of most bonded pursuits
 *  while it holds, so it belongs in one banner and never as a per-row badge. */
export interface BidWinBondingScreen {
  status: 'within_limits' | 'over_single_limit' | 'exceeds_remaining_capacity'
        | 'program_over_capacity' | 'not_bonded' | 'unknown';
  hard_refusal: boolean;
  bonded: boolean;
  owner_class: 'federal' | 'municipal' | 'private' | 'unknown';
  reasons?: string[];
  /** Set when an operator recorded that the surety will write this one. */
  override?: { reason: string | null; overridden_at: string | null };
  overridden_status?: string;
}

/** Program-level capacity, reported once per payload. */
export interface BidWinBondingBlock {
  configured: boolean;
  program_known?: boolean;
  aggregate_capacity: number | null;
  single_job_limit: number | null;
  bonded_program: number;
  remaining_capacity: number | null;
  utilization: number | null;
  over_capacity: boolean;
  hard_refusal_count?: number;
  hard_refusal_value?: number;
  overrides_applied?: number;
  screen_counts?: Record<string, number>;
}

export interface BidWinScore {
  id: number;
  buildr_project_id: string;
  name: string | null;
  owner: string | null;
  amount: number | null;
  market_sector: string | null;
  tender_type: string | null;
  contract_type: string | null;
  delivery_method: string | null;
  bid_due_date: string | null;
  cost_range: string | null;
  bd_probability: number | null;
  win_p: number;
  win_p_lo: number;
  win_p_hi: number;
  expected_value: number | null;
  score_rank: number;
  drivers: { top_factors?: BidWinDriver[] } | null;
  bid_result: BidWinResult | null;
  bonding?: BidWinBondingScreen;
  model_version: string;
  scored_at: string | null;
  // Migration 120 — pursuit display fields off the nightly mirror.
  assigned_to_name: string | null;
  project_stage: string | null;
  project_stage_position: number | null;
  stage_age_days: number | null;
  state: string | null;
  work_scope: string | null;
  division: string | null;
  expected_award_date: string | null;
  rfi_due_date: string | null;
  site_visit: string | null;
  bid_bond: string | null;       // 'Yes' | 'No' | null (Buildr custom field)
}
export interface BidWinList {
  run: {
    scored_at: string | null; model_version: string; train_n: number;
    won_n: number; base_win_rate: number | null; cv_brier: number | null;
    cv_brier_base: number | null; cv_auc: number | null; scored_n: number;
    skipped_reason: string | null;
    /** bidwin-owner-v5's time-ordered replay: pursuits predicted, the odds
     *  shown averaged over them, and the share actually won (the shown odds
     *  never count a loss, so cv_mean_p sits above cv_actual_rate). */
    cv_n?: number | null; cv_mean_p?: number | null; cv_actual_rate?: number | null;
  } | null;
  pipeline: {
    open_pursuits: number;
    nominal_value: number;
    /** Follows the viewer (bidwin-owner-v5): for executives and admins
     *  (`weighting` 'record') Σ amount × each pursuit's FULL owner record,
     *  losses counted — not amount × the win_p shown on each row; for
     *  everyone else ('shown') Σ amount × win_p. Every bucket and split under
     *  it weighs the same way; `weighting_basis` says which in words. */
    probability_weighted_value: number;
    /** Added BESIDE the two totals above, never replacing them. They exclude
     *  ONLY pursuits over the single-job limit — they are NOT net of the
     *  aggregate program line, so while `bonding.over_capacity` holds they are
     *  a ceiling, not availability. */
    bondable_open_pursuits?: number;
    bondable_nominal_value?: number;
    bondable_probability_weighted_value?: number;
    unbondable_open_pursuits?: number;
    unbondable_nominal_value?: number;
    unbondable_probability_weighted_value?: number;
    /** Mean of the odds shown across the open set — read against
     *  run.base_win_rate. The shown odds never count a loss, so they sit
     *  above it by design; mean_record_win_p is the full record's mean, the
     *  one held to the overall rate (bidwin-logit-v3.1 sat at 2.5x for two
     *  months with nothing on the page saying so). */
    mean_win_p: number | null;
    /** Executives and admins only. */
    mean_record_win_p?: number | null;
    weighting?: 'record' | 'shown';
    weighting_basis?: string;
  };
  bonding?: BidWinBondingBlock;
  returned: number;
  truncated: boolean;
  scores: BidWinScore[];
}

/** Win-probability worklist. Uncached — sweep-time data, cheap read.
 *  `filters` (the estimating filter bar) narrows the pursuits and every total
 *  with them; `marketSector` and `limit` narrow the page only. */
export async function getBidWinScores(
  token: string | null,
  opts: { marketSector?: string | null; limit?: number; filters?: BidDashboardFilters } = {},
): Promise<BidWinList | null> {
  const body: Record<string, unknown> = { ...(opts.filters ?? {}) };
  if (opts.marketSector) body.market_sector = opts.marketSector;
  if (opts.limit) body.limit = opts.limit;
  const wrap = await call<ToolResponse<BidWinList>>('/tools/bids/win-probability/list', token, body);
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load win probability', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** One open pursuit in the full owner record (bidwin-owner-v5) — executives
 *  and admins only: the record counts losses the odds shown never do. */
export interface BidWinRecordRow {
  buildr_project_id: string;
  name: string | null;
  owner: string | null;
  amount: number | null;
  score_rank: number;
  win_p: number; win_p_lo: number; win_p_hi: number;
  record_win_p: number | null; record_win_p_lo: number | null; record_win_p_hi: number | null;
  record: { owner_won: number; owner_decided: number; overall_won: number;
            overall_decided: number; detail: string } | null;
  /** The record sits below the overall rate — the pursuits the shown odds lift. */
  below_overall_rate: boolean;
}
export interface BidWinRecord {
  run: (NonNullable<BidWinList['run']> & {
    record_cv_brier?: number | null; record_cv_auc?: number | null;
    record_cv_mean_p?: number | null;
  }) | null;
  pipeline: { open_pursuits: number; probability_weighted_value: number; below_overall_rate: number };
  basis: string;
  pursuits: BidWinRecordRow[];
}

/** The full owner record beside the odds shown — executive/admin gate
 *  server-side (403 for anyone else). Uncached, fetched on demand, narrowed
 *  by the same filter bar as getBidWinScores so the two match. */
export async function getBidWinRecord(token: string | null, filters?: BidDashboardFilters): Promise<BidWinRecord | null> {
  const wrap = await call<ToolResponse<BidWinRecord>>('/tools/bids/win-probability/record', token, filters ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load the full record', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Backlog projection: awarded value spread across construction windows. */
export interface BacklogMonth { month: string; projected_value: number; bid_count: number }
export interface BacklogProjection {
  months: BacklogMonth[]; horizon_months: number;
  scheduled_bid_count: number; unscheduled_bid_count: number; unscheduled_value: number;
}
export async function getBacklogProjection(token: string | null): Promise<BacklogProjection | null> {
  const wrap = await cachedCall<ToolResponse<BacklogProjection>>('/tools/executive/bids/backlog-projection', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load backlog projection', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/** Cost-vs-budget per active project: latest budget vs change-order exposure.
 *  budget fields are null until budget_audit_log is populated; cost_exposure is
 *  0 until change_events sync. */
export interface CostVsBudgetRow {
  id: number; name: string; project_number: string | null; status: string;
  latest_budget: number | null; budget_updated_at: string | null;
  cost_exposure: number | null; change_event_count: number | null; exposure_pct: number | null;
}
export async function getPortfolioCostVsBudget(token: string | null): Promise<CostVsBudgetRow[]> {
  const wrap = await cachedCall<ToolResponse<CostVsBudgetRow>>('/tools/executive/portfolio/cost-vs-budget', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load cost vs budget', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/** Total change-order schedule-day impact per active project. */
export interface ScheduleImpactRow { name: string; total_schedule_days: number | null }
export async function getPortfolioScheduleImpact(token: string | null): Promise<ScheduleImpactRow[]> {
  const wrap = await cachedCall<ToolResponse<ScheduleImpactRow>>('/tools/executive/portfolio/schedule-impact', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load schedule impact', 500, wrap.error_code ?? undefined);
  return wrap.data ?? [];
}

/* ---------------- Tools Service — executive financials (Spectrum WIP mirror) ----------------
 * All read the migration-100 mirror tables refreshed nightly from Viewpoint
 * Spectrum; `as_of` stamps the mirror's last sync. Executive-gated. */

export interface FinWipRow {
  job_number: string; description: string | null; customer_name: string | null;
  /** Spectrum's raw payroll code, kept for existing consumers. Show `pm_name`. */
  project_manager: string | null;
  pm_code: string | null;
  /** The PM of the job, named from Spectrum (db.queries.spectrum_pm). */
  pm_name: string | null;
  project_id: number | null; project_name: string | null;
  original_contract: number; revised_contract: number; change_orders: number;
  pending_cr_amount: number; pending_cr_count: number;
  jtd_cost: number; billed: number;
  /** 'period' — cost and billings above are stated at the close period.
   *  'live'   — this job has no reconstructable period history, so they are
   *             tonight's snapshot. Mixed within one schedule is normal and
   *             the totals disclose the split. */
  actuals_basis?: 'period' | 'live';
  /** Open POs/subcontracts as they stand TODAY — a commitment register has
   *  no period history, so this column never restates. */
  committed_cost: number;
  phase_est_cost: number | null; phase_original_est_cost: number | null;
  pct_billed: number | null; pct_budget_consumed: number | null; buyout_pct: number | null;
  /** Projected cost at completion — the project card's forecast, cost to date
   *  + cost to complete per cost code (ADR-003 D16); null when the job carries
   *  no budget, or when its forecast could not be computed ('unavailable'). */
  projected_cost: number | null;
  projection_basis: ProjectionBasis | null;
  no_projection_reason?: NoProjectionReason | null;
  forecast_flags?: ProjectionFlag[];
  /** Spectrum's current estimate, job-wide, sits below the cost already booked. */
  estimate_stale: boolean;
  /** revised − projected_cost. */
  projected_margin: number | null; projected_margin_pct: number | null;
  /** Billing basis: revised contract less BILLED. */
  backlog: number; est_complete_date: string | null;
  /** Earned basis: contract less EARNED revenue, from the controller's
   *  monthly close. null = job not on the close schedule. The gap between
   *  this and `backlog` is the job's over/under-billing. */
  earned_backlog: number | null;
  /** 'June 2026' — the close `earned_backlog` is as of. */
  earned_close_label: string | null;
}
/** Basis of a projected cost (ADR-003 D16): the card's forecast, the same with a
 *  finished budget review steering some codes, or 'unavailable' when the
 *  forecast raised for that job (logged server-side; no figure). A field
 *  typed `ProjectionBasis | null` is null when the job has no budget — no
 *  projection at all (`no_projection_reason` says why). */
export type ProjectionBasis = 'forecast' | 'budget_review' | 'unavailable';

export interface FinWip extends FinVintage {
  as_of: string | null;
  method_note?: string;
  rows: FinWipRow[];
  totals: {
    original_contract: number; revised_contract: number; change_orders: number;
    pending_cr_amount: number; jtd_cost: number; billed: number; committed_cost: number;
    phase_est_cost: number; projected_cost: number; backlog: number; projected_margin: number;
    /** Σ margin ÷ Σ revised over the jobs that carry a projection. */
    projected_margin_pct: number | null;
    pct_billed: number | null; job_count: number;
    projected_jobs: number; estimate_stale_jobs: number;
    /** Jobs whose forecast could not be computed — out of the projected total. */
    projection_unavailable_jobs?: string[];
    forecast_flags?: ProjectionFlagTotals;
    /** Jobs where a finished budget review steers the forecast. */
    budget_review_jobs?: number;
    /** Earned basis, floored per job. Jobs with no close figure fall back to
     *  their billing-basis backlog so the total stays complete;
     *  earned_backlog_jobs says how many are truly on the earned basis. */
    earned_backlog: number; earned_backlog_jobs: number;
    earned_close_label: string | null;
    /** How much of the schedule actually restated at the close. */
    rows_total?: number; rows_period_basis?: number; rows_live_basis?: number;
  };
}
/** No `period` = the running schedule (tonight's figures). Pass one to
 *  state the schedule at that fiscal period instead. */
export async function getFinancialsWip(
  token: string | null, period?: FinPeriodSel | null,
): Promise<FinWip | null> {
  const wrap = await cachedCall<ToolResponse<FinWip>>('/tools/executive/financials/wip', token, period ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load WIP', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface FinCashRow {
  job_number: string; description: string | null; customer_name: string | null;
  project_id: number | null; billed: number; cash_received: number; open_ar: number;
  cash_paid: number; net_cash_flow: number; billed_30_days: number; ap_cost_30_days: number;
}
export interface FinCash {
  as_of: string | null;
  rows: FinCashRow[];
  totals: { billed: number; cash_received: number; open_ar: number; cash_paid: number; net_cash_flow: number };
}
export async function getFinancialsCash(token: string | null): Promise<FinCash | null> {
  const wrap = await cachedCall<ToolResponse<FinCash>>('/tools/executive/financials/cash', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load cash', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface FinCoRow {
  job_number: string; description: string | null; customer_name: string | null;
  project_id: number | null; original_contract: number; revised_contract: number;
  change_orders: number; co_pct_of_original: number | null;
  pending_cr_amount: number; pending_cr_count: number;
}
export interface FinChangeOrders {
  as_of: string | null;
  rows: FinCoRow[];
  totals: { change_orders: number; pending_cr_amount: number; pending_cr_count: number; co_growth_factor: number | null };
}
export async function getFinancialsChangeOrders(token: string | null): Promise<FinChangeOrders | null> {
  const wrap = await cachedCall<ToolResponse<FinChangeOrders>>('/tools/executive/financials/change-orders', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load change orders', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface FinBillingTrend {
  as_of: string | null;
  months: { month: string; billed: number }[];
  /** Σ max(0, revised − billed) over active jobs (ADR-003 D3). */
  /** Earned basis: work left to PERFORM, not left to bill. */
  remaining_backlog: number;
  backlog_basis: 'earned';
  earned_basis_jobs: number;
  backlog_job_count: number;
  /** Mean monthly billing over the last 3 full calendar months, zero months included (D4). */
  trailing_3mo_avg: number;
  /** e.g. "Jun–Aug 2026" — the months the pace averaged over. */
  pace_window?: string;
  runway_months: number | null;
}
export async function getFinancialsBillingTrend(token: string | null): Promise<FinBillingTrend | null> {
  const wrap = await cachedCall<ToolResponse<FinBillingTrend>>('/tools/executive/financials/billing-trend', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load billing trend', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface FinRedFlagRow {
  job_number: string; description: string | null; customer_name: string | null;
  project_id: number | null; flags: { key: string; label: string }[];
  revised_contract: number; jtd_cost: number; billed: number; committed_cost: number;
  phase_est_cost: number | null; pending_cr_amount: number;
  days_since_last_billing: number | null;
}
export interface FinRedFlags {
  as_of: string | null;
  rows: FinRedFlagRow[];
  flag_labels: Record<string, string>;
}
export async function getFinancialsRedFlags(token: string | null): Promise<FinRedFlags | null> {
  const wrap = await cachedCall<ToolResponse<FinRedFlags>>('/tools/executive/financials/red-flags', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load red flags', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface FinConcentration extends FinVintage {
  as_of: string | null;
  /** Sorted by backlog desc — the surety test is on remaining work. */
  rows: {
    customer_code: string; customer_name: string; job_count: number; revised_total: number; backlog: number;
    pct_of_portfolio: number | null; pct_of_backlog: number | null;
    /** Jobs in this customer's backlog taken from the controller's close
     *  workbook (only counted when its close month IS the anchor). */
    earned_basis_jobs?: number;
  }[];
  portfolio_total: number;
  backlog_total: number;
  rows_total?: number; rows_period_basis?: number; rows_live_basis?: number;
  method_note?: string;
}
/** No `period` = the running view. */
export async function getFinancialsConcentration(
  token: string | null, period?: FinPeriodSel | null,
): Promise<FinConcentration | null> {
  const wrap = await cachedCall<ToolResponse<FinConcentration>>('/tools/executive/financials/concentration', token, period ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load concentration', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface FinReconciliation {
  as_of: string | null;
  spectrum_active_jobs: number;
  spectrum_revised_total: number;
  awarded_bids: number;
  awarded_value_total: number;
  unmapped_jobs: { job_number: string; description: string | null; revised_contract: number | null }[];
  unmapped_projects: { id: number; name: string; project_number: string | null }[];
}
export async function getFinancialsReconciliation(token: string | null): Promise<FinReconciliation | null> {
  const wrap = await cachedCall<ToolResponse<FinReconciliation>>('/tools/executive/financials/reconciliation', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load reconciliation', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface SubComplianceRow {
  vendor_code: string; vendor_name: string | null; job_number: string | null;
  project_id: number | null; subcontract_number: string | null;
  tracking_item_code: string; gap_kind: 'expired' | 'missing';
  expire_date: string | null; age_days: number | null;
  /** expired > 1 year — almost always renewed-but-never-entered bookkeeping. */
  stale: boolean;
  comments: string | null; first_seen: string | null;
  /** Lower-tier vendor the document belongs to, when it isn't the sub's own
   *  (migration 200). "Sub A's insurance" and "Sub A's subtier's insurance"
   *  are different problems. */
  subtier_vendor_code: string | null;
  /** Spectrum's own Is_Open value, carried raw for corroboration. */
  spectrum_open_flag: string | null;
  /** Spectrum's flag disagrees with our derived 'missing' gap — worth a human
   *  look. Advisory ONLY: the gap still stands. See summary.open_flag_trusted. */
  needs_review: boolean;
}
export interface FinSubCompliance {
  as_of: string | null;
  rows: SubComplianceRow[];
  expired_insurance_vendors: { vendor_code: string; vendor_name: string | null; insurance_exp_date: string | null }[];
  summary: {
    expired: number; missing: number; stale: number; insurance_expired: number;
    /** 'missing' gaps Spectrum's own flag disagrees with. */
    needs_review: number;
    /** False until Is_Open's polarity is confirmed against real Spectrum
     *  values — until then `needs_review` is a prompt to look, never a
     *  reason to dismiss a gap. */
    open_flag_trusted: boolean;
  };
}
export interface ArAgingInvoice {
  job_number: string; job_description: string | null; customer_name: string | null;
  invoice_number: string; invoice_date: string | null; amount: number;
  open_amount: number; age_days: number | null; remarks: string | null;
}
export interface FinArAging {
  as_of: string | null;
  buckets: { key: string; label: string; total: number }[];
  undated_open: number;
  total_open_ar: number;
  /** Open AR ÷ average daily billing (3 full calendar months × 12 ÷ 365.25, D5). */
  dso_days: number | null;
  /** The months the daily billing pace averaged over. */
  dso_window?: string;
  /** Signed net retention on the ledger (credits reverse withholding),
   *  stale jobs included. What is still owed is ``retention_held``. */
  retention_net_held: number;
  /** Retention still held, carved out of open AR and NOT aged: buckets +
   *  undated_open + retention_held = total_open_ar. Absent on a payload
   *  from before 2026-10-10. */
  retention_held?: number;
  /** The part of retention_held owed on jobs outside the active list; it is
   *  in total_open_ar but not in the cash panel's open AR. */
  retention_held_outside_active?: number;
  retention_by_job?: ArAgingRetentionJob[];
  by_customer: { customer_name: string; open_amount: number }[];
  invoices: ArAgingInvoice[];
  method_note: string;
}
export interface ArAgingRetentionJob {
  job_number: string; job_description: string | null; customer_name: string | null;
  status: string | null; retention_held: number; in_active_ar: boolean;
}
export async function getFinancialsArAging(token: string | null): Promise<FinArAging | null> {
  const wrap = await cachedCall<ToolResponse<FinArAging>>('/tools/executive/financials/ar-aging', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load AR aging', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export interface RetentionJob {
  job_number: string;
  description: string | null;
  customer_name: string | null;
  status: string | null;              // 'A' active / 'C' closed / 'I' inactive
  net_retention: number;              // signed net still held
  open_ar: number;
  billed: number;
  outstanding: number;                // collectible = min(net_retention, open_ar)
  effective_rate: number | null;      // withheld / billed
  est_complete_date: string | null;
  timing_basis: 'est_complete' | 'past_due' | 'unknown';
  last_release_date: string | null;
  last_release_amount: number | null;
  flags: string[];                    // 'ledger_stale' | 'complete_past_due'
}
export interface FinRetention {
  as_of: string | null;
  jobs: RetentionJob[];
  releases: { job_number: string; invoice_number: string; invoice_date: string | null; amount: number }[];
  totals: {
    signed_net_total: number;
    naive_total: number;
    active_outstanding_total: number;
    /** Active jobs: net retention above their open AR (not collectible as AR). */
    active_uncollectible_total?: number;
    stale_closed_total: number;
    /** Closed jobs still carrying net retention that never tripped ledger_stale. */
    closed_untracked_total?: number;
    job_count: number;
  };
  method_note: string;
}
export async function getFinancialsRetention(token: string | null): Promise<FinRetention | null> {
  const wrap = await cachedCall<ToolResponse<FinRetention>>('/tools/executive/financials/retention', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load retention', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* Customer payment behavior — observed settlement lags (migration-105
 * capture) blended with an aging-floor model; per-customer basis labels. */
export interface PaymentBehaviorObserved {
  events: number;
  dollars: number;
  lag_p25: number | null;
  lag_p50: number | null;
  lag_p75: number | null;
  lag_mean: number | null;
  last_receipt_date: string | null;
  /** Recent-half p50 minus older-half p50 (days); positive = slowing.
   *  Null until enough observed history accrues. */
  trend_delta_days: number | null;
}
export interface PaymentBehaviorCustomer {
  customer_code: string;
  customer_name: string | null;
  job_count: number;
  /** 'retention_only' (2026-10-10): all of the customer's open AR is
   *  retention held and none of its receipts are captured, so it has no
   *  lag_p50 and no band: nothing of it is aged or observed. */
  basis: 'observed' | 'mixed' | 'modeled' | 'retention_only';
  band: 'A' | 'B' | 'C' | 'D' | null;
  lag_p50: number | null;             // the blended estimator, days
  observed: PaymentBehaviorObserved | null;
  modeled: { open_age_p50: number | null; dso_implied: number | null; lag_p50: number | null };
  /** Open AR, retention held included (it is owed). */
  open_exposure: number;
  /** Retention held in that open AR (2026-10-10). Never aged: it is not in
   *  the open invoices, the oldest age or the modeled lag. Absent on an
   *  older payload. */
  retention_held?: number;
  open_invoice_count: number;
  oldest_open_age_days: number | null;
}
export interface FinPaymentBehavior {
  as_of: string | null;
  /** The months the DSO billing pace averaged over. */
  dso_window?: string;
  capture: {
    live_since: string | null;
    days_observed: number;
    attributed_events: number;
    attributed_dollars: number;
    unattributed_dollars: number;
    credit_events: number;
    credit_dollars: number;
    reversal_count: number;
    jobs_tracked: number;
  };
  dso_days: number | null;
  /** The aging floor's DSO: open AR less retention held ÷ the same daily
   *  billing. dso_days above is AR aging's, retention included. Absent on
   *  an older payload. */
  dso_ex_retention_days?: number | null;
  bands: Record<string, number>;
  band_edges: { band: string; max_days: number | null; label: string }[];
  /** Σ customers' retention held (absent on an older payload). */
  retention_held?: number;
  customers: PaymentBehaviorCustomer[];
  method_note: string;
}
export async function getFinancialsPaymentBehavior(token: string | null): Promise<FinPaymentBehavior | null> {
  const wrap = await cachedCall<ToolResponse<FinPaymentBehavior>>('/tools/executive/financials/payment-behavior', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load payment behavior', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export async function getFinancialsSubCompliance(token: string | null): Promise<FinSubCompliance | null> {
  const wrap = await cachedCall<ToolResponse<FinSubCompliance>>('/tools/executive/financials/sub-compliance', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load sub compliance', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* ---------------- Company GL — Chart of Accounts / Trial Balance (exec) ----------------
 * Company-wide general ledger from Spectrum's Z_SYN_BUDGET_HIGHLIGHTS view,
 * mirrored nightly. Natural trial-balance signs: asset/expense balances are
 * debit-positive; liability/equity/revenue are credit-negative. Executive-only. */

export interface CoaAccount {
  gl_account: string;
  account_description: string | null;
  /** Top-level GL class by leading digit: asset|liability|equity|revenue|expense. */
  class: string;
  current_period_balance: number | null;
  current_ytd_balance: number | null;
  prior_ytd_balance: number | null;
  current_ytd_budget: number | null;
  /** current YTD − prior-year YTD (natural signs). */
  yoy_variance: number | null;
}

export interface CoaClass {
  class: string;
  accounts: CoaAccount[];
  subtotal_ytd: number | null;
  /** Same-period prior-year subtotal (comparison mode). */
  subtotal_prior_ytd?: number | null;
}

/** ``linked=false`` when the GL mirror hasn't synced yet (pre-first-sync). */
export interface ChartOfAccounts {
  linked: boolean;
  fiscal_year?: string;
  fiscal_period?: string;
  period_is_current?: boolean;
  period_label?: string;
  as_of: string | null;
  classes?: CoaClass[];
  /** No grand total: the source is hybrid-signed (balance sheet natural,
   *  P&L presentation-positive) so Σ classes is meaningless. balance_check =
   *  assets + liabilities + equity − net income; 0.00 when the mirror balances. */
  totals?: { current_ytd_budget: number | null; net_income_ytd: number | null; balance_check: number | null };
}

/* ---------------- Fiscal-period filter (controller ask, 2026-07-21) -------
 * The GL mirror stores one row per account per fiscal period with
 * YTD-CUMULATIVE balances, so "May 2026" IS January-through-May — the period
 * anchor below selects, never re-aggregates. Sent as the request body of the
 * GL-backed reads; omitted/null = the backend's latest-posted-period default.
 * cachedCall keys on the body, so each period caches independently. */

export interface FinPeriodSel {
  fiscal_year: string;    // '2026'
  fiscal_period: string;  // zero-padded '01'..'12' ('13' = year-end adj, unused in practice)
}

/** A fiscal period, described. Backend `fiscal_close.closed_block`. */
export interface FinClosedPeriod extends FinPeriodSel {
  period_key: string;   // '2026-03'
  label: string;        // 'March 2026'
  period_end: string | null;
}

/** Vintage disclosure carried by every financials payload.
 *
 *  `period` — cost/billings stated AT `period`.
 *  `live`   — the nightly snapshot; `live_reason` says why it could not be
 *             stated at a close (some surfaces genuinely cannot be).
 *
 *  `books_closed_through` is present either way: a live number sitting
 *  beside a period number reads as agreeing unless the screen says
 *  otherwise. */
export interface FinVintage {
  basis?: 'period' | 'live';
  period?: FinClosedPeriod | null;
  books_closed_through?: FinClosedPeriod | null;
  period_is_closed?: boolean;
  /** Contract value never restates - CO approval dates are unreliable in
   *  the mirror - so a period read pairs restated cost/billings with the
   *  CURRENT contract. Surfaces must say so. */
  contract_basis?: 'current' | 'live';
  live_reason?: string;
  live_reason_key?: string;
}

export interface FinPeriodOption extends FinPeriodSel {
  /** False for Spectrum's pre-created future periods (they replicate the
   *  last-posted YTD) and empty stub years — not real historical picks. */
  has_activity: boolean;
  /** The books are CLOSED through this period. Posting activity is NOT the
   *  same thing: on 2026-09-15 the GL had postings through September while
   *  the books were closed through March. */
  is_closed: boolean;
}

export interface FinPeriods {
  /** Newest first. */
  periods: FinPeriodOption[];
  /** The default anchor — the newest period with posted activity, capped
   *  at the last ended month. A RUNNING surface: the close does not cap it
   *  (see books_closed_through). */
  latest_active: FinPeriodSel | null;
  books_closed_through: FinClosedPeriod | null;
  /** The account whose posting closes a month: 4499 Over/Under Billings,
   *  the controller's last entry of the close. */
  close_signal_account: string;
  close_signal_note: string;
  /** Corroboration, never the close: how far the month-end close package
   *  (depreciation, GL 7100/1900) has run. Where it is AHEAD, those months
   *  have had their month-end booked but not their over/under true-up, so
   *  their revenue recognition is not yet adjusted. The anchor advances on
   *  its own as each true-up is posted. */
  close_package_through: FinClosedPeriod | null;
  close_package_accounts: string[];
  close_package_ahead_months: number;
  as_of: string | null;
}

export async function getFinancialsPeriods(token: string | null): Promise<FinPeriods | null> {
  const wrap = await cachedCall<ToolResponse<FinPeriods>>('/tools/executive/financials/periods', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load fiscal periods', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

export async function getChartOfAccounts(token: string | null, period?: FinPeriodSel | null): Promise<ChartOfAccounts | null> {
  const wrap = await cachedCall<ToolResponse<ChartOfAccounts>>('/tools/executive/financials/chart-of-accounts', token, period ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load chart of accounts', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* ---------------- Surety / bonding ratios (exec, Wave 3b) ----------------
 * Balance-sheet ratios a bonding agent underwrites, from the GL trial balance
 * + WIP backlog. The account-number → subclass classification is surfaced in
 * `ranges` and `components` so it can be audited/adjusted, not buried in code. */

export interface SuretyAccount {
  gl_account: string;
  account_description: string | null;
  balance: number | null;
}
export interface SuretyComponent {
  key: string;
  label: string;
  accounts: SuretyAccount[];
  subtotal: number | null;
}
export interface SuretyRange {
  subclass: string;
  low: string;
  high: string;
}
export interface SuretyRatios {
  linked: boolean;
  fiscal_year?: string;
  fiscal_period?: string;
  period_is_current?: boolean;
  period_label?: string;
  as_of: string | null;
  ranges?: SuretyRange[];
  components?: SuretyComponent[];
  metrics?: {
    current_assets: number | null;
    fixed_assets: number | null;
    total_assets: number | null;
    current_liabilities: number | null;
    longterm_liabilities: number | null;
    total_liabilities: number | null;
    equity_contributed: number | null;
    net_income_ytd: number | null;
    net_worth: number | null;
    backlog: number | null;
  };
  ratios?: {
    working_capital: number | null;
    current_ratio: number | null;
    debt_to_equity: number | null;
    backlog_to_equity: number | null;
  };
  /** Same-period-last-year balance sheet (comparison mode). backlog /
   *  backlog_to_equity are null there — backlog is a current snapshot. */
  metrics_prior?: SuretyRatios['metrics'];
  ratios_prior?: SuretyRatios['ratios'];
  /** The surety's own limits against the bonded program. */
  bonding?: BondingCapacity;
}

/* ---------------- Surety bonding capacity (2026-09-18) -------------------
 * The limits the surety actually writes to — an aggregate program cap and a
 * single-job cap, both operator-set because they are negotiated with the
 * agent and move.
 *
 * TWO THINGS THIS IS NOT, both easy to get wrong when reading the payload:
 *  1. The basis is TOTAL CONTRACT VALUE, not backlog. A job occupies its full
 *     contract until it finishes, then releases it. The backlog above (which
 *     backlog_to_equity divides into) is remaining work and is a much smaller
 *     number — do not present them as the same measure.
 *  2. It is a CURRENT snapshot of the active job mirror, not restated to the
 *     fiscal period selected for the balance sheet. */

export type BondOwnerClass = 'federal' | 'municipal' | 'private' | 'unknown';

export interface BondingNotableJob {
  job_number: string | null;
  owner_name: string | null;
  description: string | null;
  contract_value: number;
  owner_class: BondOwnerClass;
  owner_class_source: string;
  bonded: boolean;
  bond_reason: string;
  over_single_limit: boolean;
}

export interface BondingCapacity {
  /** False when no aggregate limit is set — render "not configured", never
   *  "unlimited" and never "0". */
  configured: boolean;
  /** False when the Spectrum job mirror is empty (pre-first-sync). */
  linked?: boolean;
  /** False when the bonded program could NOT be established (job read failed,
   *  or the mirror has not synced). NOT the same as a program of zero: the
   *  derived fields below are then null rather than computed from a zero
   *  nobody measured. A surface MUST check this before stating headroom —
   *  "$150M remaining, 0% used" on a failed read is the exact false claim this
   *  feature exists to prevent. */
  program_known?: boolean;
  /** True only when the job read actually threw (vs. an empty mirror). Both
   *  suppress the derived fields; only this one is a fault to chase. */
  program_read_failed?: boolean;
  aggregate_capacity: number | null;
  single_job_limit: number | null;
  /** Private work is bonded only at or above this. Null = unset, in which
   *  case excluded_private_value below is NOT in the program. */
  private_threshold: number | null;
  basis: string;
  bonded_program: number;
  bonded_jobs: number;
  /** Negative when the program is over the line. Deliberately NOT floored —
   *  "$5.2M over" and "nothing left" are different facts. */
  remaining_capacity: number | null;
  utilization: number | null;
  over_capacity: boolean;
  largest_bonded_contract: number;
  largest_bonded_job: string | null;
  jobs_over_single_limit: { job_number: string | null; owner_name: string | null; contract_value: number }[];
  /** Active private work NOT counted in the program. Show it: an under-counted
   *  program reads as headroom the company does not have. */
  excluded_private_value: number;
  excluded_private_jobs: number;
  unclassified_value: number;
  unclassified_jobs: number;
  /** Per class, total value AND how much of it is in the program. Render the
   *  split; do NOT re-derive "bonded" from owner_class — private work above
   *  the threshold is bonded too. */
  by_owner_class: {
    owner_class: BondOwnerClass; jobs: number; contract_value: number;
    bonded_jobs: number; bonded_value: number;
  }[];
  notable_jobs?: BondingNotableJob[];
  /** Planner only: each pursuit is screened against the SAME headroom, so
   *  these are independent answers, not a cumulative queue. */
  screen_basis?: string;
  screen_counts?: Record<string, number>;
}

/** Advisory bondability screen stamped on each planner candidate. */
export interface BondingScreen {
  status: 'within_limits' | 'over_single_limit' | 'exceeds_remaining_capacity'
        | 'program_over_capacity' | 'not_bonded' | 'unknown';
  reasons: string[];
  bonded: boolean;
  owner_class: BondOwnerClass;
  owner_class_source?: string;
  headroom_after: number | null;
}

export async function getSuretyRatios(token: string | null, period?: FinPeriodSel | null): Promise<SuretyRatios | null> {
  const wrap = await cachedCall<ToolResponse<SuretyRatios>>('/tools/executive/financials/surety-ratios', token, period ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load surety ratios', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* ---------------- Income statement / P&L (exec, Wave 3c) -----------------
 * Revenue + expense from the GL trial balance, presented as positive
 * magnitudes; net income = revenue − expense. Tiered since PR #144:
 * gross (operating revenue − cost of revenue 5xxx+6xxx) → operating
 * (− G&A 7xxx excl. income tax) → net. Ranges surfaced as pl_ranges. */

export interface PlAccount {
  gl_account: string;
  account_description: string | null;
  current_period_balance: number | null;
  current_ytd_balance: number | null;
  prior_ytd_balance: number | null;
  current_ytd_budget: number | null;
  yoy_variance: number | null;
  budget_variance: number | null;
}
export interface PlSide {
  accounts: PlAccount[];
  subtotal_ytd: number | null;
}
export interface IncomeStatement {
  linked: boolean;
  fiscal_year?: string;
  fiscal_period?: string;
  /** Anchor is the in-progress calendar month (partial postings). */
  period_is_current?: boolean;
  /** e.g. "YTD through August 2026". */
  period_label?: string;
  as_of: string | null;
  revenue?: PlSide;
  expense?: PlSide;
  summary?: {
    revenue_ytd: number | null;
    expense_ytd: number | null;
    net_income_ytd: number | null;
    net_margin: number | null;
    revenue_prior_ytd: number | null;
    expense_prior_ytd: number | null;
    net_income_prior_ytd: number | null;
    net_income_yoy_variance: number | null;
    revenue_ytd_budget: number | null;
    expense_ytd_budget: number | null;
    net_income_budget: number | null;
    net_income_budget_variance: number | null;
    // tiered operating view
    operating_revenue: number | null;
    /** operating revenue = contract revenue (4000–4199) + over/under billings (4499, ADR-003 D1). */
    contract_revenue?: number | null;
    over_under_billings?: number | null;
    contract_revenue_prior?: number | null;
    over_under_billings_prior?: number | null;
    other_income: number | null;
    cost_of_revenue: number | null;
    gross_profit: number | null;
    gross_margin: number | null;
    overhead_ga: number | null;
    operating_income: number | null;
    operating_margin: number | null;
    income_tax: number | null;
    // tiered view, same period last year (comparison mode)
    net_margin_prior?: number | null;
    operating_revenue_prior?: number | null;
    other_income_prior?: number | null;
    cost_of_revenue_prior?: number | null;
    gross_profit_prior?: number | null;
    gross_margin_prior?: number | null;
    overhead_ga_prior?: number | null;
    operating_income_prior?: number | null;
    operating_margin_prior?: number | null;
    income_tax_prior?: number | null;
  };
}

export async function getIncomeStatement(token: string | null, period?: FinPeriodSel | null): Promise<IncomeStatement | null> {
  const wrap = await cachedCall<ToolResponse<IncomeStatement>>('/tools/executive/financials/income-statement', token, period ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load income statement', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* ---------------- Overhead / G&A burn + break-even (exec, Wave 3d) -------
 * Overhead = G&A (7xxx ex income tax) from the GL trial balance; break-even
 * revenue = overhead ÷ gross-margin rate; margin of safety = how far actual
 * revenue sits above break-even. */

export interface OverheadBreakeven {
  linked: boolean;
  fiscal_year?: string;
  fiscal_period?: string;
  period_is_current?: boolean;
  period_label?: string;
  as_of: string | null;
  overhead?: { accounts: PlAccount[]; subtotal_ytd: number | null };
  metrics?: {
    operating_revenue_ytd: number | null;
    gross_profit_ytd: number | null;
    gross_margin: number | null;
    overhead_ytd: number | null;
    overhead_prior_ytd: number | null;
    overhead_budget_ytd: number | null;
    overhead_yoy_variance: number | null;
    overhead_budget_variance: number | null;
    operating_income_ytd: number | null;
    overhead_pct_of_revenue: number | null;
    break_even_revenue_ytd: number | null;
    /** Why break-even is null: gross margin ≤ 0 (no volume covers overhead)
     *  or no revenue posted. null when a break-even exists. */
    break_even_reason?: 'negative_gross_margin' | 'no_revenue' | null;
    margin_of_safety: number | null;
    margin_of_safety_pct: number | null;
    // same period last year (comparison mode)
    operating_revenue_prior_ytd?: number | null;
    gross_profit_prior_ytd?: number | null;
    gross_margin_prior?: number | null;
    overhead_pct_of_revenue_prior?: number | null;
    break_even_revenue_prior_ytd?: number | null;
    break_even_reason_prior?: 'negative_gross_margin' | 'no_revenue' | null;
    margin_of_safety_prior?: number | null;
    margin_of_safety_prior_pct?: number | null;
  };
}

export async function getOverheadBreakeven(token: string | null, period?: FinPeriodSel | null): Promise<OverheadBreakeven | null> {
  const wrap = await cachedCall<ToolResponse<OverheadBreakeven>>('/tools/executive/financials/overhead', token, period ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load overhead', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* ---------------- Company vitals scorecard (exec, Wave 3e) --------------
 * One curated health read assembled from the other financial reads. Every
 * vital carries its `source` and the `thresholds` used to band it, so the
 * scorecard is transparent about where each number comes from. */

export interface Vital {
  key: string;
  label: string;
  value: number | null;
  unit: string;                 // pct | ratio | days | months | count | usd
  band: string;                 // good | watch | risk | unknown
  source: string;
  thresholds: { dir: string; good: number; watch: number | null } | null;
  /** Why a null value still carries a band (e.g. "below break-even: gross margin is −8.1%"). */
  note?: string;
}
export interface VitalContext {
  label: string;
  value: number | null;
  unit: string;
  source: string;
}
export interface CompanyVitals {
  linked: boolean;
  as_of: string | null;
  /** GL anchor the GL-sourced vitals reflect (WIP/AR rows stay current). */
  fiscal_year?: string | null;
  fiscal_period?: string | null;
  period_is_current?: boolean | null;
  period_label?: string | null;
  overall: string;              // good | watch | risk | unknown
  counts: { good: number; watch: number; risk: number; unknown: number };
  vitals: Vital[];
  context: VitalContext[];
}

export async function getCompanyVitals(token: string | null, period?: FinPeriodSel | null): Promise<CompanyVitals | null> {
  const wrap = await cachedCall<ToolResponse<CompanyVitals>>('/tools/executive/financials/vitals', token, period ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load company vitals', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* ---------------- Payroll / labor cost (exec, financial layer #3) --------
 * PII-safe aggregates from the Spectrum payroll mirrors: loaded labor cost
 * (direct vs indirect), burden rate, total payroll cash, trend, top jobs and
 * cost centers. No employee-identifiable data. */

export interface PayrollTrendPoint {
  month: string;
  labor_cost: number;
  direct_labor: number;
  indirect_labor: number;
  hours: number;
  total_payroll_cash: number;
}
export interface PayrollJob {
  job_number: string;
  description: string | null;
  labor_cost: number;
  hours: number;
  direct: boolean;
}
export interface PayrollCostCenter {
  cost_center: string;
  gross_earnings: number;
  employer_burden: number;
  total_cash: number;
  check_count: number;
}
/** Prior-year same-window payroll comparison (null when the mirrors carry no
 *  rows for January-through-anchor-month of the prior year). */
export interface PayrollPriorSummary {
  year: number;
  month: number;
  labor_cost_ytd: number | null;
  hours_ytd: number | null;
  direct_labor_ytd: number | null;
  indirect_labor_ytd: number | null;
  indirect_labor_pct: number | null;
  burden_rate: number | null;
  avg_cost_per_hour: number | null;
  gross_payroll_ytd: number | null;
  employer_burden_ytd: number | null;
  total_payroll_cash_ytd: number | null;
  check_count_ytd: number;
}

export interface PayrollLaborCost {
  linked: boolean;
  as_of: string | null;
  latest_month?: string;
  summary?: {
    year: number;
    /** Anchor month — YTD runs January through this month. */
    month?: number;
    labor_cost_ytd: number | null;
    base_pay_ytd: number | null;
    burden_ytd: number | null;
    hours_ytd: number | null;
    direct_labor_ytd: number | null;
    indirect_labor_ytd: number | null;
    indirect_labor_pct: number | null;
    burden_rate: number | null;
    avg_cost_per_hour: number | null;
    gross_payroll_ytd: number | null;
    net_pay_ytd: number | null;
    employer_burden_ytd: number | null;
    total_payroll_cash_ytd: number | null;
    check_count_ytd: number;
  };
  prior_summary?: PayrollPriorSummary | null;
  trend?: PayrollTrendPoint[];
  by_job?: PayrollJob[];
  by_cost_center?: PayrollCostCenter[];
  method_note?: string;
}

export async function getPayrollLaborCost(token: string | null, period?: FinPeriodSel | null): Promise<PayrollLaborCost | null> {
  const wrap = await cachedCall<ToolResponse<PayrollLaborCost>>('/tools/executive/financials/payroll', token, period ?? {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load payroll analytics', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* ---------------- AP position (exec, financial layer #4) -----------------
 * Per-vendor current balances from the Spectrum vendor mirror: total owed,
 * retention payable, held / pay-when-paid, an approximate terms split, and a
 * GL control-account cross-check. Position snapshot, not invoice aging. */

export interface ApVendor {
  vendor_code: string;
  vendor_name: string | null;
  vendor_type: string | null;
  balance: number;
  retention_balance: number;
  terms_days: number;
  date_last_invoice: string | null;
  date_last_payment: string | null;
  expected_pay_date: string | null;
  hold: boolean;
  pay_when_paid: boolean;
}
export interface ApGlAccount {
  gl_account: string;
  account_description: string | null;
  balance: number;
}
export interface ApPosition {
  linked: boolean;
  as_of: string | null;
  /** GL period the control-account cross-check was read at (latest posted). */
  gl_period?: { fiscal_year: string; fiscal_period: string } | null;
  summary?: {
    total_ap_balance: number | null;
    vendor_count: number;
    total_retention_payable: number | null;
    held_balance: number | null;
    pay_when_paid_balance: number | null;
    within_terms_balance: number | null;
    past_terms_balance: number | null;
    undated_balance: number | null;
    /** Σ debit (negative) vendor balances as a positive magnitude. */
    credit_balance_total?: number;
    /** Vendors with unset terms (0 days, no code) where net-30 was assumed. */
    vendors_terms_unknown?: number;
    /** Σ balance on vendors whose master status is not active (still owed). */
    inactive_vendor_balance?: number;
    gl_ap_control: number | null;
    subledger_vs_gl_variance: number | null;
  };
  gl_control_accounts?: ApGlAccount[];
  vendors?: ApVendor[];
  method_note?: string;
}

export async function getApPosition(token: string | null): Promise<ApPosition | null> {
  const wrap = await cachedCall<ToolResponse<ApPosition>>('/tools/executive/financials/ap-position', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load AP position', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* ---------------- 13-week cash forecast (exec, financial layer #2) -------
 * Direct receipts-and-disbursements forecast over already-mirrored data:
 * opening bank cash, weekly AR + retention receipts vs payroll + AP outflows,
 * running balance and low point. A model, not a bank report — assumptions
 * surfaced. */

export interface CashForecastWeek {
  week: number;
  week_start: string;
  week_end: string;
  ar_receipts: number;
  retention_receipts: number;
  total_receipts: number;
  payroll_outflow: number;
  ap_outflow: number;
  total_outflows: number;
  net_change: number;
  closing_cash: number;
}
export interface CashAccount {
  gl_account: string;
  account_description: string | null;
  balance: number;
}
export interface CashForecast {
  linked: boolean;
  as_of: string | null;
  /** GL period the opening cash was read at (latest posted). */
  gl_period?: { fiscal_year: string; fiscal_period: string } | null;
  horizon_weeks?: number;
  opening_cash?: number;
  opening_cash_accounts?: CashAccount[];
  weeks?: CashForecastWeek[];
  summary?: {
    opening_cash: number;
    total_receipts_13wk: number;
    total_outflows_13wk: number;
    net_change_13wk: number;
    closing_cash_13wk: number;
    min_cash: number | null;
    min_cash_week: number | null;
    weekly_payroll_run_rate: number;
    unscheduled_retention: number;
    held_ap: number;
    /** Σ AR receipt events — open AR net of retention still held. */
    ar_ex_retention?: number;
    /** Vendor retention payable: paid at owner release, carried as a memo. */
    retention_payable_unscheduled?: number;
  };
  beyond_horizon?: { ar: number; retention: number; ap: number };
  assumptions?: Record<string, string>;
  method_note?: string;
}

export async function getCashForecast(token: string | null): Promise<CashForecast | null> {
  const wrap = await cachedCall<ToolResponse<CashForecast>>('/tools/executive/financials/cash-forecast', token, {});
  if (!wrap.success) throw new ApiError(wrap.error ?? 'failed to load cash forecast', 500, wrap.error_code ?? undefined);
  return wrap.data?.[0] ?? null;
}

/* ---------------- Tools Service — Togal × Project merge ---------------- */

export interface TogalFile {
  drive_item_id: string;
  name: string;
  size?: number | null;
  last_modified?: string | null;
  web_url?: string | null;
}

export interface TogalListFilesResult {
  togal: TogalFile[];
  project: TogalFile[];
}

export interface TogalFolderRouting {
  togal_folder: string;
  target_sheet: string | null;
  method: 'exact' | 'ai' | 'manual' | 'unmapped';
  confidence: number;
  reason: string;
  row_count: number;
  auto_approved: boolean;
}

export interface TogalSheetCapacity {
  sheet_name: string;
  free_slots: number;
  incoming_rows: number;
  needs_insert: boolean;
}

export interface TogalMergePlan {
  togal_name: string;
  project_name: string;
  togal_row_count: number;
  detail_sheet_count: number;
  routings: TogalFolderRouting[];
  capacity: TogalSheetCapacity[];
  skipped_sheets: string[];
  warnings: string[];
}

export interface TogalConfirmedRouting {
  togal_folder: string;
  target_sheet: string | null;
}

export interface TogalExecuteStats {
  written_per_sheet: Record<string, number>;
  inserted_per_sheet: Record<string, number>;
  total_written: number;
}

export interface TogalExecuteResult {
  merged_drive_item_id: string;
  merged_name: string;
  merged_file_url: string;
  merged_embed_url: string;
  merged_drive_id: string;
  version: number;
  summary: string;
  plan: TogalMergePlan;
  applied_routings: TogalFolderRouting[];
  stats: TogalExecuteStats;
}

export async function togalListFiles(token: string | null): Promise<TogalListFilesResult> {
  const wrap = await call<ToolResponse<TogalListFilesResult>>(
    '/tools/togal/list-files',
    token,
    {}
  );
  if (!wrap.data?.length) {
    return { togal: [], project: [] };
  }
  return wrap.data[0];
}

export async function togalUploadFile(
  token: string | null,
  kind: 'togal' | 'project',
  file: File
): Promise<TogalFile> {
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file);
  const wrap = await callMultipart<ToolResponse<TogalFile>>(
    '/tools/togal/upload-file',
    token,
    form
  );
  if (!wrap.data?.length) {
    throw new ApiError('togal upload returned no data', 500);
  }
  return wrap.data[0];
}

export async function togalPreviewMerge(
  token: string | null,
  togal_drive_item_id: string,
  project_drive_item_id: string
): Promise<TogalMergePlan> {
  const wrap = await call<ToolResponse<TogalMergePlan>>(
    '/tools/togal/preview-merge',
    token,
    { togal_drive_item_id, project_drive_item_id }
  );
  if (!wrap.data?.length) {
    throw new ApiError('togal preview-merge returned no data', 500);
  }
  return wrap.data[0];
}

export async function togalExecuteMerge(
  token: string | null,
  togal_drive_item_id: string,
  project_drive_item_id: string,
  confirmed_routings: TogalConfirmedRouting[] = []
): Promise<TogalExecuteResult> {
  const wrap = await call<ToolResponse<TogalExecuteResult>>(
    '/tools/togal/execute-merge',
    token,
    { togal_drive_item_id, project_drive_item_id, confirmed_routings }
  );
  if (!wrap.data?.length) {
    throw new ApiError('togal execute-merge returned no data', 500);
  }
  return wrap.data[0];
}

/* ---------------- Tools Service — feedback ---------------- */

export async function submitToolRequest(
  token: string | null,
  req: {
    name: string;
    problem: string;
    workflow?: string;
    frequency?: string;
  }
): Promise<{ request_id: string; status: string }> {
  const wrap = await call<ToolResponse<{ request_id: string; status: string }>>(
    '/tools/agents/feedback/tool-request',
    token,
    {
      name: req.name,
      problem: req.problem,
      workflow: req.workflow ?? '',
      frequency: req.frequency ?? 'a few times a month',
    }
  );
  if (!wrap.data?.length) {
    throw new ApiError('tool-request returned no data', 500);
  }
  return wrap.data[0];
}
